import { useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import wasmBindgenInit, {
  newGameStateForBoard,
  type GameStateHandle,
} from '@/KdlRust/pkg/kill_doctor_lucky_rust';
import TreeSearchWorker from '@/workers/treeSearchWorker?worker';
import boardAltDownData from '../data/boards/BoardAltDown.json';
import boardAltDownNoWarpData from '../data/boards/BoardAltDownNoWarp.json';
import boardAltUpData from '../data/boards/BoardAltUp.json';
import boardAltUpNoWarpData from '../data/boards/BoardAltUpNoWarp.json';
import boardHauntedDownData from '../data/boards/BoardHauntedDown.json';
import boardHauntedUpData from '../data/boards/BoardHauntedUp.json';
import boardJacob1Data from '../data/boards/BoardJacob1.json';
import boardJacob1BData from '../data/boards/BoardJacob1B.json';
import boardLairNorthData from '../data/boards/BoardLairNorth.json';
import boardLairSouthData from '../data/boards/BoardLairSouth.json';
import boardMainEastData from '../data/boards/BoardMainEast.json';
import boardMainWestData from '../data/boards/BoardMainWest.json';

type PieceId = 'doctor' | 'player1' | 'player2' | 'stranger1' | 'stranger2';
type NormalPlayerPieceId = 'player1' | 'player2';

type TurnPlanEntry = {
  pieceId: PieceId;
  roomId: number;
};

type TurnPlanPreviewResponse = {
  isValid: boolean;
  validationMessage: string;
  nextPlayerPieceId: string;
  hasWinner: boolean;
  winnerPieceId: string;
  attackers: string[];
  currentPlayerLoots: boolean;
  doctorRoomId: number;
  movedStrangers: Array<{
    pieceId: string;
    roomId: number;
  }>;
};

type PreviewToken =
  | {
      kind: 'text';
      text: string;
      colorPieceId: PieceId | null;
    }
  | {
      kind: 'winner';
      winnerPieceId: PieceId | null;
      winnerText: string;
    };

type PreviewDisplay = {
  message: string | null;
  tokens: PreviewToken[];
};

type InfoPopupKind = 'rules' | 'turnPlanner' | 'ai' | 'playerInfoBox';

type PendingAutoSelectedRoomPiece = {
  roomId: number;
  pieceId: PieceId;
};

type PlayerStatsRow = {
  pieceId: Exclude<PieceId, 'doctor'>;
  doctorDistance: number;
  strength: number;
  moveCards: number;
  weaponCards: number;
  failureCards: number;
  equivalentClovers: number;
};

type PieceIndicators = {
  top: string | null;
  bottom: string | null;
};

type AnimatedPieceIndicators = Partial<Record<PieceId, PieceIndicators>>;

const getMoveCardIndicatorText = (moveCards: number) => {
  const moveCardsThirtySeconds = Math.round(moveCards * 32);
  const roundedDownMoveCards = Math.floor(moveCardsThirtySeconds / 32);
  const fractionalThirtySeconds = ((moveCardsThirtySeconds % 32) + 32) % 32;
  const suffix = fractionalThirtySeconds >= 21 ? ':' : fractionalThirtySeconds >= 10 ? '•' : '';
  return `${formatPlayerInteger(roundedDownMoveCards)}${suffix}`;
};

const getLivePieceIndicators = (gameState: GameStateHandle | null, pieceId: PieceId): PieceIndicators => {
  if (!gameState || pieceId === 'doctor') {
    return { top: null, bottom: null };
  }
  const isNormalPlayer = pieceId === 'player1' || pieceId === 'player2';
  return {
    top: isNormalPlayer ? getMoveCardIndicatorText(gameState.pieceMoveCards(pieceId)) : null,
    bottom: formatPlayerInteger(gameState.pieceAttackStrength(pieceId)),
  };
};

const capturePieceIndicators = (gameState: GameStateHandle | null) => {
  if (!gameState) {
    return null as AnimatedPieceIndicators | null;
  }
  const indicators: AnimatedPieceIndicators = {};
  pieceOrder.forEach((pieceId) => {
    if (pieceId === 'doctor') {
      return;
    }
    indicators[pieceId] = getLivePieceIndicators(gameState, pieceId);
  });
  return indicators;
};

const getPlayerStatsRows = (gameState: GameStateHandle | null) => {
  if (!gameState) {
    return [] as PlayerStatsRow[];
  }
  return playerStatsRowOrder.map((pieceId) => ({
    pieceId,
    doctorDistance: gameState.pieceDoctorDistance(pieceId),
    strength: gameState.pieceStrength(pieceId),
    moveCards: gameState.pieceMoveCards(pieceId),
    weaponCards: gameState.pieceWeaponCards(pieceId),
    failureCards: gameState.pieceFailureCards(pieceId),
    equivalentClovers: gameState.pieceEquivalentClovers(pieceId),
  }));
};

const removeTwoSpaceLineIndent = (text: string) => text.replace(/^ {2}/gm, '');

const touchRoomDoubleTapThresholdMs = 500;

type BestTurnResponse = {
  isValid: boolean;
  validationMessage: string;
  suggestedTurnText: string;
  suggestedTurn: TurnPlanEntry[];
  heuristicScore: number;
  numStatesVisited: number;
  elapsedMs: number;
};

type TreeSearchWorkerInitRequest = {
  type: 'init';
  wasmModule: WebAssembly.Module;
};

type PieceRenderPosition = {
  pieceId: PieceId;
  x: number;
  y: number;
  size: number;
};

type TreeSearchWorkerAnalyzeRequest = {
  type: 'analyze';
  runId: number;
  stateJson: string;
  analysisLevel: number;
};

type TreeSearchWorkerRequest = TreeSearchWorkerInitRequest | TreeSearchWorkerAnalyzeRequest;

type TreeSearchWorkerResponse =
  | {
      type: 'analysisResult';
      runId: number;
      analysisRaw: string;
      previewRaw: string;
    }
  | {
      type: 'analysisError';
      runId: number;
      message: string;
    };

type AiSuggestion = {
  sourceTurnCounter: number;
  analysisLevel: number;
  bestTurn: BestTurnResponse;
  previewRaw: string;
  elapsedMs: number;
  levelElapsedMs: number;
};

type AiResultsCacheEntry = {
  boardName: string;
  stateJson: string;
  analysisLevel: number;
  bestTurn: BestTurnResponse;
  previewRaw: string;
  elapsedMs: number;
  levelElapsedMs: number;
  lastUsedAtMs: number;
};

type AiResultsCacheStore = {
  version: 1;
  entries: AiResultsCacheEntry[];
};

type AnalysisRunKind = 'think' | 'mull';

type AnalysisRunLimits = {
  kind: AnalysisRunKind;
  minAnalysisLevel: number;
  maxAnalysisLevel: number | null;
  maxTimeMs: number;
};

type WasmBindgenInitWithModule = typeof wasmBindgenInit & {
  __wbindgen_wasm_module?: WebAssembly.Module;
};

type BoardRoomRaw = {
  Id: string | number;
  Name?: string;
  Coords: number[];
  Adjacent?: Array<string | number>;
  Visible?: Array<string | number>;
};

type BoardRoom = {
  id: number;
  name?: string;
  coords: number[];
  adjacent: number[];
  visible: number[];
};

type BoardUiLayout = {
  BoardWidth: number;
  BoardHeight: number;
  BoardOverlayFontSizePx: number;
  PieceSizeTarget: number;
  WinnerOverlayFontSizePx: number;
  RoomDistanceBoxWidth: number;
  RoomDistanceBoxHeight: number;
  RoomDistanceBoxTopRatio: number;
  ActionDisplayBounds?: {
    MinXAfterRoomId: number;
    MaxXBeforeRoomId: number;
    MinYBelowRoomId: number;
  };
};

type BoardLayout = {
  ImagePath: string;
  JsonName: string;
  Name: string;
  PlayerStartRoomIds: number[];
  DoctorStartRoomIds: number[];
  CatStartRoomIds: number[];
  DogStartRoomIds: number[];
  Rooms: BoardRoomRaw[];
  UiLayout: BoardUiLayout;
};

type BoardContext = {
  layout: BoardLayout;
  jsonName: string;
  displayName: string;
  uiLayout: BoardUiLayout;
  width: number;
  height: number;
  overlayFontSizePx: number;
  pieceSizeTarget: number;
  winnerOverlayFontSizePx: number;
  roomDistanceBoxWidth: number;
  roomDistanceBoxHeight: number;
  roomDistanceBoxTopRatio: number;
  imageHref: string;
  rooms: BoardRoom[];
  roomById: Map<number, BoardRoom>;
  actionDisplayBounds: { minX: number; maxX: number; minY: number } | null;
};

type ActionKind = 'loot' | 'attack';

type ActionInfo = {
  kind: ActionKind;
  actor: PieceId;
};

type ShapeProps = {
  className?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  pointerEvents?: string;
  onClick?: (event: MouseEvent<SVGElement>) => void;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
};

type RoomRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type SlotPosition = {
  col: number;
  row: number;
};

type SlotLayout = {
  cols: number;
  rows: number;
  slots: SlotPosition[];
};

const boardLayouts = [
  boardAltDownData,
  boardAltDownNoWarpData,
  boardAltUpData,
  boardAltUpNoWarpData,
  boardMainEastData,
  boardMainWestData,
  boardHauntedDownData,
  boardHauntedUpData,
  boardJacob1Data,
  boardJacob1BData,
  boardLairNorthData,
  boardLairSouthData,
].map(
  (data) => data as BoardLayout,
);
const defaultBoardJsonName = 'BoardAltDown';
const boardLayoutByJsonName = new Map(boardLayouts.map((layout) => [layout.JsonName, layout] as const));
const getBoardLayout = (boardName: string) =>
  boardLayoutByJsonName.get(boardName) ?? boardLayoutByJsonName.get(defaultBoardJsonName) ?? boardLayouts[0];
const buildBoardContext = (layout: BoardLayout): BoardContext => {
  const roomRectFromCoords = (coords: number[]) => ({
    x: coords[0],
    y: coords[1],
    width: coords[2] - coords[0],
    height: coords[3] - coords[1],
  });
  const rooms = layout.Rooms.map((room) => ({
    id: Number(room.Id),
    name: room.Name,
    coords: room.Coords,
    adjacent: room.Adjacent?.map(Number) ?? [],
    visible: room.Visible?.map(Number) ?? [],
  }));
  const roomById = new Map<number, BoardRoom>(rooms.map((room) => [room.id, room] as const));
  const config = layout.UiLayout.ActionDisplayBounds;
  const actionDisplayBounds = (() => {
    if (!config) {
      return null;
    }
    const minYRoom = roomById.get(config.MinYBelowRoomId);
    const maxXRoom = roomById.get(config.MaxXBeforeRoomId);
    const minXRoom = roomById.get(config.MinXAfterRoomId);
    if (!minYRoom || !maxXRoom || !minXRoom) {
      return null;
    }
    const minYRoomRect = roomRectFromCoords(minYRoom.coords);
    const maxXRoomRect = roomRectFromCoords(maxXRoom.coords);
    const minXRoomRect = roomRectFromCoords(minXRoom.coords);
    return {
      minX: minXRoomRect.x + minXRoomRect.width,
      maxX: maxXRoomRect.x,
      minY: minYRoomRect.y + minYRoomRect.height,
    };
  })();
  return {
    layout,
    jsonName: layout.JsonName,
    displayName: layout.Name,
    uiLayout: layout.UiLayout,
    width: layout.UiLayout.BoardWidth,
    height: layout.UiLayout.BoardHeight,
    overlayFontSizePx: layout.UiLayout.BoardOverlayFontSizePx,
    pieceSizeTarget: layout.UiLayout.PieceSizeTarget,
    winnerOverlayFontSizePx: layout.UiLayout.WinnerOverlayFontSizePx,
    roomDistanceBoxWidth: layout.UiLayout.RoomDistanceBoxWidth,
    roomDistanceBoxHeight: layout.UiLayout.RoomDistanceBoxHeight,
    roomDistanceBoxTopRatio: layout.UiLayout.RoomDistanceBoxTopRatio,
    imageHref: `${import.meta.env.BASE_URL}${layout.ImagePath.replace(/^\//, '')}`,
    rooms,
    roomById,
    actionDisplayBounds,
  };
};
const boardContextByJsonName = new Map(boardLayouts.map((layout) => [layout.JsonName, buildBoardContext(layout)]));
const getBoardContext = (boardName: string) =>
  boardContextByJsonName.get(boardName) ??
  boardContextByJsonName.get(defaultBoardJsonName) ??
  buildBoardContext(getBoardLayout(defaultBoardJsonName));
let currentBoardContext = getBoardContext(defaultBoardJsonName);

const pieceOrder: PieceId[] = ['doctor', 'player1', 'player2', 'stranger1', 'stranger2'];
const normalPlayerPieceIds: NormalPlayerPieceId[] = ['player1', 'player2'];
const playerStatsRowOrder: Array<Exclude<PieceId, 'doctor'>> = ['stranger2', 'player1', 'stranger1', 'player2'];
const animationSpeeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4, 5];
const defaultSpeedIndex = 8;
const analysisMaxTimeOptions = [
  { label: 'No AI', ms: null },
  { label: '100ms', ms: 100 },
  { label: '500ms', ms: 500 },
  { label: '1s', ms: 1000 },
  { label: '2s', ms: 2000 },
  { label: '5s', ms: 5000 },
  { label: '10s', ms: 10000 },
  { label: '15s', ms: 15000 },
  { label: '30s', ms: 30000 },
  { label: '45s', ms: 45000 },
  { label: '1min', ms: 60000 },
  { label: '2min', ms: 120000 },
  { label: '5min', ms: 300000 },
  { label: '8min', ms: 480000 },
  { label: '12min', ms: 720000 },
  { label: '30min', ms: 1.8e6 },
  { label: '1hr', ms: 3.6e6 },
] as const;
const mullMaxTimeMs = 99999 * 1000;
const aiAnalysisDisabledStatusMessage = 'AI analysis disabled.';
const defaultAnalysisMaxTimeIndex = 2;
const defaultMinAnalysisLevel = 1;
const defaultMaxAnalysisLevel = 18;
const pieceLabels: Record<PieceId, string> = {
  doctor: 'Dr',
  player1: 'P1',
  player2: 'P3',
  stranger1: 'p2',
  stranger2: 'p4',
};
const isPieceId = (value: string): value is PieceId => pieceOrder.includes(value as PieceId);
const isSetupNormalPlayerPieceId = (value: string): value is NormalPlayerPieceId =>
  normalPlayerPieceIds.includes(value as NormalPlayerPieceId);
const animationPrefsStorageKey = 'kdl.settings.v1';
const aiPrefsStorageKey = 'kdl.ai.v1';
const aiResultsCacheStorageKey = 'kdl.aiResultsCache.v1';
const setupPrefsStorageKey = 'kdl.setup.v1';
const gameStateStorageKey = 'kdl.gameState.v1';
const redoStateStackStorageKey = 'kdl.redoStack.v1';
const selectedBoardStorageKey = 'kdl.selectedBoard.v1';
const assumedLocalStorageLimitBytes = 5 * 1024 * 1024;
const localStorageUsageThresholdRatio = 0.85;
const boardScopedStorageKey = (baseKey: string, boardName: string) => `${baseKey}.${boardName}`;
const defaultSetupPrefsForBoard = (boardContext: BoardContext): SetupPrefs => {
  const defaultSetupRoomId = boardContext.rooms[0]?.id ?? 1;
  const firstOpenStartRoom = (roomIds: number[]) =>
    roomIds.find((roomId) => boardContext.roomById.has(Number(roomId))) ?? defaultSetupRoomId;
  return {
    boardName: boardContext.jsonName,
    moveCards: 1,
    weaponCards: 2,
    failureCards: 6,
    player2MoveCards: 1,
    player2WeaponCards: 2,
    player2FailureCards: 6,
    doctorRoomId: firstOpenStartRoom(boardContext.layout.DoctorStartRoomIds),
    player1RoomId: firstOpenStartRoom(boardContext.layout.PlayerStartRoomIds),
    stranger1RoomId: firstOpenStartRoom(boardContext.layout.PlayerStartRoomIds),
    player2RoomId: firstOpenStartRoom(boardContext.layout.PlayerStartRoomIds),
    stranger2RoomId: firstOpenStartRoom(boardContext.layout.PlayerStartRoomIds),
    player1Strength: 1,
    stranger1Strength: 1,
    player2Strength: 1,
    stranger2Strength: 1,
    turnId: 1,
    currentPlayerPieceId: 'player1' as NormalPlayerPieceId,
  };
};
type AnimationPrefs = {
  animationEnabled: boolean;
  animationSpeedIndex: number;
};

type AiPrefs = {
  minAnalysisLevel: number;
  maxAnalysisLevel: number;
  analysisMaxTimeIndex: number;
  controlP1: boolean;
  controlP3: boolean;
  showOnBoardP1: boolean;
  showOnBoardP3: boolean;
};

type SetupPrefs = {
  boardName: string;
  moveCards: number;
  weaponCards: number;
  failureCards: number;
  player2MoveCards: number;
  player2WeaponCards: number;
  player2FailureCards: number;
  doctorRoomId: number;
  player1RoomId: number;
  stranger1RoomId: number;
  player2RoomId: number;
  stranger2RoomId: number;
  player1Strength: number;
  stranger1Strength: number;
  player2Strength: number;
  stranger2Strength: number;
  turnId: number;
  currentPlayerPieceId: NormalPlayerPieceId;
};

type SetupPrefsDraft = {
  boardName: string;
  moveCards: string;
  weaponCards: string;
  failureCards: string;
  player2MoveCards: string;
  player2WeaponCards: string;
  player2FailureCards: string;
  doctorRoomId: string;
  player1RoomId: string;
  stranger1RoomId: string;
  player2RoomId: string;
  stranger2RoomId: string;
  player1Strength: string;
  stranger1Strength: string;
  player2Strength: string;
  stranger2Strength: string;
  turnId: string;
  currentPlayerPieceId: NormalPlayerPieceId;
};

type StepDirection = 'down' | 'up';
type SetupRoomField = 'doctorRoomId' | 'player1RoomId' | 'stranger1RoomId' | 'player2RoomId' | 'stranger2RoomId';

const createTreeSearchWorker = () => {
  if (typeof Worker === 'undefined') {
    return null;
  }
  try {
    return new TreeSearchWorker();
  } catch {
    return null;
  }
};
let idleAnalysisWorker: Worker | null = createTreeSearchWorker();
const takeIdleAnalysisWorker = () => {
  if (idleAnalysisWorker) {
    const worker = idleAnalysisWorker;
    idleAnalysisWorker = null;
    return worker;
  }
  return createTreeSearchWorker();
};
const replaceIdleAnalysisWorker = () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return;
  }
  if (idleAnalysisWorker) {
    return;
  }
  idleAnalysisWorker = createTreeSearchWorker();
};

const parseNormalTurnCountFromSnapshotJson = (snapshotJson: string): number | null => {
  try {
    const parsed = JSON.parse(snapshotJson) as {
      normalTurns?: unknown;
      normal_turns?: unknown;
    };
    const turns = Array.isArray(parsed.normalTurns)
      ? parsed.normalTurns
      : Array.isArray(parsed.normal_turns)
        ? parsed.normal_turns
        : null;
    if (!turns) {
      return null;
    }
    return turns.length + 1;
  } catch {
    return null;
  }
};

const clampAnimationSpeedIndex = (value: number) => Math.min(animationSpeeds.length - 1, Math.max(0, value));
const clampAnalysisMaxTimeIndex = (value: number) => Math.min(analysisMaxTimeOptions.length - 1, Math.max(0, value));
const getAnalysisMaxTimeMs = (index: number) =>
  (analysisMaxTimeOptions[clampAnalysisMaxTimeIndex(index)] ?? analysisMaxTimeOptions[defaultAnalysisMaxTimeIndex]).ms;
const isFiniteNonNegative = (value: number) => Number.isFinite(value) && value >= 0;
const formatSetupCardDraft = (value: number) =>
  (Number.isInteger(value) ? value.toString() : value.toFixed(2)).replace(/(?:\.0+|(\.\d*?[1-9])0+)$/, '$1');
const parseMinAnalysisLevelDraft = (draft: string) => {
  const parsed = Number(draft);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Math.trunc(parsed);
};
const parseMaxAnalysisLevelDraft = (draft: string) => {
  const parsed = Number(draft);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Math.trunc(parsed);
};
const isValidBoardRoomId = (value: number, boardContext = currentBoardContext) =>
  Number.isInteger(value) && boardContext.roomById.has(value);
const isFiniteNonNegativeInteger = (value: number) => Number.isFinite(value) && value >= 0 && Number.isInteger(value);
const toSetupPrefsDraft = (prefs: SetupPrefs): SetupPrefsDraft => ({
  boardName: prefs.boardName,
  moveCards: formatSetupCardDraft(prefs.moveCards),
  weaponCards: formatSetupCardDraft(prefs.weaponCards),
  failureCards: formatSetupCardDraft(prefs.failureCards),
  player2MoveCards: formatSetupCardDraft(prefs.player2MoveCards),
  player2WeaponCards: formatSetupCardDraft(prefs.player2WeaponCards),
  player2FailureCards: formatSetupCardDraft(prefs.player2FailureCards),
  doctorRoomId: prefs.doctorRoomId.toString(),
  player1RoomId: prefs.player1RoomId.toString(),
  stranger1RoomId: prefs.stranger1RoomId.toString(),
  player2RoomId: prefs.player2RoomId.toString(),
  stranger2RoomId: prefs.stranger2RoomId.toString(),
  player1Strength: prefs.player1Strength.toString(),
  stranger1Strength: prefs.stranger1Strength.toString(),
  player2Strength: prefs.player2Strength.toString(),
  stranger2Strength: prefs.stranger2Strength.toString(),
  turnId: prefs.turnId.toString(),
  currentPlayerPieceId: prefs.currentPlayerPieceId,
});
const parseSetupPrefsDraft = (draft: SetupPrefsDraft): SetupPrefs | null => {
  const boardContext = getBoardContext(draft.boardName);
  const parsed = {
    boardName: boardContext.jsonName,
    moveCards: Number(draft.moveCards),
    weaponCards: Number(draft.weaponCards),
    failureCards: Number(draft.failureCards),
    player2MoveCards: Number(draft.player2MoveCards),
    player2WeaponCards: Number(draft.player2WeaponCards),
    player2FailureCards: Number(draft.player2FailureCards),
    doctorRoomId: Number(draft.doctorRoomId),
    player1RoomId: Number(draft.player1RoomId),
    stranger1RoomId: Number(draft.stranger1RoomId),
    player2RoomId: Number(draft.player2RoomId),
    stranger2RoomId: Number(draft.stranger2RoomId),
    player1Strength: Number(draft.player1Strength),
    stranger1Strength: Number(draft.stranger1Strength),
    player2Strength: Number(draft.player2Strength),
    stranger2Strength: Number(draft.stranger2Strength),
    turnId: Number(draft.turnId),
    currentPlayerPieceId: draft.currentPlayerPieceId,
  };
  return isFiniteNonNegative(parsed.moveCards) &&
    isFiniteNonNegative(parsed.weaponCards) &&
    isFiniteNonNegative(parsed.failureCards) &&
    isFiniteNonNegative(parsed.player2MoveCards) &&
    isFiniteNonNegative(parsed.player2WeaponCards) &&
    isFiniteNonNegative(parsed.player2FailureCards) &&
    isValidBoardRoomId(parsed.doctorRoomId, boardContext) &&
    isValidBoardRoomId(parsed.player1RoomId, boardContext) &&
    isValidBoardRoomId(parsed.stranger1RoomId, boardContext) &&
    isValidBoardRoomId(parsed.player2RoomId, boardContext) &&
    isValidBoardRoomId(parsed.stranger2RoomId, boardContext) &&
    isFiniteNonNegativeInteger(parsed.player1Strength) &&
    isFiniteNonNegativeInteger(parsed.stranger1Strength) &&
    isFiniteNonNegativeInteger(parsed.player2Strength) &&
    isFiniteNonNegativeInteger(parsed.stranger2Strength) &&
    isFiniteNonNegativeInteger(parsed.turnId) &&
    parsed.turnId >= 1 &&
    isSetupNormalPlayerPieceId(parsed.currentPlayerPieceId)
    ? parsed
    : null;
};
const stepNonNegativeText = (raw: string, direction: StepDirection, step: number) => {
  const parsed = Number(raw);
  const base = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  if (step >= 1) {
    if (Number.isInteger(base)) {
      const next = direction === 'down' ? Math.max(0, base - step) : base + step;
      return Math.trunc(next).toString();
    }
    return (direction === 'down' ? Math.floor(base) : Math.ceil(base)).toString();
  }
  const next = direction === 'down' ? Math.max(0, base - step) : base + step;
  return formatSetupCardDraft(next);
};
const isBoardNameForContext = (value: unknown, boardContext: BoardContext) =>
  typeof value !== 'string' || value === '' || value === boardContext.jsonName || value === boardContext.displayName;
const sanitizeSetupPrefs = (
  candidate: Partial<SetupPrefs>,
  fallback: SetupPrefs,
  boardContext = getBoardContext(fallback.boardName),
): SetupPrefs => {
  if (!isBoardNameForContext(candidate.boardName, boardContext)) {
    return fallback;
  }
  const moveCards = isFiniteNonNegative(candidate.moveCards ?? NaN)
    ? (candidate.moveCards as number)
    : fallback.moveCards;
  const weaponCards = isFiniteNonNegative(candidate.weaponCards ?? NaN)
    ? (candidate.weaponCards as number)
    : fallback.weaponCards;
  const failureCards = isFiniteNonNegative(candidate.failureCards ?? NaN)
    ? (candidate.failureCards as number)
    : fallback.failureCards;
  const player2MoveCards = isFiniteNonNegative(candidate.player2MoveCards ?? NaN)
    ? (candidate.player2MoveCards as number)
    : moveCards;
  const player2WeaponCards = isFiniteNonNegative(candidate.player2WeaponCards ?? NaN)
    ? (candidate.player2WeaponCards as number)
    : weaponCards;
  const player2FailureCards = isFiniteNonNegative(candidate.player2FailureCards ?? NaN)
    ? (candidate.player2FailureCards as number)
    : failureCards;
  const doctorRoomId = isValidBoardRoomId(candidate.doctorRoomId ?? NaN, boardContext)
    ? (candidate.doctorRoomId as number)
    : fallback.doctorRoomId;
  const player1RoomId = isValidBoardRoomId(candidate.player1RoomId ?? NaN, boardContext)
    ? (candidate.player1RoomId as number)
    : fallback.player1RoomId;
  const stranger1RoomId = isValidBoardRoomId(candidate.stranger1RoomId ?? NaN, boardContext)
    ? (candidate.stranger1RoomId as number)
    : fallback.stranger1RoomId;
  const player2RoomId = isValidBoardRoomId(candidate.player2RoomId ?? NaN, boardContext)
    ? (candidate.player2RoomId as number)
    : fallback.player2RoomId;
  const stranger2RoomId = isValidBoardRoomId(candidate.stranger2RoomId ?? NaN, boardContext)
    ? (candidate.stranger2RoomId as number)
    : fallback.stranger2RoomId;
  const player1Strength = isFiniteNonNegativeInteger(candidate.player1Strength ?? NaN)
    ? (candidate.player1Strength as number)
    : fallback.player1Strength;
  const stranger1Strength = isFiniteNonNegativeInteger(candidate.stranger1Strength ?? NaN)
    ? (candidate.stranger1Strength as number)
    : fallback.stranger1Strength;
  const player2Strength = isFiniteNonNegativeInteger(candidate.player2Strength ?? NaN)
    ? (candidate.player2Strength as number)
    : fallback.player2Strength;
  const stranger2Strength = isFiniteNonNegativeInteger(candidate.stranger2Strength ?? NaN)
    ? (candidate.stranger2Strength as number)
    : fallback.stranger2Strength;
  const turnId = isFiniteNonNegativeInteger(candidate.turnId ?? NaN) && (candidate.turnId as number) >= 1
    ? (candidate.turnId as number)
    : fallback.turnId;
  const currentPlayerPieceId = isSetupNormalPlayerPieceId(candidate.currentPlayerPieceId ?? '')
    ? (candidate.currentPlayerPieceId as NormalPlayerPieceId)
    : fallback.currentPlayerPieceId;
  return {
    boardName: boardContext.jsonName,
    moveCards,
    weaponCards,
    failureCards,
    player2MoveCards,
    player2WeaponCards,
    player2FailureCards,
    doctorRoomId,
    player1RoomId,
    stranger1RoomId,
    player2RoomId,
    stranger2RoomId,
    player1Strength,
    stranger1Strength,
    player2Strength,
    stranger2Strength,
    turnId,
    currentPlayerPieceId,
  };
};
const snapCardQuantityToThirtySeconds = (value: number) => Math.round(value * 32) / 32;
const shouldUseAdvancedSetup = (prefs: SetupPrefs, defaults: SetupPrefs) =>
  prefs.player2MoveCards !== prefs.moveCards ||
  prefs.player2WeaponCards !== prefs.weaponCards ||
  prefs.player2FailureCards !== prefs.failureCards ||
  prefs.doctorRoomId !== defaults.doctorRoomId ||
  prefs.player1RoomId !== defaults.player1RoomId ||
  prefs.stranger1RoomId !== defaults.stranger1RoomId ||
  prefs.player2RoomId !== defaults.player2RoomId ||
  prefs.stranger2RoomId !== defaults.stranger2RoomId ||
  prefs.player1Strength !== defaults.player1Strength ||
  prefs.stranger1Strength !== defaults.stranger1Strength ||
  prefs.player2Strength !== defaults.player2Strength ||
  prefs.stranger2Strength !== defaults.stranger2Strength ||
  prefs.turnId !== defaults.turnId ||
  prefs.currentPlayerPieceId !== defaults.currentPlayerPieceId;
const buildSetupPrefsForStart = (prefs: SetupPrefs, defaults: SetupPrefs, useAdvanced: boolean): SetupPrefs => ({
  boardName: prefs.boardName,
  moveCards: snapCardQuantityToThirtySeconds(prefs.moveCards),
  weaponCards: snapCardQuantityToThirtySeconds(prefs.weaponCards),
  failureCards: snapCardQuantityToThirtySeconds(prefs.failureCards),
  player2MoveCards: useAdvanced
    ? snapCardQuantityToThirtySeconds(prefs.player2MoveCards)
    : snapCardQuantityToThirtySeconds(prefs.moveCards),
  player2WeaponCards: useAdvanced
    ? snapCardQuantityToThirtySeconds(prefs.player2WeaponCards)
    : snapCardQuantityToThirtySeconds(prefs.weaponCards),
  player2FailureCards: useAdvanced
    ? snapCardQuantityToThirtySeconds(prefs.player2FailureCards)
    : snapCardQuantityToThirtySeconds(prefs.failureCards),
  doctorRoomId: useAdvanced ? prefs.doctorRoomId : defaults.doctorRoomId,
  player1RoomId: useAdvanced ? prefs.player1RoomId : defaults.player1RoomId,
  stranger1RoomId: useAdvanced ? prefs.stranger1RoomId : defaults.stranger1RoomId,
  player2RoomId: useAdvanced ? prefs.player2RoomId : defaults.player2RoomId,
  stranger2RoomId: useAdvanced ? prefs.stranger2RoomId : defaults.stranger2RoomId,
  player1Strength: useAdvanced ? prefs.player1Strength : defaults.player1Strength,
  stranger1Strength: useAdvanced ? prefs.stranger1Strength : defaults.stranger1Strength,
  player2Strength: useAdvanced ? prefs.player2Strength : defaults.player2Strength,
  stranger2Strength: useAdvanced ? prefs.stranger2Strength : defaults.stranger2Strength,
  turnId: useAdvanced ? prefs.turnId : defaults.turnId,
  currentPlayerPieceId: useAdvanced ? prefs.currentPlayerPieceId : defaults.currentPlayerPieceId,
});
const parseSetupPrefsJson = (raw: string, fallback: SetupPrefs, boardContext = getBoardContext(fallback.boardName)): SetupPrefs => {
  try {
    const parsed = JSON.parse(raw) as Partial<SetupPrefs>;
    return sanitizeSetupPrefs(parsed, fallback, boardContext);
  } catch {
    return fallback;
  }
};
const emitGameDebugLog = (message: string) => {
  console.debug(message);
};
let pendingLoadedGameSnapshotLog: string | null = null;
const emitLoadedGameDebugLogs = (snapshot: string, messages: string[]) => {
  if (pendingLoadedGameSnapshotLog === snapshot) {
    return;
  }
  pendingLoadedGameSnapshotLog = snapshot;
  messages.forEach(emitGameDebugLog);
  queueMicrotask(() => {
    if (pendingLoadedGameSnapshotLog === snapshot) {
      pendingLoadedGameSnapshotLog = null;
    }
  });
};
const formatSetupDebugInfo = (prefs: SetupPrefs, boardContext = getBoardContext(prefs.boardName)) => {
  const formatCards = (moveCards: number, weaponCards: number, failureCards: number) =>
    `${formatSetupCardDraft(moveCards)}m/${formatSetupCardDraft(weaponCards)}w/${formatSetupCardDraft(failureCards)}f`;
  return [
    `board=${boardContext.displayName} (${boardContext.jsonName})`,
    `cards={P1=${formatCards(prefs.moveCards, prefs.weaponCards, prefs.failureCards)}, P3=${formatCards(prefs.player2MoveCards, prefs.player2WeaponCards, prefs.player2FailureCards)}}`,
    `rooms={Dr=R${prefs.doctorRoomId}, P1=R${prefs.player1RoomId}, p2=R${prefs.stranger1RoomId}, P3=R${prefs.player2RoomId}, p4=R${prefs.stranger2RoomId}}`,
    `strengths={P1=${prefs.player1Strength}, p2=${prefs.stranger1Strength}, P3=${prefs.player2Strength}, p4=${prefs.stranger2Strength}}`,
    `first=${pieceLabels[prefs.currentPlayerPieceId]}`,
    `turnId=${prefs.turnId}`,
  ].join('; ');
};
const formatGameProgressDebugInfo = (gameState: GameStateHandle) => {
  const snapshot = gameState.exportStateJson();
  const turnNumber = parseNormalTurnCountFromSnapshotJson(snapshot) ?? '?';
  const currentPlayerPieceIdRaw = gameState.currentPlayerPieceId();
  const currentPlayerLabel = isPieceId(currentPlayerPieceIdRaw) ? pieceLabels[currentPlayerPieceIdRaw] : '?';
  return `T${turnNumber}, current=${currentPlayerLabel}`;
};
const formatGameStateDebugInfo = (gameState: GameStateHandle) => {
  const snapshot = gameState.exportStateJson();
  const boardName = boardNameFromSnapshotJson(snapshot) ?? '?';
  const positions = Array.from(gameState.piecePositions(), (value) => Number(value));
  const locationPieceIds: PieceId[] = ['doctor', 'player1', 'stranger1', 'player2', 'stranger2'];
  const playerPieceIds: Array<Exclude<PieceId, 'doctor'>> = ['player1', 'stranger1', 'player2', 'stranger2'];
  const locationsText = locationPieceIds
    .map((pieceId) => `${pieceLabels[pieceId]}=R${positions[pieceOrder.indexOf(pieceId)] ?? '?'}`)
    .join(', ');
  const strengthsText = playerPieceIds
    .map((pieceId) => `${pieceLabels[pieceId]}=${gameState.pieceStrength(pieceId)}`)
    .join(', ');
  const cardsText = (['player1', 'player2'] as const)
    .map(
      (pieceId) =>
        `${pieceLabels[pieceId]}=${formatSetupCardDraft(gameState.pieceMoveCards(pieceId))}m/${formatSetupCardDraft(gameState.pieceWeaponCards(pieceId))}w/${formatSetupCardDraft(gameState.pieceFailureCards(pieceId))}f`,
    )
    .join(', ');
  return `${formatGameProgressDebugInfo(gameState)}; board=${boardName}; locations={${locationsText}}; strengths={${strengthsText}}; cards={${cardsText}}`;
};
const emitGameStateDebugLog = (gameState: GameStateHandle) => {
  emitGameDebugLog(`Game state: ${formatGameStateDebugInfo(gameState)}`);
};
const loadSetupPrefs = (fallback: SetupPrefs, boardContext = getBoardContext(fallback.boardName)): SetupPrefs => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const raw =
      window.localStorage.getItem(boardScopedStorageKey(setupPrefsStorageKey, boardContext.jsonName)) ??
      (boardContext.jsonName === defaultBoardJsonName ? window.localStorage.getItem(setupPrefsStorageKey) : null);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw) as Partial<SetupPrefs>;
    return sanitizeSetupPrefs(parsed, fallback, boardContext);
  } catch {
    return fallback;
  }
};
const saveSetupPrefs = (prefs: SetupPrefs, boardContext = getBoardContext(prefs.boardName)) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      boardScopedStorageKey(setupPrefsStorageKey, boardContext.jsonName),
      JSON.stringify(sanitizeSetupPrefs(prefs, defaultSetupPrefsForBoard(boardContext), boardContext)),
    );
    if (boardContext.jsonName === defaultBoardJsonName) {
      window.localStorage.setItem(
        setupPrefsStorageKey,
        JSON.stringify(sanitizeSetupPrefs(prefs, defaultSetupPrefsForBoard(boardContext), boardContext)),
      );
    }
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const loadSelectedBoardName = () => {
  if (typeof window === 'undefined') {
    return defaultBoardJsonName;
  }

  try {
    const raw = window.localStorage.getItem(selectedBoardStorageKey);
    if (raw && boardLayoutByJsonName.has(raw)) {
      return raw;
    }
  } catch {
    // Fall back to the default board.
  }
  return defaultBoardJsonName;
};

const saveSelectedBoardName = (boardName: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(selectedBoardStorageKey, getBoardContext(boardName).jsonName);
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const loadAnimationPrefs = (): AnimationPrefs => {
  const defaults: AnimationPrefs = { animationEnabled: true, animationSpeedIndex: defaultSpeedIndex };
  if (typeof window === 'undefined') {
    return defaults;
  }

  try {
    const raw = window.localStorage.getItem(animationPrefsStorageKey);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<AnimationPrefs>;
    const animationEnabled = typeof parsed.animationEnabled === 'boolean' ? parsed.animationEnabled : true;
    const parsedSpeedIndex =
      typeof parsed.animationSpeedIndex === 'number' && Number.isFinite(parsed.animationSpeedIndex)
        ? Math.trunc(parsed.animationSpeedIndex)
        : defaultSpeedIndex;
    return {
      animationEnabled,
      animationSpeedIndex: clampAnimationSpeedIndex(parsedSpeedIndex),
    };
  } catch {
    return defaults;
  }
};

const saveAnimationPrefs = (prefs: AnimationPrefs) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      animationPrefsStorageKey,
      JSON.stringify({
        animationEnabled: prefs.animationEnabled,
        animationSpeedIndex: clampAnimationSpeedIndex(prefs.animationSpeedIndex),
      }),
    );
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const sanitizeAiPrefs = (candidate: Partial<AiPrefs>): AiPrefs => {
  const minAnalysisLevel = isFiniteNonNegative(candidate.minAnalysisLevel ?? NaN)
    ? Math.trunc(candidate.minAnalysisLevel as number)
    : defaultMinAnalysisLevel;
  const maxAnalysisLevel = isFiniteNonNegative(candidate.maxAnalysisLevel ?? NaN)
    ? Math.trunc(candidate.maxAnalysisLevel as number)
    : defaultMaxAnalysisLevel;
  const rawMaxTimeIndex =
    typeof candidate.analysisMaxTimeIndex === 'number' && Number.isFinite(candidate.analysisMaxTimeIndex)
      ? Math.trunc(candidate.analysisMaxTimeIndex)
      : defaultAnalysisMaxTimeIndex;
  const controlP1 = typeof candidate.controlP1 === 'boolean' ? candidate.controlP1 : false;
  const controlP3 = typeof candidate.controlP3 === 'boolean' ? candidate.controlP3 : false;
  const showOnBoardP1 = typeof candidate.showOnBoardP1 === 'boolean' ? candidate.showOnBoardP1 : false;
  const showOnBoardP3 = typeof candidate.showOnBoardP3 === 'boolean' ? candidate.showOnBoardP3 : false;
  return {
    minAnalysisLevel,
    maxAnalysisLevel,
    analysisMaxTimeIndex: clampAnalysisMaxTimeIndex(rawMaxTimeIndex),
    controlP1,
    controlP3,
    showOnBoardP1,
    showOnBoardP3,
  };
};

const loadAiPrefs = (): AiPrefs => {
  const defaults = {
    minAnalysisLevel: defaultMinAnalysisLevel,
    maxAnalysisLevel: defaultMaxAnalysisLevel,
    analysisMaxTimeIndex: defaultAnalysisMaxTimeIndex,
    controlP1: false,
    controlP3: false,
    showOnBoardP1: false,
    showOnBoardP3: false,
  };
  if (typeof window === 'undefined') {
    return defaults;
  }

  try {
    const raw = window.localStorage.getItem(aiPrefsStorageKey);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<AiPrefs>;
    return sanitizeAiPrefs(parsed);
  } catch {
    return defaults;
  }
};

const saveAiPrefs = (prefs: AiPrefs) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(aiPrefsStorageKey, JSON.stringify(sanitizeAiPrefs(prefs)));
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const createEmptyAiResultsCacheStore = (): AiResultsCacheStore => ({
  version: 1,
  entries: [],
});

const sanitizeCachedTurnPlanEntry = (candidate: unknown): TurnPlanEntry | null => {
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const parsed = candidate as { pieceId?: unknown; roomId?: unknown };
  const pieceId = parsed.pieceId;
  if (typeof pieceId !== 'string' || !isPieceId(pieceId)) {
    return null;
  }
  if (typeof parsed.roomId !== 'number' || !Number.isFinite(parsed.roomId)) {
    return null;
  }
  return {
    pieceId,
    roomId: parsed.roomId,
  };
};

const sanitizeCachedBestTurnResponse = (candidate: unknown): BestTurnResponse | null => {
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const parsed = candidate as Partial<BestTurnResponse>;
  if (parsed.isValid !== true) {
    return null;
  }
  if (typeof parsed.suggestedTurnText !== 'string') {
    return null;
  }
  if (!Array.isArray(parsed.suggestedTurn)) {
    return null;
  }
  const suggestedTurn = parsed.suggestedTurn
    .map((entry) => sanitizeCachedTurnPlanEntry(entry))
    .filter((entry): entry is TurnPlanEntry => entry !== null);
  if (
    typeof parsed.validationMessage !== 'string' ||
    typeof parsed.heuristicScore !== 'number' ||
    !Number.isFinite(parsed.heuristicScore) ||
    typeof parsed.numStatesVisited !== 'number' ||
    !Number.isFinite(parsed.numStatesVisited) ||
    typeof parsed.elapsedMs !== 'number' ||
    !Number.isFinite(parsed.elapsedMs)
  ) {
    return null;
  }
  return {
    isValid: true,
    validationMessage: parsed.validationMessage,
    suggestedTurnText: parsed.suggestedTurnText,
    suggestedTurn,
    heuristicScore: parsed.heuristicScore,
    numStatesVisited: parsed.numStatesVisited,
    elapsedMs: parsed.elapsedMs,
  };
};

const sanitizeAiResultsCacheEntry = (candidate: unknown): AiResultsCacheEntry | null => {
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const parsed = candidate as Partial<AiResultsCacheEntry>;
  if (typeof parsed.stateJson !== 'string' || parsed.stateJson.length === 0) {
    return null;
  }
  const boardName =
    typeof parsed.boardName === 'string' && boardLayoutByJsonName.has(parsed.boardName)
      ? parsed.boardName
      : boardNameFromSnapshotJson(parsed.stateJson);
  if (!boardName) {
    return null;
  }
  if (typeof parsed.analysisLevel !== 'number' || !Number.isFinite(parsed.analysisLevel) || parsed.analysisLevel < 0) {
    return null;
  }
  const bestTurn = sanitizeCachedBestTurnResponse(parsed.bestTurn);
  if (!bestTurn) {
    return null;
  }
  if (typeof parsed.previewRaw !== 'string') {
    return null;
  }
  if (typeof parsed.elapsedMs !== 'number' || !Number.isFinite(parsed.elapsedMs) || parsed.elapsedMs < 0) {
    return null;
  }
  if (
    typeof parsed.levelElapsedMs !== 'number' ||
    !Number.isFinite(parsed.levelElapsedMs) ||
    parsed.levelElapsedMs < 0
  ) {
    return null;
  }
  if (typeof parsed.lastUsedAtMs !== 'number' || !Number.isFinite(parsed.lastUsedAtMs) || parsed.lastUsedAtMs < 0) {
    return null;
  }

  return {
    boardName,
    stateJson: parsed.stateJson,
    analysisLevel: Math.trunc(parsed.analysisLevel),
    bestTurn,
    previewRaw: parsed.previewRaw,
    elapsedMs: parsed.elapsedMs,
    levelElapsedMs: parsed.levelElapsedMs,
    lastUsedAtMs: parsed.lastUsedAtMs,
  };
};

const normalizeAiResultsCacheEntries = (entries: AiResultsCacheEntry[]) => {
  const entriesByState = new Map<string, AiResultsCacheEntry>();
  entries.forEach((entry) => {
    const cacheKey = `${entry.boardName}\n${entry.stateJson}`;
    const previous = entriesByState.get(cacheKey);
    if (!previous || previous.lastUsedAtMs <= entry.lastUsedAtMs) {
      entriesByState.set(cacheKey, entry);
    }
  });
  return Array.from(entriesByState.values());
};

const estimateStorageStringBytes = (value: string) => value.length * 2;

const estimateLocalStorageUsageBytes = (storage: Storage, excludedKey?: string) => {
  let totalBytes = 0;
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || key === excludedKey) {
      continue;
    }
    const value = storage.getItem(key) ?? '';
    totalBytes += estimateStorageStringBytes(key) + estimateStorageStringBytes(value);
  }
  return totalBytes;
};

const getOldestAiResultsCacheEntryIndex = (entries: AiResultsCacheEntry[]) => {
  if (entries.length === 0) {
    return -1;
  }
  let oldestIndex = 0;
  let oldestTime = entries[0].lastUsedAtMs;
  for (let index = 1; index < entries.length; index += 1) {
    if (entries[index].lastUsedAtMs < oldestTime) {
      oldestTime = entries[index].lastUsedAtMs;
      oldestIndex = index;
    }
  }
  return oldestIndex;
};

const saveAiResultsCacheStore = (store: AiResultsCacheStore): AiResultsCacheStore => {
  const normalizedStore: AiResultsCacheStore = {
    version: 1,
    entries: normalizeAiResultsCacheEntries(store.entries),
  };
  if (typeof window === 'undefined') {
    return normalizedStore;
  }

  const storage = window.localStorage;
  const usageThresholdBytes = Math.floor(assumedLocalStorageLimitBytes * localStorageUsageThresholdRatio);
  const bytesWithoutCacheKey = estimateLocalStorageUsageBytes(storage, aiResultsCacheStorageKey);
  const nextEntries = [...normalizedStore.entries];

  while (nextEntries.length > 0) {
    const serialized = JSON.stringify({ version: 1, entries: nextEntries } satisfies AiResultsCacheStore);
    const projectedUsageBytes =
      bytesWithoutCacheKey +
      estimateStorageStringBytes(aiResultsCacheStorageKey) +
      estimateStorageStringBytes(serialized);
    if (projectedUsageBytes < usageThresholdBytes) {
      try {
        storage.setItem(aiResultsCacheStorageKey, serialized);
        return {
          version: 1,
          entries: [...nextEntries],
        };
      } catch {
        // Try again after removing oldest entries until a write succeeds.
      }
    }
    const oldestEntryIndex = getOldestAiResultsCacheEntryIndex(nextEntries);
    if (oldestEntryIndex < 0) {
      break;
    }
    nextEntries.splice(oldestEntryIndex, 1);
  }

  try {
    storage.removeItem(aiResultsCacheStorageKey);
  } catch {
    // Ignore cleanup failures.
  }
  return createEmptyAiResultsCacheStore();
};

const loadAiResultsCacheStore = (): AiResultsCacheStore => {
  if (typeof window === 'undefined') {
    return createEmptyAiResultsCacheStore();
  }

  try {
    const raw = window.localStorage.getItem(aiResultsCacheStorageKey);
    if (!raw) {
      return createEmptyAiResultsCacheStore();
    }
    const parsed = JSON.parse(raw) as { entries?: unknown };
    if (!Array.isArray(parsed.entries)) {
      window.localStorage.removeItem(aiResultsCacheStorageKey);
      return createEmptyAiResultsCacheStore();
    }
    const sanitizedEntries = parsed.entries
      .map((entry) => sanitizeAiResultsCacheEntry(entry))
      .filter((entry): entry is AiResultsCacheEntry => entry !== null);
    return saveAiResultsCacheStore({
      version: 1,
      entries: sanitizedEntries,
    });
  } catch {
    try {
      window.localStorage.removeItem(aiResultsCacheStorageKey);
    } catch {
      // Ignore cleanup failures.
    }
    return createEmptyAiResultsCacheStore();
  }
};

const clearAiResultsCacheStore = (): AiResultsCacheStore => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(aiResultsCacheStorageKey);
    } catch {
      // Ignore cleanup failures.
    }
  }
  return createEmptyAiResultsCacheStore();
};

const boardNameFromSnapshotJson = (snapshotJson: string): string | null => {
  try {
    const parsed = JSON.parse(snapshotJson) as { boardName?: unknown; board_name?: unknown };
    const boardName = typeof parsed.boardName === 'string' ? parsed.boardName : parsed.board_name;
    return typeof boardName === 'string' && boardLayoutByJsonName.has(boardName) ? boardName : null;
  } catch {
    return null;
  }
};

const saveGameStateSnapshot = (gameState: GameStateHandle | null, boardName = currentBoardContext.jsonName) => {
  if (!gameState || typeof window === 'undefined') {
    return;
  }

  try {
    const snapshot = gameState.exportStateJson();
    const snapshotBoardName = boardNameFromSnapshotJson(snapshot) ?? boardName;
    window.localStorage.setItem(boardScopedStorageKey(gameStateStorageKey, snapshotBoardName), snapshot);
    if (snapshotBoardName === defaultBoardJsonName) {
      window.localStorage.setItem(gameStateStorageKey, snapshot);
    }
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const loadRedoStateStack = (boardName = currentBoardContext.jsonName): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const gameStateKey = boardScopedStorageKey(gameStateStorageKey, boardName);
    const redoStateKey = boardScopedStorageKey(redoStateStackStorageKey, boardName);
    const hasGameStateSnapshot =
      window.localStorage.getItem(gameStateKey) !== null ||
      (boardName === defaultBoardJsonName && window.localStorage.getItem(gameStateStorageKey) !== null);
    if (!hasGameStateSnapshot) {
      window.localStorage.removeItem(redoStateKey);
      if (boardName === defaultBoardJsonName) {
        window.localStorage.removeItem(redoStateStackStorageKey);
      }
      return [];
    }

    const raw =
      window.localStorage.getItem(redoStateKey) ??
      (boardName === defaultBoardJsonName ? window.localStorage.getItem(redoStateStackStorageKey) : null);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      window.localStorage.removeItem(redoStateKey);
      if (boardName === defaultBoardJsonName) {
        window.localStorage.removeItem(redoStateStackStorageKey);
      }
      return [];
    }
    return parsed.filter(
      (snapshot): snapshot is string =>
        typeof snapshot === 'string' && (boardNameFromSnapshotJson(snapshot) ?? boardName) === boardName,
    );
  } catch {
    try {
      window.localStorage.removeItem(boardScopedStorageKey(redoStateStackStorageKey, boardName));
    } catch {
      // Ignore cleanup failures.
    }
    return [];
  }
};

const saveRedoStateStack = (redoStateStack: string[], boardName = currentBoardContext.jsonName) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const redoStateKey = boardScopedStorageKey(redoStateStackStorageKey, boardName);
    const snapshots = redoStateStack.filter(
      (snapshot): snapshot is string =>
        typeof snapshot === 'string' && (boardNameFromSnapshotJson(snapshot) ?? boardName) === boardName,
    );
    if (snapshots.length === 0) {
      window.localStorage.removeItem(redoStateKey);
      if (boardName === defaultBoardJsonName) {
        window.localStorage.removeItem(redoStateStackStorageKey);
      }
      return;
    }
    window.localStorage.setItem(redoStateKey, JSON.stringify(snapshots));
    if (boardName === defaultBoardJsonName) {
      window.localStorage.setItem(redoStateStackStorageKey, JSON.stringify(snapshots));
    }
  } catch {
    // Ignore persistence failures (e.g. private mode / quota).
  }
};

const loadGameStateSnapshot = (gameState: GameStateHandle, boardContext = currentBoardContext) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const rawSnapshot =
      window.localStorage.getItem(boardScopedStorageKey(gameStateStorageKey, boardContext.jsonName)) ??
      (boardContext.jsonName === defaultBoardJsonName ? window.localStorage.getItem(gameStateStorageKey) : null);
    if (!rawSnapshot) {
      return;
    }
    if (boardNameFromSnapshotJson(rawSnapshot) !== boardContext.jsonName) {
      return;
    }

    const importError = gameState.importStateJson(rawSnapshot);
    if (importError) {
      window.localStorage.removeItem(boardScopedStorageKey(gameStateStorageKey, boardContext.jsonName));
      window.localStorage.removeItem(boardScopedStorageKey(redoStateStackStorageKey, boardContext.jsonName));
      if (boardContext.jsonName === defaultBoardJsonName) {
        window.localStorage.removeItem(gameStateStorageKey);
        window.localStorage.removeItem(redoStateStackStorageKey);
      }
      console.warn(`Saved game ignored: ${importError}`);
      return;
    }
    const setupFromGame = parseSetupPrefsJson(
      gameState.currentNormalSetupJson(),
      defaultSetupPrefsForBoard(boardContext),
      boardContext,
    );
    saveSetupPrefs(setupFromGame, boardContext);
    emitLoadedGameDebugLogs(
      rawSnapshot,
      [
        `Game state loaded from local storage: ${formatSetupDebugInfo(setupFromGame, boardContext)}`,
        `Game state: ${formatGameStateDebugInfo(gameState)}`,
      ],
    );
  } catch {
    try {
      window.localStorage.removeItem(boardScopedStorageKey(gameStateStorageKey, boardContext.jsonName));
      window.localStorage.removeItem(boardScopedStorageKey(redoStateStackStorageKey, boardContext.jsonName));
      if (boardContext.jsonName === defaultBoardJsonName) {
        window.localStorage.removeItem(gameStateStorageKey);
        window.localStorage.removeItem(redoStateStackStorageKey);
      }
    } catch {
      // Ignore cleanup failures.
    }
  }
};

const pieceConfig: Record<
  PieceId,
  {
    label: string;
    shape: 'circle' | 'square' | 'hex';
    color: string;
    textColor: string;
    showLabel: boolean;
  }
> = {
  doctor: { label: pieceLabels.doctor, shape: 'circle', color: '#000000', textColor: '#ffffff', showLabel: true },
  player1: { label: pieceLabels.player1, shape: 'square', color: '#ff4444', textColor: '#000000', showLabel: true },
  player2: { label: pieceLabels.player2, shape: 'square', color: '#44ff44', textColor: '#000000', showLabel: true },
  stranger1: { label: pieceLabels.stranger1, shape: 'hex', color: '#f08a24', textColor: '#000000', showLabel: true },
  stranger2: { label: pieceLabels.stranger2, shape: 'hex', color: '#55cccc', textColor: '#000000', showLabel: true },
};

const pieceGap = 3;
const emptyLayoutCellPenalty = 0.45;

const getRoomRect = (coords: number[]): RoomRect => ({
  x: coords[0],
  y: coords[1],
  width: coords[2] - coords[0],
  height: coords[3] - coords[1],
});

const getSlotLayout = (count: number, roomRect: RoomRect): SlotLayout => {
  if (count <= 1) {
    return { cols: 1, rows: 1, slots: [{ col: 0, row: 0 }] };
  }

  const roomAspect = roomRect.width > 0 && roomRect.height > 0 ? roomRect.width / roomRect.height : 1;
  let bestLayout: SlotLayout | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  let bestUncappedSize = 0;

  for (let cols = 1; cols <= count; cols += 1) {
    const rows = Math.ceil(count / cols);
    const emptyCells = cols * rows - count;
    const layoutAspect = cols / rows;
    const aspectPenalty = Math.abs(Math.log(layoutAspect / roomAspect));
    const uncappedSize = Math.min(
      (roomRect.width - pieceGap * (cols - 1)) / cols,
      (roomRect.height - pieceGap * (rows - 1)) / rows,
    );
    const score = aspectPenalty + emptyCells * emptyLayoutCellPenalty;

    if (
      !bestLayout ||
      score < bestScore - 0.0001 ||
      (Math.abs(score - bestScore) <= 0.0001 && uncappedSize > bestUncappedSize)
    ) {
      const slots: SlotPosition[] = [];
      let placed = 0;
      for (let row = 0; row < rows && placed < count; row += 1) {
        const remaining = count - placed;
        const itemsInRow = Math.min(cols, remaining);
        const startCol = (cols - itemsInRow) / 2;
        for (let index = 0; index < itemsInRow; index += 1) {
          slots.push({ col: startCol + index, row });
          placed += 1;
        }
      }

      bestLayout = { cols, rows, slots };
      bestScore = score;
      bestUncappedSize = uncappedSize;
    }
  }

  return bestLayout ?? { cols: 1, rows: count, slots: Array.from({ length: count }, (_, row) => ({ col: 0, row })) };
};

const getPiecePositionsInRoom = (count: number, roomRect: RoomRect) => {
  if (count === 0) {
    return { size: currentBoardContext.pieceSizeTarget, positions: [] as Array<{ x: number; y: number }> };
  }

  const { cols, rows, slots } = getSlotLayout(count, roomRect);
  const maxPieceWidth = (roomRect.width - pieceGap * (cols - 1)) / cols;
  const maxPieceHeight = (roomRect.height - pieceGap * (rows - 1)) / rows;
  const size = Math.min(currentBoardContext.pieceSizeTarget, maxPieceWidth, maxPieceHeight);
  const gridWidth = cols * size + pieceGap * (cols - 1);
  const gridHeight = rows * size + pieceGap * (rows - 1);
  const offsetX = roomRect.x + (roomRect.width - gridWidth) / 2;
  const offsetY = roomRect.y + (roomRect.height - gridHeight) / 2;

  const positions = slots.slice(0, count).map((slot) => ({
    x: offsetX + slot.col * (size + pieceGap),
    y: offsetY + slot.row * (size + pieceGap),
  }));

  return { size, positions };
};

const getHexPoints = (x: number, y: number, size: number) => {
  const radius = size / 2;
  const centerX = x + radius;
  const centerY = y + radius;
  const startAngle = Math.PI / 6;
  const points = Array.from({ length: 6 }, (_, index) => {
    const angle = startAngle + index * (Math.PI / 3);
    return `${centerX + radius * Math.cos(angle)},${centerY + radius * Math.sin(angle)}`;
  });

  return points.join(' ');
};

const buildRoomDistanceMap = (startRoomId: number) => {
  const distances = new Map<number, number>();
  const queue = [startRoomId];
  distances.set(startRoomId, 0);

  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) {
      continue;
    }
    const currentDistance = distances.get(current);
    if (currentDistance === undefined) {
      continue;
    }
    const room = currentBoardContext.roomById.get(current);
    if (!room) {
      continue;
    }
    room.adjacent.forEach((neighbor) => {
      if (distances.has(neighbor)) {
        return;
      }
      distances.set(neighbor, currentDistance + 1);
      queue.push(neighbor);
    });
  }

  return distances;
};

const parseActionActor = (token: string): PieceId | null => {
  const trimmed = token.trim();
  if (!trimmed) {
    return null;
  }
  const prefix = trimmed[0];
  const num = Number(trimmed.slice(1));
  if (!Number.isFinite(num)) {
    return null;
  }
  if (prefix === 'P') {
    if (num === 1) {
      return 'player1';
    }
    if (num === 2 || num === 3) {
      return 'player2';
    }
    return null;
  }
  if (prefix === 'p') {
    if (num === 2) {
      return 'stranger1';
    }
    if (num === 4) {
      return 'stranger2';
    }
    return null;
  }
  return null;
};

const parseActionSummaries = (summary: string): Array<ActionInfo | null> => {
  const trimmed = summary.trim();
  if (!trimmed) {
    return [];
  }
  const lines = summary.split(/\r?\n/);
  const summaries: string[][] = [];
  let current: string[] = [];

  lines.forEach((line) => {
    if (line.startsWith('  Turn')) {
      if (current.length > 0) {
        summaries.push(current);
      }
      current = [line];
    } else if (current.length > 0) {
      current.push(line);
    }
  });

  if (current.length > 0) {
    summaries.push(current);
  }

  return summaries.map((entry) => {
    const actionKind = entry.some((line) => line.includes('    LOOT '))
      ? 'loot'
      : entry.some((line) => line.includes('    ATTACK:'))
        ? 'attack'
        : null;
    if (!actionKind) {
      return null;
    }
    const firstLine = entry[0] ?? '';
    const match = firstLine.match(/\(([^)]+)\)/);
    if (!match) {
      return null;
    }
    const actorToken = match[1].replace(/[MLA]+$/g, '');
    const actor = parseActionActor(actorToken);
    if (!actor) {
      return null;
    }
    return { kind: actionKind, actor };
  });
};

const buildActionOverlayLayout = (text: string, options?: { bottomAlignToBoardShell?: boolean }) => {
  const fontSize = currentBoardContext.overlayFontSizePx;
  const paddingX = 16;
  const paddingY = 8;
  const estimatedTextWidth = Math.max(60, text.length * fontSize * 0.6);
  const boxHeight = fontSize + paddingY * 2;
  const bounds = currentBoardContext.actionDisplayBounds;
  const minX = bounds ? bounds.minX + 8 : 12;
  const maxX = bounds ? bounds.maxX - 8 : currentBoardContext.width - 12;
  const minY = bounds ? bounds.minY + 6 : 12;
  const maxWidth = Math.max(120, maxX - minX);
  const boxWidth = Math.min(maxWidth, Math.max(160, estimatedTextWidth + paddingX * 2));
  const desiredX = currentBoardContext.width / 2 - boxWidth / 2;
  const desiredY = options?.bottomAlignToBoardShell
    ? currentBoardContext.height - boxHeight
    : currentBoardContext.height - boxHeight - 12;
  const clampedX = Math.min(Math.max(desiredX, minX), Math.max(minX, maxX - boxWidth));
  const clampedY = options?.bottomAlignToBoardShell
    ? Math.max(desiredY, minY)
    : Math.min(Math.max(desiredY, minY), Math.max(minY, currentBoardContext.height - boxHeight - 6));

  return {
    boxX: clampedX,
    boxY: clampedY,
    boxWidth,
    boxHeight,
    textX: clampedX + boxWidth / 2,
    textY: clampedY + boxHeight / 2 + 0.5,
    fontSize,
  };
};

const buildWinnerOverlayLayout = (text: string) => {
  const fontSize = currentBoardContext.winnerOverlayFontSizePx;
  const paddingX = 24;
  const paddingY = 18;
  const estimatedTextWidth = Math.max(180, text.length * fontSize * 0.58);
  const maxWidth = Math.max(220, currentBoardContext.width - 40);
  const boxWidth = Math.min(maxWidth, Math.max(280, estimatedTextWidth + paddingX * 2));
  const boxHeight = fontSize + paddingY * 2;
  const boxX = currentBoardContext.width / 2 - boxWidth / 2;
  const boxY = currentBoardContext.height / 2 - boxHeight / 2;

  return {
    boxX,
    boxY,
    boxWidth,
    boxHeight,
    textX: boxX + boxWidth / 2,
    textY: boxY + boxHeight / 2 + 1,
  };
};

const blendHexColor = (from: string, to: string, ratio: number) => {
  const normalize = (value: string) => value.replace('#', '').trim();
  const fromHex = normalize(from);
  const toHex = normalize(to);
  if (fromHex.length !== 6 || toHex.length !== 6) {
    return from;
  }
  const fromNum = parseInt(fromHex, 16);
  const toNum = parseInt(toHex, 16);
  if (Number.isNaN(fromNum) || Number.isNaN(toNum)) {
    return from;
  }
  const clamp = (value: number) => Math.min(255, Math.max(0, value));
  const fromRgb = [(fromNum >> 16) & 0xff, (fromNum >> 8) & 0xff, fromNum & 0xff];
  const toRgb = [(toNum >> 16) & 0xff, (toNum >> 8) & 0xff, toNum & 0xff];
  const blended = fromRgb.map((channel, index) => clamp(Math.round(channel + (toRgb[index] - channel) * ratio)));
  return `#${blended.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

const toPreviewDisplay = (rawPreview: string, invalidMessage: string): PreviewDisplay => {
  const emptyTokens: PreviewToken[] = [];
  let parsed: TurnPlanPreviewResponse;
  try {
    parsed = JSON.parse(rawPreview) as TurnPlanPreviewResponse;
  } catch {
    return {
      message: 'Preview unavailable.',
      tokens: emptyTokens,
    };
  }

  if (!parsed.isValid) {
    return {
      message: parsed.validationMessage || invalidMessage,
      tokens: emptyTokens,
    };
  }

  const nextPieceId = isPieceId(parsed.nextPlayerPieceId) ? parsed.nextPlayerPieceId : null;
  const nextText = nextPieceId ? pieceConfig[nextPieceId].label : parsed.nextPlayerPieceId || '??';
  const winnerPieceId = isPieceId(parsed.winnerPieceId) ? parsed.winnerPieceId : null;
  const winnerText = winnerPieceId
    ? pieceConfig[winnerPieceId].label
    : parsed.winnerPieceId || (nextPieceId ? pieceConfig[nextPieceId].label : '??');
  const tokens: PreviewToken[] = parsed.hasWinner
    ? [{ kind: 'winner', winnerPieceId, winnerText }]
    : [{ kind: 'text', text: `Next:${nextText}`, colorPieceId: nextPieceId }];

  if (parsed.attackers.length > 0) {
    const attackerLabels = parsed.attackers.map((pieceId) =>
      isPieceId(pieceId) ? pieceConfig[pieceId].label : pieceId,
    );
    tokens.push({ kind: 'text', text: `Atk:${attackerLabels.join(',')}`, colorPieceId: null });
  }

  if (parsed.currentPlayerLoots) {
    tokens.push({ kind: 'text', text: 'Loot', colorPieceId: null });
  }

  parsed.movedStrangers.forEach((entry) => {
    const pieceId = isPieceId(entry.pieceId) ? entry.pieceId : null;
    const pieceText = pieceId ? pieceConfig[pieceId].label : entry.pieceId;
    const roomText = Number.isFinite(entry.roomId) ? entry.roomId : '?';
    const colorPieceId = pieceId === 'stranger1' || pieceId === 'stranger2' ? pieceId : null;
    tokens.push({ kind: 'text', text: `${pieceText}:R${roomText}`, colorPieceId });
  });

  const doctorRoomText = Number.isFinite(parsed.doctorRoomId) ? parsed.doctorRoomId : '?';
  tokens.push({ kind: 'text', text: `Dr:R${doctorRoomText}`, colorPieceId: null });

  return {
    message: null,
    tokens,
  };
};

function FittingPreviewValue({
  display,
  keyPrefix,
}: {
  display: PreviewDisplay;
  keyPrefix: string;
}) {
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef<number | null>(null);

  const fitPreviewValue = (element: HTMLSpanElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (!element) {
      return;
    }

    const observedElement = element.parentElement ?? element;
    let lastObservedWidth = observedElement.clientWidth;
    const fit = (force = false) => {
      const observedWidth = observedElement.clientWidth;
      if (!force && observedWidth === lastObservedWidth) {
        return;
      }
      lastObservedWidth = observedWidth;
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = window.requestAnimationFrame(() => {
        element.style.fontSize = '';
        const availableWidth = element.clientWidth;
        const requiredWidth = element.scrollWidth;
        if (availableWidth <= 0 || requiredWidth <= availableWidth) {
          return;
        }

        const defaultFontSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
        if (!Number.isFinite(defaultFontSize) || defaultFontSize <= 0) {
          return;
        }

        const nextFontSize = Math.max(8, defaultFontSize * (availableWidth / requiredWidth));
        element.style.fontSize = `${nextFontSize.toFixed(2)}px`;
      });
    };

    fit(true);
    observerRef.current = new ResizeObserver(() => fit());
    observerRef.current.observe(observedElement);
  };

  return (
    <span className="planner-value planner-value--preview" ref={fitPreviewValue}>
      {display.message
        ? display.message
        : display.tokens.map((token, index) => {
            if (token.kind === 'winner') {
              const winnerPieceStyle = token.winnerPieceId
                ? {
                    backgroundColor: pieceConfig[token.winnerPieceId].color,
                    color: pieceConfig[token.winnerPieceId].textColor,
                  }
                : undefined;
              return (
                <span key={`${keyPrefix}-preview-token-win-${token.winnerText}-${index}`}>
                  {index > 0 && <span className="planner-preview-sep">|</span>}
                  <span className="planner-preview-token planner-preview-token--badge planner-preview-token--win">
                    WIN:
                  </span>
                  <span
                    className={
                      token.winnerPieceId
                        ? 'planner-preview-token planner-preview-token--badge'
                        : 'planner-preview-token'
                    }
                    style={winnerPieceStyle}
                  >
                    {token.winnerText}
                  </span>
                </span>
              );
            }
            const colorPieceId = token.colorPieceId;
            const previewTokenStyle = colorPieceId
              ? {
                  backgroundColor: pieceConfig[colorPieceId].color,
                  color: pieceConfig[colorPieceId].textColor,
                }
              : undefined;
            return (
              <span key={`${keyPrefix}-preview-token-text-${token.text}-${index}`}>
                {index > 0 && <span className="planner-preview-sep">|</span>}
                <span
                  className={
                    colorPieceId ? 'planner-preview-token planner-preview-token--badge' : 'planner-preview-token'
                  }
                  style={previewTokenStyle}
                >
                  {token.text}
                </span>
              </span>
            );
          })}
    </span>
  );
}

const formatElapsedTime = (elapsedMs: number) => {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return '0ms';
  }
  if (elapsedMs < 1000) {
    return `${Math.round(elapsedMs)}ms`;
  }
  return `${(elapsedMs / 1000).toFixed(2)}s`;
};

const formatWholeSeconds = (elapsedMs: number) => {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return '0s';
  }
  return `${Math.floor(elapsedMs / 1000)}s`;
};

const formatRoundedSeconds = (elapsedMs: number) => {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return '0s';
  }
  return `${Math.round(elapsedMs / 1000)}s`;
};

const isTerminalHeuristicScore = (score: number) => Number.isFinite(score) && Math.abs(score) > 1e12;
const terminalHeuristicOutcomeLabel = (score: number) =>
  isTerminalHeuristicScore(score) ? (score > 0 ? 'win' : 'loss') : null;
const terminalHeuristicOutcomeText = (score: number) =>
  isTerminalHeuristicScore(score) ? (score > 0 ? 'WIN' : 'LOSS') : null;

const formatSuggestedTurnText = (bestTurn: BestTurnResponse) => {
  if (bestTurn.suggestedTurn.length > 0) {
    return bestTurn.suggestedTurn.map((entry) => `${pieceConfig[entry.pieceId].label}@R${entry.roomId}`).join(', ');
  }

  const normalized = bestTurn.suggestedTurnText
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (normalized.length > 0) {
    return normalized.join(', ');
  }
  return '(none)';
};

const formatSuggestedTurnTextForBoard = (bestTurn: BestTurnResponse) => {
  if (bestTurn.suggestedTurn.length > 0) {
    return bestTurn.suggestedTurn.map((entry) => `${pieceConfig[entry.pieceId].label}@${entry.roomId}`).join(', ');
  }
  return null;
};

const formatDebugSeconds = (elapsedMs: number) => {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return '0.0s';
  }
  return `${(elapsedMs / 1000).toFixed(1)}s`;
};

type AnalysisStopDebugInfo = {
  runKind: AnalysisRunKind;
  minAnalysisLevel: number;
  maxAnalysisLevel: number | null;
  maxTimeMs: number;
  runElapsedMs?: number | null;
  remainingMs?: number | null;
  currentLevel?: number | null;
  currentLevelElapsedMs?: number | null;
  lastCompletedLevel?: number | null;
  lastCompletedLevelElapsedMs?: number | null;
};

const formatOptionalDebugSeconds = (elapsedMs: number | null | undefined) =>
  typeof elapsedMs === 'number' && Number.isFinite(elapsedMs) ? formatDebugSeconds(elapsedMs) : '?';

const formatAnalysisStopDebugInfo = (info: AnalysisStopDebugInfo) => {
  const maxLevelText = info.maxAnalysisLevel === null ? 'none' : `L${info.maxAnalysisLevel}`;
  const currentLevelText = info.currentLevel === null || info.currentLevel === undefined ? '?' : `L${info.currentLevel}`;
  const lastCompletedLevelText =
    info.lastCompletedLevel === null || info.lastCompletedLevel === undefined ? '?' : `L${info.lastCompletedLevel}`;
  return [
    `kind=${info.runKind}`,
    `min=L${info.minAnalysisLevel}`,
    `max=${maxLevelText}`,
    `maxTime=${formatDebugSeconds(info.maxTimeMs)}`,
    `elapsed=${formatOptionalDebugSeconds(info.runElapsedMs)}`,
    `remaining=${formatOptionalDebugSeconds(info.remainingMs)}`,
    `current=${currentLevelText}`,
    `currentElapsed=${formatOptionalDebugSeconds(info.currentLevelElapsedMs)}`,
    `lastCompleted=${lastCompletedLevelText}`,
    `lastCompletedElapsed=${formatOptionalDebugSeconds(info.lastCompletedLevelElapsedMs)}`,
  ].join(', ');
};

const formatAnalysisLevelDebugLine = (
  playerLabel: string,
  turnNumber: number,
  analysisLevel: number,
  levelElapsedMs: number,
  bestTurn: BestTurnResponse,
) =>
  `${playerLabel}, T${turnNumber}, L${analysisLevel}, ${formatDebugSeconds(levelElapsedMs)}; ${formatSuggestedTurnText(bestTurn)}`;

const emitAnalysisDebugLog = (message: string) => {
  if (typeof navigator !== 'undefined' && navigator.webdriver) {
    return;
  }
  console.debug(message);
};

const formatHeuristicScore = (score: number) => {
  if (!Number.isFinite(score)) {
    return '?';
  }
  if (Math.abs(score) > 1e12) {
    return score > 0 ? 'WIN' : 'LOSE';
  }
  return `${score >= 0 ? '+' : ''}${score.toFixed(2)}`;
};

const formatPlayerStatDecimal = (value: number) => (Number.isFinite(value) ? value.toFixed(2) : '?');

const formatPlayerInteger = (value: number) => (Number.isFinite(value) ? Math.trunc(value).toString() : '?');

const isNormalPlayerPieceId = (pieceId: PieceId): pieceId is NormalPlayerPieceId =>
  pieceId === 'player1' || pieceId === 'player2';

function PlayArea() {
  const [selectedBoardName, setSelectedBoardName] = useState(loadSelectedBoardName);
  const boardContext = getBoardContext(selectedBoardName);
  currentBoardContext = boardContext;
  const boardJsonName = boardContext.jsonName;
  const boardWidth = boardContext.width;
  const boardHeight = boardContext.height;
  const boardImageHref = boardContext.imageHref;
  const boardRooms = boardContext.rooms;
  const boardRoomById = boardContext.roomById;
  const roomDistanceBoxWidth = boardContext.roomDistanceBoxWidth;
  const roomDistanceBoxHeight = boardContext.roomDistanceBoxHeight;
  const roomDistanceBoxTopRatio = boardContext.roomDistanceBoxTopRatio;
  const fallbackSetupPrefsForCurrentBoard = defaultSetupPrefsForBoard(boardContext);
  const [gameState] = useState<GameStateHandle | null>(() => {
    try {
      const freshState = newGameStateForBoard(boardContext.jsonName);
      loadGameStateSnapshot(freshState, boardContext);
      const setupFromGame = parseSetupPrefsJson(
        freshState.currentNormalSetupJson(),
        fallbackSetupPrefsForCurrentBoard,
        boardContext,
      );
      saveSetupPrefs(setupFromGame, boardContext);
      return freshState;
    } catch {
      return null;
    }
  });
  const defaultSetupPrefs = gameState
    ? parseSetupPrefsJson(gameState.defaultNormalSetupJson(), fallbackSetupPrefsForCurrentBoard, boardContext)
    : fallbackSetupPrefsForCurrentBoard;
  const currentSetupPrefs = gameState
    ? parseSetupPrefsJson(gameState.currentNormalSetupJson(), defaultSetupPrefs, boardContext)
    : defaultSetupPrefs;
  const persistedSetupPrefs = loadSetupPrefs(currentSetupPrefs, boardContext);
  const [selectedPieceId, setSelectedPieceId] = useState<PieceId | null>(null);
  const [distancePreviewRoomId, setDistancePreviewRoomId] = useState<number | null>(null);
  const [plannedMoves, setPlannedMoves] = useState<Partial<Record<PieceId, number>>>({});
  const [planOrder, setPlanOrder] = useState<PieceId[]>([]);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [infoPopup, setInfoPopup] = useState<InfoPopupKind | null>(null);
  const [setupPopupOpen, setSetupPopupOpen] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [setupPrefsDraft, setSetupPrefsDraft] = useState<SetupPrefsDraft>(() =>
    toSetupPrefsDraft(persistedSetupPrefs),
  );
  const [setupAdvanced, setSetupAdvanced] = useState(() =>
    shouldUseAdvancedSetup(persistedSetupPrefs, defaultSetupPrefs),
  );
  const [redoStateStack, setRedoStateStack] = useState<string[]>(() =>
    gameState ? loadRedoStateStack(boardContext.jsonName) : [],
  );
  const redoStateStackRef = useRef(redoStateStack);
  redoStateStackRef.current = redoStateStack;
  const [turnCounter, setTurnCounter] = useState(0);
  const turnCounterRef = useRef(turnCounter);
  turnCounterRef.current = turnCounter;
  const initialAutoAnalysisQueuedRef = useRef(false);
  const [minAnalysisLevelDraft, setMinAnalysisLevelDraft] = useState(() => loadAiPrefs().minAnalysisLevel.toString());
  const [maxAnalysisLevelDraft, setMaxAnalysisLevelDraft] = useState(() => loadAiPrefs().maxAnalysisLevel.toString());
  const [analysisMaxTimeIndex, setAnalysisMaxTimeIndex] = useState(() => loadAiPrefs().analysisMaxTimeIndex);
  const [aiControlP1, setAiControlP1] = useState(() => loadAiPrefs().controlP1);
  const [aiControlP3, setAiControlP3] = useState(() => loadAiPrefs().controlP3);
  const [aiShowOnBoardP1, setAiShowOnBoardP1] = useState(() => loadAiPrefs().showOnBoardP1);
  const [aiShowOnBoardP3, setAiShowOnBoardP3] = useState(() => loadAiPrefs().showOnBoardP3);
  const aiControlP1Ref = useRef(aiControlP1);
  aiControlP1Ref.current = aiControlP1;
  const aiControlP3Ref = useRef(aiControlP3);
  aiControlP3Ref.current = aiControlP3;
  const aiShowOnBoardP1Ref = useRef(aiShowOnBoardP1);
  aiShowOnBoardP1Ref.current = aiShowOnBoardP1;
  const aiShowOnBoardP3Ref = useRef(aiShowOnBoardP3);
  aiShowOnBoardP3Ref.current = aiShowOnBoardP3;
  const [analysisIsRunning, setAnalysisIsRunning] = useState(false);
  const analysisIsRunningRef = useRef(false);
  const setAnalysisRunningState = (isRunning: boolean) => {
    analysisIsRunningRef.current = isRunning;
    setAnalysisIsRunning(isRunning);
  };
  const [analysisRunningLevel, setAnalysisRunningLevel] = useState<number | null>(null);
  const analysisRunningLevelRef = useRef<number | null>(analysisRunningLevel);
  analysisRunningLevelRef.current = analysisRunningLevel;
  const [analysisElapsedMs, setAnalysisElapsedMs] = useState(0);
  const [analysisCurrentLevelElapsedMs, setAnalysisCurrentLevelElapsedMs] = useState(0);
  const [analysisRunMaxTimeMs, setAnalysisRunMaxTimeMs] = useState<number>(
    () => getAnalysisMaxTimeMs(loadAiPrefs().analysisMaxTimeIndex) ?? 0,
  );
  const [analysisRunKind, setAnalysisRunKind] = useState<AnalysisRunKind | null>(null);
  const [analysisStatusMessage, setAnalysisStatusMessage] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<AiSuggestion | null>(null);
  const aiResultsCacheRef = useRef<AiResultsCacheStore>(loadAiResultsCacheStore());
  const analysisTimerRef = useRef<number | null>(null);
  const analysisDeadlineTimerRef = useRef<number | null>(null);
  const analysisTimingRef = useRef<{ runStartMs: number; levelStartMs: number } | null>(null);
  const analysisRunLimitsRef = useRef<AnalysisRunLimits | null>(null);
  const analysisTimeLimitReachedRef = useRef(false);
  const analysisWorkerRef = useRef<Worker | null>(null);
  const analysisWorkerInitializedRef = useRef(false);
  const analysisRunIdRef = useRef(0);
  const [animationEnabled, setAnimationEnabled] = useState(() => loadAnimationPrefs().animationEnabled);
  const [animationSpeedIndex, setAnimationSpeedIndex] = useState(() => loadAnimationPrefs().animationSpeedIndex);
  const [actionOverlay, setActionOverlay] = useState<string | null>(null);
  const [actionHighlightPieceId, setActionHighlightPieceId] = useState<PieceId | null>(null);
  const [animatedPieces, setAnimatedPieces] = useState<Array<{
    pieceId: PieceId;
    roomId: number;
    x: number;
    y: number;
    size: number;
  }> | null>(null);
  const [animatedPieceIndicators, setAnimatedPieceIndicators] = useState<AnimatedPieceIndicators | null>(null);
  const animationRef = useRef<{
    segments: Array<{
      from: Array<{ pieceId: PieceId; roomId: number; x: number; y: number; size: number }>;
      to: Array<{ pieceId: PieceId; roomId: number; x: number; y: number; size: number }>;
      actionText: string | null;
      highlightPieceId: PieceId | null;
    }>;
    segmentIndex: number;
    startTime: number;
    durationMs: number;
    speed: number;
    rafId: number | null;
  } | null>(null);
  const queuedAutoSubmitRef = useRef<{
    moves: Partial<Record<PieceId, number>>;
    order: PieceId[];
    sourceTurnCounter: number;
  } | null>(null);
  const deferredAutoSubmitRafRef = useRef<number | null>(null);
  const pendingAutoSelectedRoomPieceRef = useRef<PendingAutoSelectedRoomPiece | null>(null);
  const lastTouchRoomTapRef = useRef<{
    roomId: number;
    tappedAtMs: number;
    startedWithNoSelection: boolean;
  } | null>(null);
  const lastRoomPointerTypeRef = useRef<string | null>(null);
  const animationSpeed = animationSpeeds[animationSpeedIndex];
  const summary = gameState ? gameState.summary(0) : 'Failed to create game state.';
  const prevTurnSummary = gameState ? removeTwoSpaceLineIndent(gameState.prevTurnSummaryVerbose()) : '';
  const history = gameState ? gameState.normalTurnHistory() : '';
  const currentPlayerPieceId = gameState ? (gameState.currentPlayerPieceId() as PieceId) : null;
  const currentNormalTurnCount = gameState
    ? (parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? 1)
    : 1;
  const highestRememberedUndoneTurnCount = redoStateStack.reduce((highest, snapshot) => {
    const snapshotNormalTurnCount = parseNormalTurnCountFromSnapshotJson(snapshot);
    return snapshotNormalTurnCount === null ? highest : Math.max(highest, snapshotNormalTurnCount);
  }, currentNormalTurnCount);
  const currentTurnCountText =
    highestRememberedUndoneTurnCount === currentNormalTurnCount
      ? `${currentNormalTurnCount}`
      : `${currentNormalTurnCount}/${highestRememberedUndoneTurnCount}`;
  const currentTurnTitleText = `Turn ${currentTurnCountText}: ${currentPlayerPieceId ? pieceConfig[currentPlayerPieceId].label : '??'}`;
  const hasWinner = gameState ? gameState.hasWinner() : false;
  const completedNormalTurnCount = Math.max(0, currentNormalTurnCount - 1);
  const canUndo = history.trim().length > 0;
  const canRedo = redoStateStack.length > 0;
  const canUndoAll = canUndo;
  const canRedoAll = canRedo;
  const canCancelTurnPlan =
    selectedPieceId !== null || distancePreviewRoomId !== null || planOrder.length > 0 || validationMessage !== null;
  const winnerPieceIdRaw = gameState ? gameState.winnerPieceId() : '';
  const winnerPieceId =
    winnerPieceIdRaw === 'player1' || winnerPieceIdRaw === 'player2' ? (winnerPieceIdRaw as PieceId) : null;
  const winnerOverlayText = winnerPieceId ? `${pieceConfig[winnerPieceId].label} won!` : null;
  const winnerTurnTitleText = winnerPieceId
    ? `Turn ${currentNormalTurnCount}: ${pieceConfig[winnerPieceId].label} won!`
    : null;
  const saveCurrentAiPrefs = (overrides?: Partial<AiPrefs>) => {
    const fallbackAiPrefs = loadAiPrefs();
    saveAiPrefs({
      minAnalysisLevel: parseMinAnalysisLevelDraft(minAnalysisLevelDraft) ?? fallbackAiPrefs.minAnalysisLevel,
      maxAnalysisLevel: parseMaxAnalysisLevelDraft(maxAnalysisLevelDraft) ?? fallbackAiPrefs.maxAnalysisLevel,
      analysisMaxTimeIndex,
      controlP1: aiControlP1Ref.current,
      controlP3: aiControlP3Ref.current,
      showOnBoardP1: aiShowOnBoardP1Ref.current,
      showOnBoardP3: aiShowOnBoardP3Ref.current,
      ...overrides,
    });
  };
  const updateAiControlPrefs = (nextControlP1: boolean, nextControlP3: boolean) => {
    aiControlP1Ref.current = nextControlP1;
    aiControlP3Ref.current = nextControlP3;
    setAiControlP1(nextControlP1);
    setAiControlP3(nextControlP3);
    saveCurrentAiPrefs({
      controlP1: nextControlP1,
      controlP3: nextControlP3,
    });
  };
  const updateAiShowOnBoardPrefs = (nextShowOnBoardP1: boolean, nextShowOnBoardP3: boolean) => {
    aiShowOnBoardP1Ref.current = nextShowOnBoardP1;
    aiShowOnBoardP3Ref.current = nextShowOnBoardP3;
    setAiShowOnBoardP1(nextShowOnBoardP1);
    setAiShowOnBoardP3(nextShowOnBoardP3);
    saveCurrentAiPrefs({
      showOnBoardP1: nextShowOnBoardP1,
      showOnBoardP3: nextShowOnBoardP3,
    });
  };
  const findAiResultsCacheEntry = (stateJson: string) => {
    const boardName = boardNameFromSnapshotJson(stateJson);
    return (
      aiResultsCacheRef.current.entries.find(
        (entry) => entry.stateJson === stateJson && entry.boardName === boardName,
      ) ?? null
    );
  };
  const toAiSuggestionFromCacheEntry = (entry: AiResultsCacheEntry, sourceTurnCounter: number): AiSuggestion => ({
    sourceTurnCounter,
    analysisLevel: entry.analysisLevel,
    bestTurn: entry.bestTurn,
    previewRaw: entry.previewRaw,
    elapsedMs: entry.elapsedMs,
    levelElapsedMs: entry.levelElapsedMs,
  });
  const touchAiResultsCacheEntry = (entry: AiResultsCacheEntry, touchedAtMs: number) => {
    const nextEntry: AiResultsCacheEntry = {
      ...entry,
      lastUsedAtMs: touchedAtMs,
    };
    const otherEntries = aiResultsCacheRef.current.entries.filter(
      (candidate) => candidate.stateJson !== entry.stateJson || candidate.boardName !== entry.boardName,
    );
    aiResultsCacheRef.current = saveAiResultsCacheStore({
      version: 1,
      entries: [...otherEntries, nextEntry],
    });
    return nextEntry;
  };
  const upsertAiResultsCacheFromSuggestion = (stateJson: string, suggestion: AiSuggestion, touchedAtMs: number) => {
    const boardName = boardNameFromSnapshotJson(stateJson) ?? boardJsonName;
    const nextEntry: AiResultsCacheEntry = {
      boardName,
      stateJson,
      analysisLevel: suggestion.analysisLevel,
      bestTurn: suggestion.bestTurn,
      previewRaw: suggestion.previewRaw,
      elapsedMs: suggestion.elapsedMs,
      levelElapsedMs: suggestion.levelElapsedMs,
      lastUsedAtMs: touchedAtMs,
    };
    const otherEntries = aiResultsCacheRef.current.entries.filter(
      (entry) => entry.stateJson !== stateJson || entry.boardName !== boardName,
    );
    aiResultsCacheRef.current = saveAiResultsCacheStore({
      version: 1,
      entries: [...otherEntries, nextEntry],
    });
  };
  const handleClearAiResultsCache = () => {
    aiResultsCacheRef.current = clearAiResultsCacheStore();
    setAnalysisStatusMessage('AI cache cleared.');
  };
  const pieceRooms = gameState ? gameState.piecePositions() : null;
  const attackHistoryText = gameState ? gameState.attackHistoryText() : '';
  const attackHistoryLabel = gameState ? gameState.attackHistoryLabel() : 'Atks0,0';
  const pieceRoomMap = (() => {
    const map = new Map<PieceId, number>();
    if (!pieceRooms) {
      return map;
    }
    pieceOrder.forEach((pieceId, index) => {
      if (pieceRooms.length > index) {
        map.set(pieceId, pieceRooms[index]);
      }
    });
    return map;
  })();
  const playerStatsRows = getPlayerStatsRows(gameState);
  const getPieceIndicators = (pieceId: PieceId) => {
    return animatedPieceIndicators?.[pieceId] ?? getLivePieceIndicators(gameState, pieceId);
  };
  const renderPieceLabelStack = (
    piece: PieceRenderPosition,
    label: string,
    labelFill: string,
    labelClassName: string,
    labelSize: number,
  ) => {
    const { top, bottom } = getPieceIndicators(piece.pieceId);
    const centerX = piece.x + piece.size / 2;
    const centerY = piece.y + piece.size / 2;
    const indicatorSize = Math.min(piece.size * 0.525, labelSize * 1.2, 45);
    const indicatorOffset = piece.size * 0.3;

    return (
      <>
        {top && (
          <text
            className={`${labelClassName} piece-label--indicator`}
            x={centerX - indicatorOffset}
            y={centerY - indicatorOffset}
            fontSize={indicatorSize}
            fill={labelFill}
          >
            {top}
          </text>
        )}
        <text className={labelClassName} x={centerX} y={centerY} fontSize={labelSize} fill={labelFill}>
          {label}
        </text>
        {bottom && (
          <text
            className={`${labelClassName} piece-label--indicator`}
            x={isNormalPlayerPieceId(piece.pieceId) ? centerX + indicatorOffset : centerX}
            y={centerY + indicatorOffset}
            fontSize={indicatorSize}
            fill={labelFill}
          >
            {bottom}
          </text>
        )}
      </>
    );
  };
  const plannedEntries = pieceOrder
    .map((pieceId) => {
      const roomId = plannedMoves[pieceId];
      if (roomId === undefined) {
        return null;
      }
      return { pieceId, roomId };
    })
    .filter((entry): entry is TurnPlanEntry => entry !== null);
  const renderPieces = (() => {
    type RenderToken = {
      pieceId: PieceId;
      roomId: number;
      kind: 'actual' | 'ghost';
      x: number;
      y: number;
      size: number;
    };
    const roomTokens = new Map<number, Array<Omit<RenderToken, 'x' | 'y' | 'size'>>>();

    pieceOrder.forEach((pieceId) => {
      const roomId = pieceRoomMap.get(pieceId);
      if (roomId === undefined) {
        return;
      }
      const list = roomTokens.get(roomId) ?? [];
      list.push({ pieceId, roomId, kind: 'actual' });
      roomTokens.set(roomId, list);
    });

    plannedEntries.forEach((entry) => {
      const list = roomTokens.get(entry.roomId) ?? [];
      list.push({ pieceId: entry.pieceId, roomId: entry.roomId, kind: 'ghost' });
      roomTokens.set(entry.roomId, list);
    });

    const tokens: RenderToken[] = [];
    roomTokens.forEach((entries, roomId) => {
      const room = boardRoomById.get(roomId);
      if (!room) {
        return;
      }
      const orderedEntries = entries.slice().sort((a, b) => {
        const aIndex = pieceOrder.indexOf(a.pieceId);
        const bIndex = pieceOrder.indexOf(b.pieceId);
        if (aIndex !== bIndex) {
          return aIndex - bIndex;
        }
        if (a.kind === b.kind) {
          return 0;
        }
        return a.kind === 'actual' ? -1 : 1;
      });

      const roomRect = getRoomRect(room.coords);
      const { size, positions } = getPiecePositionsInRoom(orderedEntries.length, roomRect);
      orderedEntries.forEach((entry, index) => {
        const placement = positions[index];
        if (!placement) {
          return;
        }
        tokens.push({ ...entry, x: placement.x, y: placement.y, size });
      });
    });

    return tokens;
  })();
  const animatedPieceList = animatedPieces ?? [];

  const isPieceSelectable = (pieceId: PieceId) =>
    !hasWinner &&
    currentPlayerPieceId !== null &&
    (pieceId === currentPlayerPieceId || pieceId === 'stranger1' || pieceId === 'stranger2');

  const alliedStranger =
    currentPlayerPieceId === 'player1' ? 'stranger2' : currentPlayerPieceId === 'player2' ? 'stranger1' : null;
  const opposingStranger =
    currentPlayerPieceId === 'player1' ? 'stranger1' : currentPlayerPieceId === 'player2' ? 'stranger2' : null;

  const getPreferredSelectablePieceInRoom = (roomId: number) => {
    const preference: Array<PieceId | null> = [currentPlayerPieceId, alliedStranger, opposingStranger];
    for (const pieceId of preference) {
      if (!pieceId) {
        continue;
      }
      if (!isPieceSelectable(pieceId)) {
        continue;
      }
      if (pieceRoomMap.get(pieceId) === roomId) {
        return pieceId;
      }
    }
    return null;
  };

  const clearPendingAutoSelectedRoomPiece = () => {
    pendingAutoSelectedRoomPieceRef.current = null;
  };

  const recordOrConsumeTouchRoomDoubleTap = (roomId: number, startedWithNoSelection: boolean) => {
    const tappedAtMs = Date.now();
    const previousTouchTap = lastTouchRoomTapRef.current;
    const isDoubleTap =
      previousTouchTap !== null &&
      previousTouchTap.startedWithNoSelection &&
      previousTouchTap.roomId === roomId &&
      tappedAtMs - previousTouchTap.tappedAtMs <= touchRoomDoubleTapThresholdMs;
    if (isDoubleTap) {
      lastTouchRoomTapRef.current = null;
      return true;
    }
    lastTouchRoomTapRef.current = {
      roomId,
      tappedAtMs,
      startedWithNoSelection,
    };
    return false;
  };

  const roomClickMatchesPendingAutoSelection = (roomId: number, pieceId: PieceId | null) => {
    if (!pieceId) {
      return false;
    }
    const pendingAutoSelection = pendingAutoSelectedRoomPieceRef.current;
    return pendingAutoSelection?.roomId === roomId && pendingAutoSelection.pieceId === pieceId;
  };

  const submitCurrentPlayerMoveToRoom = (roomId: number) => {
    clearPendingAutoSelectedRoomPiece();
    setDistancePreviewRoomId(null);
    if (!currentPlayerPieceId) {
      setValidationMessage('No current player available.');
      return;
    }
    if (pieceRoomMap.get(currentPlayerPieceId) === roomId) {
      setValidationMessage(null);
      return;
    }
    const nextMoves = { ...plannedMoves, [currentPlayerPieceId]: roomId };
    const nextOrder = planOrder.includes(currentPlayerPieceId) ? planOrder : [...planOrder, currentPlayerPieceId];
    submitPlan(nextMoves, nextOrder);
  };

  const clearPlannedMoveForPiece = (pieceId: PieceId) => {
    setPlannedMoves((prev) => {
      if (prev[pieceId] === undefined) {
        return prev;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [pieceId]: _removed, ...remainingMoves } = prev;
      return remainingMoves;
    });
    setPlanOrder((prev) => (prev.includes(pieceId) ? prev.filter((id) => id !== pieceId) : prev));
  };

  const handleSelectedPieceDestination = (roomId: number) => {
    clearPendingAutoSelectedRoomPiece();
    if (!selectedPieceId) {
      return false;
    }
    if (pieceRoomMap.get(selectedPieceId) === roomId) {
      const plannedRoomId = plannedMoves[selectedPieceId];
      if (plannedRoomId !== undefined && plannedRoomId !== roomId) {
        clearPlannedMoveForPiece(selectedPieceId);
      }
      setSelectedPieceId(null);
      setDistancePreviewRoomId(null);
      setValidationMessage(null);
      return true;
    }

    setPlannedMoves((prev) => ({ ...prev, [selectedPieceId]: roomId }));
    setPlanOrder((prev) => (prev.includes(selectedPieceId) ? prev : [...prev, selectedPieceId]));
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
    return true;
  };

  const handlePieceClick = (pieceId: PieceId) => {
    lastTouchRoomTapRef.current = null;
    clearPendingAutoSelectedRoomPiece();
    if (hasWinner) {
      return;
    }

    const pieceRoomId = pieceRoomMap.get(pieceId);
    if (pieceRoomId === undefined) {
      return;
    }

    if (!isPieceSelectable(pieceId)) {
      handleRoomClick(pieceRoomId);
      return;
    }

    if (selectedPieceId) {
      handleSelectedPieceDestination(pieceRoomId);
      return;
    }

    if (selectedPieceId === pieceId) {
      setSelectedPieceId(null);
      setDistancePreviewRoomId(pieceRoomId);
      setValidationMessage(null);
      return;
    }

    setSelectedPieceId(pieceId);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
  };

  const handleRoomPointerDown = (event: PointerEvent<SVGRectElement>) => {
    // Keep the latest pointer type so touch taps can use a narrow same-room double-tap path
    // while touch-originated browser dblclick events are still ignored. Android Chrome can
    // synthesize dblclick after quick taps, including across different rooms.
    lastRoomPointerTypeRef.current = event.pointerType;
    if (event.pointerType !== 'touch') {
      lastTouchRoomTapRef.current = null;
    }
  };

  const handleRoomClick = (roomId: number) => {
    if (
      lastRoomPointerTypeRef.current === 'touch' &&
      recordOrConsumeTouchRoomDoubleTap(roomId, selectedPieceId === null)
    ) {
      handleRoomDoubleClick(roomId, { allowTouchShortcut: true });
      return;
    }
    if (hasWinner) {
      clearPendingAutoSelectedRoomPiece();
      return;
    }
    if (!selectedPieceId) {
      const preferredPiece = getPreferredSelectablePieceInRoom(roomId);
      if (preferredPiece) {
        pendingAutoSelectedRoomPieceRef.current = { roomId, pieceId: preferredPiece };
        setSelectedPieceId(preferredPiece);
        setDistancePreviewRoomId(null);
        setValidationMessage(null);
        return;
      }
      clearPendingAutoSelectedRoomPiece();
      setDistancePreviewRoomId((prev) => (prev === roomId ? null : roomId));
      setValidationMessage(null);
      return;
    }
    handleSelectedPieceDestination(roomId);
  };

  const stopAnalysisTimer = () => {
    const timerId = analysisTimerRef.current;
    if (timerId !== null) {
      window.clearInterval(timerId);
      analysisTimerRef.current = null;
    }
    const deadlineTimerId = analysisDeadlineTimerRef.current;
    if (deadlineTimerId !== null) {
      window.clearTimeout(deadlineTimerId);
      analysisDeadlineTimerRef.current = null;
    }
    analysisTimingRef.current = null;
  };

  const stopAnalysisWorker = (options?: { replace?: boolean }) => {
    if (!analysisWorkerRef.current) {
      if (options?.replace) {
        replaceIdleAnalysisWorker();
      }
      return;
    }
    analysisWorkerRef.current.terminate();
    analysisWorkerRef.current = null;
    analysisWorkerInitializedRef.current = false;
    if (options?.replace) {
      replaceIdleAnalysisWorker();
    }
  };

  const stopAnalysisRun = (
    statusMessage: string | null,
    options?: {
      terminateWorker?: boolean;
    },
  ) => {
    stopAnalysisTimer();
    if (options?.terminateWorker ?? true) {
      stopAnalysisWorker({ replace: true });
    }
    setAnalysisRunningState(false);
    analysisRunLimitsRef.current = null;
    analysisTimeLimitReachedRef.current = false;
    setAnalysisRunKind(null);
    analysisRunningLevelRef.current = null;
    setAnalysisRunningLevel(null);
    setAnalysisCurrentLevelElapsedMs(0);
    setAnalysisStatusMessage(statusMessage);
  };

  const resetAiOutputs = () => {
    setAiSuggestion(null);
    analysisRunLimitsRef.current = null;
    analysisTimeLimitReachedRef.current = false;
    setAnalysisRunKind(null);
    analysisRunningLevelRef.current = null;
    setAnalysisRunningLevel(null);
    setAnalysisElapsedMs(0);
    setAnalysisCurrentLevelElapsedMs(0);
    setAnalysisStatusMessage(null);
  };

  const stopAiAnalysisBecauseDisabled = () => {
    resetAiOutputs();
    setAnalysisStatusMessage(aiAnalysisDisabledStatusMessage);
  };

  const submitPlan = (
    moves: Partial<Record<PieceId, number>>,
    order: PieceId[],
    options?: {
      animateFromCurrentState?: boolean;
    },
  ) => {
    if (!gameState || gameState.hasWinner()) {
      return;
    }
    const initialRoomsForAnimation = options?.animateFromCurrentState
      ? Array.from(gameState.piecePositions(), (value) => Number(value))
      : null;
    const planEntries = order
      .map((pieceId) => {
        const roomId = moves[pieceId];
        if (roomId === undefined) {
          return null;
        }
        return { pieceId, roomId };
      })
      .filter((entry): entry is TurnPlanEntry => entry !== null);
    const validation = gameState.validateTurnPlan(JSON.stringify(planEntries));
    if (validation) {
      setPlannedMoves(moves);
      setPlanOrder(order);
      setValidationMessage(validation);
      return;
    }
    const submittedTurnNumber = parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? '?';
    const submittedByPieceIdRaw = gameState.currentPlayerPieceId();
    const submittedByLabel = isPieceId(submittedByPieceIdRaw) ? pieceLabels[submittedByPieceIdRaw] : '?';
    const initialPieceIndicatorsForAnimation = capturePieceIndicators(gameState);
    const currentPlayerPieceIdForSpend = gameState.currentPlayerPieceId() as PieceId;
    if (isNormalPlayerPieceId(currentPlayerPieceIdForSpend) && initialPieceIndicatorsForAnimation) {
      const currentPieceRooms = Array.from(gameState.piecePositions(), (value) => Number(value));
      const distanceBetweenRooms = (sourceRoomId: number, destRoomId: number) => {
        const distances = new Map<number, number>();
        const queue = [sourceRoomId];
        distances.set(sourceRoomId, 0);

        while (queue.length > 0) {
          const current = queue.shift();
          if (current === undefined) {
            continue;
          }
          const currentDistance = distances.get(current);
          if (currentDistance === undefined) {
            continue;
          }
          if (current === destRoomId) {
            return currentDistance;
          }
          boardRoomById.get(current)?.adjacent.forEach((neighbor) => {
            if (distances.has(neighbor)) {
              return;
            }
            distances.set(neighbor, currentDistance + 1);
            queue.push(neighbor);
          });
        }

        return 0;
      };
      const totalDistance = planEntries.reduce((sum, entry) => {
        const pieceIndex = pieceOrder.indexOf(entry.pieceId);
        const sourceRoomId = currentPieceRooms[pieceIndex];
        if (sourceRoomId === undefined) {
          return sum;
        }
        return sum + distanceBetweenRooms(sourceRoomId, entry.roomId);
      }, 0);
      const moveCardsUsed = Math.max(0, totalDistance - 1);
      const currentPlayerIndicators = initialPieceIndicatorsForAnimation[currentPlayerPieceIdForSpend] ?? {
        top: null,
        bottom: null,
      };
      initialPieceIndicatorsForAnimation[currentPlayerPieceIdForSpend] = {
        ...currentPlayerIndicators,
        top: getMoveCardIndicatorText(gameState.pieceMoveCards(currentPlayerPieceIdForSpend) - moveCardsUsed),
      };
    }
    const applyError = gameState.applyTurnPlan(JSON.stringify(planEntries));
    if (applyError) {
      setPlannedMoves(moves);
      setPlanOrder(order);
      setValidationMessage(applyError);
      return;
    }
    const submittedMovesText =
      planEntries.length > 0
        ? planEntries.map((entry) => `${pieceLabels[entry.pieceId]}@R${entry.roomId}`).join(', ')
        : '(no moves)';
    emitGameDebugLog(`Turn submitted: ${submittedByLabel}, T${submittedTurnNumber}; ${submittedMovesText}`);
    emitGameStateDebugLog(gameState);
    const nextStateSnapshot = gameState.exportStateJson();
    const currentRedoStateStack = redoStateStackRef.current;
    const nextRedoStateStack =
      currentRedoStateStack.length > 0 && currentRedoStateStack[currentRedoStateStack.length - 1] === nextStateSnapshot
        ? currentRedoStateStack.slice(0, -1)
        : [];
    setPlannedMoves({});
    setPlanOrder([]);
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    resetAiOutputs();
    redoStateStackRef.current = nextRedoStateStack;
    setRedoStateStack(nextRedoStateStack);
    saveRedoStateStack(nextRedoStateStack);
    saveGameStateSnapshot(gameState);
    const nextTurnCounter = advanceTurnCounter();
    startAnimationFromState(initialRoomsForAnimation, { indicators: initialPieceIndicatorsForAnimation });
    if (!gameState.hasWinner()) {
      startBestTurnAnalysis(false, nextTurnCounter);
    }
  };
  const handleSubmit = () => {
    if (!gameState || hasWinner) {
      return;
    }
    submitPlan(plannedMoves, planOrder);
  };

  const entriesToMovesAndOrder = (entries: TurnPlanEntry[]) => {
    const moves: Partial<Record<PieceId, number>> = {};
    const order: PieceId[] = [];
    entries.forEach((entry) => {
      moves[entry.pieceId] = entry.roomId;
      order.push(entry.pieceId);
    });
    return { moves, order };
  };

  const advanceTurnCounter = () => {
    const nextTurnCounter = turnCounterRef.current + 1;
    turnCounterRef.current = nextTurnCounter;
    setTurnCounter(nextTurnCounter);
    return nextTurnCounter;
  };

  const startBestTurnAnalysis = (
    autoSubmit: boolean,
    sourceTurnCounterOverride?: number,
    runOptions?: { kind?: AnalysisRunKind; minAnalysisLevel: number; maxAnalysisLevel: number; maxTimeMs: number },
  ) => {
    if (!gameState) {
      setAnalysisStatusMessage('Analysis unavailable.');
      return;
    }
    if (gameState.hasWinner()) {
      setAnalysisStatusMessage('Game already has a winner.');
      return;
    }
    const hasRunOverrides = runOptions !== undefined;
    const runKind = runOptions?.kind ?? 'think';
    const selectedMaxTimeMs = getAnalysisMaxTimeMs(analysisMaxTimeIndex);
    if (!hasRunOverrides && selectedMaxTimeMs === null) {
      stopAiAnalysisBecauseDisabled();
      return;
    }

    const parsedMinLevel = hasRunOverrides ? runOptions.minAnalysisLevel : Number(minAnalysisLevelDraft);
    if (!Number.isFinite(parsedMinLevel) || parsedMinLevel < 0) {
      setAnalysisStatusMessage('Min turn depth must be a number >= 0.');
      return;
    }
    const parsedMaxLevel = hasRunOverrides ? runOptions.maxAnalysisLevel : Number(maxAnalysisLevelDraft);
    if (!Number.isFinite(parsedMaxLevel) || parsedMaxLevel < 0) {
      setAnalysisStatusMessage('Max turn depth must be a number >= 0.');
      return;
    }

    const minAnalysisLevel = Math.trunc(parsedMinLevel);
    const maxAnalysisLevel = Math.trunc(parsedMaxLevel);
    const effectiveMaxAnalysisLevel = minAnalysisLevel <= maxAnalysisLevel ? maxAnalysisLevel : null;
    const maxTimeMs = hasRunOverrides
      ? Math.max(0, Math.trunc(runOptions.maxTimeMs))
      : (selectedMaxTimeMs ?? 0);
    const initialRunLimits: AnalysisRunLimits = {
      kind: runKind,
      minAnalysisLevel,
      maxAnalysisLevel: effectiveMaxAnalysisLevel,
      maxTimeMs,
    };
    analysisRunLimitsRef.current = initialRunLimits;
    setAnalysisRunKind(runKind);
    setAnalysisRunMaxTimeMs(maxTimeMs);
    if (!hasRunOverrides) {
      setMinAnalysisLevelDraft(minAnalysisLevel.toString());
      setMaxAnalysisLevelDraft(maxAnalysisLevel.toString());
      saveCurrentAiPrefs({
        minAnalysisLevel,
        maxAnalysisLevel,
        analysisMaxTimeIndex,
      });
    }
    analysisRunIdRef.current += 1;
    const runId = analysisRunIdRef.current;
    const sourceTurnCounter = sourceTurnCounterOverride ?? turnCounterRef.current;
    const sourceStateJson = gameState.exportStateJson();
    const sourceNormalTurnCount = parseNormalTurnCountFromSnapshotJson(sourceStateJson) ?? currentNormalTurnCount;
    const sourceCurrentPlayerPieceIdRaw = gameState.currentPlayerPieceId();
    const sourceCurrentPlayerPieceId = isPieceId(sourceCurrentPlayerPieceIdRaw) ? sourceCurrentPlayerPieceIdRaw : null;
    const sourceCurrentPlayerLabel = sourceCurrentPlayerPieceId ? pieceConfig[sourceCurrentPlayerPieceId].label : '?';
    let loggedStopReason = false;
    const buildAnalysisStopDebugInfo = (
      overrides?: Partial<Omit<AnalysisStopDebugInfo, 'runKind' | 'minAnalysisLevel' | 'maxAnalysisLevel' | 'maxTimeMs'>>,
    ): AnalysisStopDebugInfo => {
      const runLimits = analysisRunLimitsRef.current ?? initialRunLimits;
      return {
        runKind: runLimits.kind,
        minAnalysisLevel: runLimits.minAnalysisLevel,
        maxAnalysisLevel: runLimits.maxAnalysisLevel,
        maxTimeMs: runLimits.maxTimeMs,
        ...overrides,
      };
    };
    const debugAnalysisStopReason = (
      stoppedLevel: number | null,
      reason: string,
      details?: AnalysisStopDebugInfo,
    ) => {
      if (loggedStopReason) {
        return;
      }
      loggedStopReason = true;
      const levelText = stoppedLevel === null ? '?' : `${stoppedLevel}`;
      const detailsText = `; ${formatAnalysisStopDebugInfo(details ?? buildAnalysisStopDebugInfo())}`;
      emitAnalysisDebugLog(
        `AI analysis stopped: ${sourceCurrentPlayerLabel}, T${sourceNormalTurnCount}, L${levelText}, reason=${reason}${detailsText}`,
      );
    };
    const shouldAutoSubmitAtThisMoment = () => {
      if (autoSubmit) {
        return true;
      }
      if (sourceCurrentPlayerPieceId === 'player1') {
        return aiControlP1Ref.current;
      }
      if (sourceCurrentPlayerPieceId === 'player2') {
        return aiControlP3Ref.current;
      }
      return false;
    };
    const cachedEntry = findAiResultsCacheEntry(sourceStateJson);
    const cachedSuggestion = cachedEntry
      ? toAiSuggestionFromCacheEntry(touchAiResultsCacheEntry(cachedEntry, Date.now()), sourceTurnCounter)
      : null;
    if (cachedSuggestion) {
      emitAnalysisDebugLog(
        `${formatAnalysisLevelDebugLine(
          sourceCurrentPlayerLabel,
          sourceNormalTurnCount,
          cachedSuggestion.analysisLevel,
          cachedSuggestion.levelElapsedMs,
          cachedSuggestion.bestTurn,
        )} (cache)`,
      );
    }
    const initialAnalysisLevel = cachedSuggestion
      ? Math.max(minAnalysisLevel, cachedSuggestion.analysisLevel + 1)
      : minAnalysisLevel;

    stopAnalysisTimer();
    if (analysisIsRunningRef.current) {
      stopAnalysisWorker();
    }

    if (
      effectiveMaxAnalysisLevel !== null &&
      cachedSuggestion &&
      cachedSuggestion.analysisLevel >= effectiveMaxAnalysisLevel
    ) {
      debugAnalysisStopReason(
        Math.min(cachedSuggestion.analysisLevel, effectiveMaxAnalysisLevel),
        `max turn depth is L${effectiveMaxAnalysisLevel}`,
        buildAnalysisStopDebugInfo({
          runElapsedMs: cachedSuggestion.elapsedMs,
          remainingMs: Math.max(0, maxTimeMs - cachedSuggestion.elapsedMs),
          lastCompletedLevel: cachedSuggestion.analysisLevel,
          lastCompletedLevelElapsedMs: cachedSuggestion.levelElapsedMs,
        }),
      );
      setAiSuggestion(cachedSuggestion);
      setAnalysisRunningState(false);
      analysisRunLimitsRef.current = null;
      setAnalysisRunKind(null);
      analysisRunningLevelRef.current = null;
      setAnalysisRunningLevel(null);
      setAnalysisCurrentLevelElapsedMs(0);
      setAnalysisElapsedMs(cachedSuggestion.elapsedMs);
      const cacheStopPrefix = `Max turn depth reached from cache at L${cachedSuggestion.analysisLevel}.`;
      if (!shouldAutoSubmitAtThisMoment()) {
        setAnalysisStatusMessage(cacheStopPrefix);
        return;
      }
      if (sourceTurnCounter !== turnCounterRef.current) {
        setAnalysisStatusMessage(`${cacheStopPrefix} Auto-submit skipped because the position changed.`);
        return;
      }
      const planned = entriesToMovesAndOrder(cachedSuggestion.bestTurn.suggestedTurn);
      if (animationRef.current) {
        queuedAutoSubmitRef.current = {
          moves: planned.moves,
          order: planned.order,
          sourceTurnCounter,
        };
        setAnalysisStatusMessage(`${cacheStopPrefix} Suggested turn queued.`);
        return;
      }
      setAnalysisStatusMessage(`${cacheStopPrefix} Suggested turn submitted.`);
      submitPlan(planned.moves, planned.order, { animateFromCurrentState: true });
      return;
    }
    const cachedTerminalOutcomeText =
      cachedSuggestion && cachedSuggestion.bestTurn.isValid
        ? terminalHeuristicOutcomeText(cachedSuggestion.bestTurn.heuristicScore)
        : null;
    if (cachedSuggestion && cachedTerminalOutcomeText) {
      debugAnalysisStopReason(
        cachedSuggestion.analysisLevel,
        `${cachedTerminalOutcomeText} was found from cache at L${cachedSuggestion.analysisLevel}`,
        buildAnalysisStopDebugInfo({
          runElapsedMs: cachedSuggestion.elapsedMs,
          remainingMs: Math.max(0, maxTimeMs - cachedSuggestion.elapsedMs),
          lastCompletedLevel: cachedSuggestion.analysisLevel,
          lastCompletedLevelElapsedMs: cachedSuggestion.levelElapsedMs,
        }),
      );
      setAiSuggestion(cachedSuggestion);
      setAnalysisRunningState(false);
      analysisRunLimitsRef.current = null;
      setAnalysisRunKind(null);
      analysisRunningLevelRef.current = null;
      setAnalysisRunningLevel(null);
      setAnalysisCurrentLevelElapsedMs(0);
      setAnalysisElapsedMs(cachedSuggestion.elapsedMs);
      const cacheStopPrefix = `${cachedTerminalOutcomeText} found from cache at L${cachedSuggestion.analysisLevel}.`;
      if (!shouldAutoSubmitAtThisMoment()) {
        setAnalysisStatusMessage(cacheStopPrefix);
        return;
      }
      if (sourceTurnCounter !== turnCounterRef.current) {
        setAnalysisStatusMessage(`${cacheStopPrefix} Auto-submit skipped because the position changed.`);
        return;
      }
      const planned = entriesToMovesAndOrder(cachedSuggestion.bestTurn.suggestedTurn);
      if (animationRef.current) {
        queuedAutoSubmitRef.current = {
          moves: planned.moves,
          order: planned.order,
          sourceTurnCounter,
        };
        setAnalysisStatusMessage(`${cacheStopPrefix} Suggested turn queued.`);
        return;
      }
      setAnalysisStatusMessage(`${cacheStopPrefix} Suggested turn submitted.`);
      submitPlan(planned.moves, planned.order, { animateFromCurrentState: true });
      return;
    }

    setAiSuggestion(cachedSuggestion);
    setAnalysisRunningState(true);
    analysisRunningLevelRef.current = initialAnalysisLevel;
    setAnalysisRunningLevel(initialAnalysisLevel);
    setAnalysisCurrentLevelElapsedMs(0);
    setAnalysisStatusMessage(shouldAutoSubmitAtThisMoment() ? 'Analyzing and auto-submitting...' : 'Analyzing...');
    setAnalysisElapsedMs(0);

    const timerStart = performance.now();
    analysisTimingRef.current = {
      runStartMs: timerStart,
      levelStartMs: timerStart,
    };
    analysisTimerRef.current = window.setInterval(() => {
      const timing = analysisTimingRef.current;
      if (!timing) {
        return;
      }
      const now = performance.now();
      setAnalysisElapsedMs(Math.max(0, now - timing.runStartMs));
      setAnalysisCurrentLevelElapsedMs(Math.max(0, now - timing.levelStartMs));
    }, 100);

    let worker = analysisWorkerRef.current;
    if (!worker) {
      worker = takeIdleAnalysisWorker();
      if (!worker) {
        debugAnalysisStopReason(initialAnalysisLevel, 'analysis worker failed to start');
        stopAnalysisRun('Failed to start analysis worker.');
        return;
      }
      analysisWorkerRef.current = worker;
      analysisWorkerInitializedRef.current = false;
    }

    let currentLevel = initialAnalysisLevel;
    let deepestCompletedSuggestion: AiSuggestion | null = cachedSuggestion;
    let mostRecentCompletedLevelElapsedMs: number | null = cachedSuggestion ? cachedSuggestion.levelElapsedMs : null;
    analysisTimeLimitReachedRef.current = false;
    const getActiveRunLimits = () => analysisRunLimitsRef.current ?? initialRunLimits;

    const completeAndMaybeSubmit = (
      completionMessage: string,
      debugReason: string,
      options?: {
        terminateWorker?: boolean;
        stopLevel?: number | null;
      },
    ) => {
      const terminateWorker = options?.terminateWorker ?? false;
      const stopLevel =
        options?.stopLevel ?? deepestCompletedSuggestion?.analysisLevel ?? analysisRunningLevelRef.current;
      const activeRunLimits = getActiveRunLimits();
      const now = performance.now();
      const runElapsedMs = Math.max(0, now - timerStart);
      const timing = analysisTimingRef.current;
      const currentLevelElapsedMs = timing ? Math.max(0, now - timing.levelStartMs) : null;
      debugAnalysisStopReason(
        stopLevel,
        debugReason,
        buildAnalysisStopDebugInfo({
          runElapsedMs,
          remainingMs: Math.max(0, activeRunLimits.maxTimeMs - runElapsedMs),
          currentLevel,
          currentLevelElapsedMs,
          lastCompletedLevel: deepestCompletedSuggestion?.analysisLevel ?? null,
          lastCompletedLevelElapsedMs: mostRecentCompletedLevelElapsedMs,
        }),
      );
      if (!shouldAutoSubmitAtThisMoment()) {
        stopAnalysisRun(completionMessage, { terminateWorker });
        return;
      }
      if (!deepestCompletedSuggestion) {
        stopAnalysisRun(`${completionMessage} No suggested turn found.`, { terminateWorker });
        return;
      }
      if (sourceTurnCounter !== turnCounterRef.current) {
        stopAnalysisRun(`${completionMessage} Auto-submit skipped because the position changed.`, {
          terminateWorker,
        });
        return;
      }

      const planned = entriesToMovesAndOrder(deepestCompletedSuggestion.bestTurn.suggestedTurn);
      if (animationRef.current) {
        queuedAutoSubmitRef.current = {
          moves: planned.moves,
          order: planned.order,
          sourceTurnCounter,
        };
        stopAnalysisRun(`${completionMessage} Suggested turn queued.`, { terminateWorker });
        return;
      }
      stopAnalysisRun(`${completionMessage} Suggested turn submitted.`, { terminateWorker });
      submitPlan(planned.moves, planned.order, { animateFromCurrentState: true });
    };

    const requestCurrentLevel = () => {
      if (runId !== analysisRunIdRef.current) {
        return;
      }
      const activeRunLimits = getActiveRunLimits();
      const activeMaxAnalysisLevel = activeRunLimits.maxAnalysisLevel;
      if (activeMaxAnalysisLevel !== null && currentLevel > activeMaxAnalysisLevel) {
        const deepestCompletedLevel = deepestCompletedSuggestion?.analysisLevel ?? activeMaxAnalysisLevel;
        completeAndMaybeSubmit(
          `Max turn depth reached at L${deepestCompletedLevel}.`,
          `max turn depth is L${activeMaxAnalysisLevel}`,
          {
            stopLevel: deepestCompletedLevel,
          },
        );
        return;
      }
      const now = performance.now();
      const elapsedMs = Math.max(0, now - timerStart);
      setAnalysisElapsedMs(elapsedMs);
      if (analysisTimeLimitReachedRef.current) {
        const levelAtTimeout = analysisRunningLevelRef.current ?? currentLevel;
        const stopLevel = deepestCompletedSuggestion?.analysisLevel ?? levelAtTimeout;
        completeAndMaybeSubmit(
          `Time limit during L${levelAtTimeout}.`,
          `time limit was reached during L${levelAtTimeout}`,
          { stopLevel },
        );
        return;
      }
      if (currentLevel > activeRunLimits.minAnalysisLevel && elapsedMs >= activeRunLimits.maxTimeMs) {
        const levelAtTimeout = analysisRunningLevelRef.current ?? currentLevel;
        const stopLevel = deepestCompletedSuggestion?.analysisLevel ?? levelAtTimeout;
        completeAndMaybeSubmit(
          `Time limit during L${levelAtTimeout}.`,
          `time limit was reached during L${levelAtTimeout}`,
          { stopLevel },
        );
        return;
      }
      if (currentLevel > activeRunLimits.minAnalysisLevel && mostRecentCompletedLevelElapsedMs !== null) {
        const remainingMs = Math.max(0, activeRunLimits.maxTimeMs - elapsedMs);
        if (remainingMs < mostRecentCompletedLevelElapsedMs) {
          const deepestCompletedLevel = deepestCompletedSuggestion?.analysisLevel ?? currentLevel - 1;
          completeAndMaybeSubmit(
            `smart stop at L${deepestCompletedLevel}`,
            `L${deepestCompletedLevel} took ${formatDebugSeconds(
              mostRecentCompletedLevelElapsedMs,
            )} and there was only ${formatDebugSeconds(remainingMs)} remaining for L${currentLevel}`,
            {
              stopLevel: deepestCompletedLevel,
            },
          );
          return;
        }
      }
      analysisTimingRef.current = {
        runStartMs: timerStart,
        levelStartMs: now,
      };
      setAnalysisCurrentLevelElapsedMs(0);
      analysisRunningLevelRef.current = currentLevel;
      setAnalysisRunningLevel(currentLevel);
      if (!analysisWorkerInitializedRef.current) {
        const wasmModule = (wasmBindgenInit as WasmBindgenInitWithModule).__wbindgen_wasm_module;
        if (!wasmModule) {
          debugAnalysisStopReason(currentLevel, 'analysis worker wasm module was unavailable');
          stopAnalysisRun('Failed to initialize analysis worker.');
          return;
        }
        const initRequest: TreeSearchWorkerRequest = {
          type: 'init',
          wasmModule,
        };
        worker.postMessage(initRequest);
        analysisWorkerInitializedRef.current = true;
      }
      const request: TreeSearchWorkerRequest = {
        type: 'analyze',
        runId,
        stateJson: sourceStateJson,
        analysisLevel: currentLevel,
      };
      worker.postMessage(request);
    };

    const scheduleAnalysisDeadline = () => {
      const existingDeadlineTimerId = analysisDeadlineTimerRef.current;
      if (existingDeadlineTimerId !== null) {
        window.clearTimeout(existingDeadlineTimerId);
      }
      const activeRunLimits = getActiveRunLimits();
      const elapsedMs = Math.max(0, performance.now() - timerStart);
      const remainingMs = Math.max(0, activeRunLimits.maxTimeMs - elapsedMs);
      analysisDeadlineTimerRef.current = window.setTimeout(() => {
        if (runId !== analysisRunIdRef.current) {
          return;
        }
        const latestRunLimits = getActiveRunLimits();
        const latestElapsedMs = Math.max(0, performance.now() - timerStart);
        if (latestElapsedMs < latestRunLimits.maxTimeMs) {
          scheduleAnalysisDeadline();
          return;
        }
        analysisTimeLimitReachedRef.current = true;
        const levelAtTimeout = analysisRunningLevelRef.current ?? currentLevel;
        if (levelAtTimeout <= latestRunLimits.minAnalysisLevel) {
          return;
        }
        analysisRunIdRef.current += 1;
        const stopLevel = deepestCompletedSuggestion?.analysisLevel ?? levelAtTimeout;
        completeAndMaybeSubmit(
          `Time limit during L${levelAtTimeout}.`,
          `time limit was reached during L${levelAtTimeout}`,
          {
            terminateWorker: true,
            stopLevel,
          },
        );
      }, remainingMs);
    };
    scheduleAnalysisDeadline();

    worker.onmessage = (event: MessageEvent<TreeSearchWorkerResponse>) => {
      if (runId !== analysisRunIdRef.current) {
        return;
      }
      const now = performance.now();
      const completedElapsedMs = Math.max(0, now - timerStart);

      const message = event.data;
      if (message.type === 'analysisError') {
        debugAnalysisStopReason(
          deepestCompletedSuggestion?.analysisLevel ?? currentLevel,
          `analysis error: ${message.message}`,
        );
        stopAnalysisRun(`Analysis failed: ${message.message}`);
        return;
      }

      let bestTurn: BestTurnResponse;
      try {
        bestTurn = JSON.parse(message.analysisRaw) as BestTurnResponse;
      } catch {
        debugAnalysisStopReason(
          deepestCompletedSuggestion?.analysisLevel ?? currentLevel,
          'analysis response JSON was invalid',
        );
        stopAnalysisRun('Analysis failed: invalid JSON response.');
        return;
      }

      setAnalysisElapsedMs(completedElapsedMs);
      const timing = analysisTimingRef.current;
      const completedLevelElapsedMs = Math.max(0, now - (timing?.levelStartMs ?? now));
      setAnalysisCurrentLevelElapsedMs(completedLevelElapsedMs);
      if (!bestTurn.isValid) {
        const invalidMessage = bestTurn.validationMessage || 'No suggested turn found.';
        if (!deepestCompletedSuggestion) {
          debugAnalysisStopReason(currentLevel, invalidMessage);
          stopAnalysisRun(invalidMessage, { terminateWorker: false });
          return;
        }
        completeAndMaybeSubmit(`Analysis stopped at L${currentLevel}: ${invalidMessage}`, invalidMessage);
        return;
      }

      deepestCompletedSuggestion = {
        sourceTurnCounter,
        analysisLevel: currentLevel,
        bestTurn,
        previewRaw: message.previewRaw,
        elapsedMs: completedElapsedMs,
        levelElapsedMs: completedLevelElapsedMs,
      };
      upsertAiResultsCacheFromSuggestion(sourceStateJson, deepestCompletedSuggestion, Date.now());
      mostRecentCompletedLevelElapsedMs = completedLevelElapsedMs;
      setAiSuggestion(deepestCompletedSuggestion);
      emitAnalysisDebugLog(
        formatAnalysisLevelDebugLine(
          sourceCurrentPlayerLabel,
          sourceNormalTurnCount,
          currentLevel,
          completedLevelElapsedMs,
          bestTurn,
        ),
      );

      const activeRunLimits = getActiveRunLimits();
      const activeMaxAnalysisLevel = activeRunLimits.maxAnalysisLevel;
      if (activeMaxAnalysisLevel !== null && currentLevel >= activeMaxAnalysisLevel) {
        completeAndMaybeSubmit(
          `Max turn depth reached at L${currentLevel}.`,
          `max turn depth is L${activeMaxAnalysisLevel}`,
          {
            stopLevel: currentLevel,
          },
        );
        return;
      }

      if (isTerminalHeuristicScore(bestTurn.heuristicScore)) {
        completeAndMaybeSubmit(
          `${formatHeuristicScore(bestTurn.heuristicScore)} found at L${currentLevel}.`,
          `${formatHeuristicScore(bestTurn.heuristicScore)} was found at L${currentLevel}`,
          { stopLevel: currentLevel },
        );
        return;
      }

      if (completedElapsedMs >= activeRunLimits.maxTimeMs) {
        completeAndMaybeSubmit(
          `Time limit during L${currentLevel}.`,
          `time limit was reached during L${currentLevel}`,
          {
            stopLevel: currentLevel,
          },
        );
        return;
      }

      currentLevel += 1;
      requestCurrentLevel();
    };

    worker.onerror = () => {
      if (runId !== analysisRunIdRef.current) {
        return;
      }
      debugAnalysisStopReason(deepestCompletedSuggestion?.analysisLevel ?? currentLevel, 'analysis worker failed');
      stopAnalysisRun('Analysis worker failed.');
    };

    requestCurrentLevel();
  };

  const handleAnalysisCancel = () => {
    if (!analysisIsRunningRef.current) {
      return;
    }
    const levelAtCancel = analysisRunningLevelRef.current;
    analysisRunIdRef.current += 1;
    stopAnalysisRun(`cancelled during L${levelAtCancel ?? '?'}`, { terminateWorker: false });
    stopAnalysisWorker({ replace: true });
  };

  const handleThink = () => {
    startBestTurnAnalysis(false);
  };

  const handleMull = () => {
    if (analysisIsRunningRef.current && analysisRunKind === 'mull') {
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunLimitsRef.current = {
        kind: 'mull',
        minAnalysisLevel: 1,
        maxAnalysisLevel: 99,
        maxTimeMs: mullMaxTimeMs,
      };
      analysisTimeLimitReachedRef.current = false;
      setAnalysisRunKind('mull');
      setAnalysisRunMaxTimeMs(mullMaxTimeMs);
      setAnalysisStatusMessage('Analyzing with Mull limits...');
      return;
    }
    startBestTurnAnalysis(false, undefined, {
      kind: 'mull',
      minAnalysisLevel: 1,
      maxAnalysisLevel: 99,
      maxTimeMs: mullMaxTimeMs,
    });
  };

  const handleDoSuggestedTurn = () => {
    if (!aiSuggestion || hasWinner) {
      return;
    }

    if (aiSuggestion.sourceTurnCounter !== turnCounterRef.current) {
      setAnalysisStatusMessage('Suggested turn is stale. Run Think again.');
      return;
    }

    const planned = entriesToMovesAndOrder(aiSuggestion.bestTurn.suggestedTurn);
    if (animationRef.current) {
      queuedAutoSubmitRef.current = {
        moves: planned.moves,
        order: planned.order,
        sourceTurnCounter: aiSuggestion.sourceTurnCounter,
      };
      setAnalysisStatusMessage('Suggested turn queued.');
      return;
    }
    submitPlan(planned.moves, planned.order, { animateFromCurrentState: true });
  };

  const isAiControlledNormalPlayer = (pieceId: NormalPlayerPieceId) =>
    pieceId === 'player1' ? aiControlP1Ref.current : aiControlP3Ref.current;

  const countUndoStepsForMostRecentAiSequence = () => {
    if (!gameState) {
      return 0;
    }
    const snapshot = gameState.exportStateJson();
    const simulation = newGameStateForBoard(boardJsonName);
    try {
      const importError = simulation.importStateJson(snapshot);
      if (importError) {
        return 0;
      }
      let undoCount = 0;
      let targetPieceId: NormalPlayerPieceId | null = null;
      while (simulation.undoLastTurn()) {
        undoCount += 1;
        const actorRaw = simulation.currentPlayerPieceId();
        if (targetPieceId === null) {
          if (actorRaw === 'player1' || actorRaw === 'player2') {
            if (isAiControlledNormalPlayer(actorRaw)) {
              targetPieceId = actorRaw;
            }
          }
          continue;
        }
        const currentPieceIdRaw = simulation.currentPlayerPieceId();
        const otherNormalPlayerPieceId = targetPieceId === 'player1' ? 'player2' : 'player1';
        if (currentPieceIdRaw === otherNormalPlayerPieceId) {
          return undoCount;
        }
      }
      return targetPieceId ? undoCount : 0;
    } finally {
      simulation.free();
    }
  };
  const undoAiUndoCount = countUndoStepsForMostRecentAiSequence();
  const canUndoAi = undoAiUndoCount > 0;

  const finalizeHistoryMutation = (
    nextRedoStateStack: string[],
    options?: {
      animateFromRooms?: number[] | null;
      animateIndicators?: AnimatedPieceIndicators | null;
      validationMessage?: string | null;
    },
  ) => {
    if (!gameState) {
      return;
    }
    redoStateStackRef.current = nextRedoStateStack;
    setRedoStateStack(nextRedoStateStack);
    saveRedoStateStack(nextRedoStateStack);
    stopAnimation();
    setPlannedMoves({});
    setPlanOrder([]);
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(options?.validationMessage ?? null);
    resetAiOutputs();
    saveGameStateSnapshot(gameState);
    const nextTurnCounter = advanceTurnCounter();
    if (options?.animateFromRooms) {
      startAnimationFromState(options.animateFromRooms, {
        force: true,
        indicators: options.animateIndicators ?? null,
      });
    }
    if (!gameState.hasWinner()) {
      startBestTurnAnalysis(false, nextTurnCounter);
    }
  };

  const applyUndoSteps = (
    undoCount: number,
    options?: {
      preserveAiControl?: boolean;
      clearAllAiControl?: boolean;
    },
  ) => {
    if (!gameState) {
      return false;
    }
    const turnNumberBeforeUndo = parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? '?';
    let nextRedoStateStack = redoStateStack.slice();
    let appliedUndoCount = 0;
    for (let index = 0; index < undoCount; index += 1) {
      const snapshotBeforeUndo = gameState.exportStateJson();
      const didUndo = gameState.undoLastTurn();
      if (!didUndo) {
        break;
      }
      nextRedoStateStack = [...nextRedoStateStack, snapshotBeforeUndo];
      appliedUndoCount += 1;
    }
    if (appliedUndoCount === 0) {
      return false;
    }
    const turnNumberAfterUndo = parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? '?';
    const currentPlayerPieceIdRaw = gameState.currentPlayerPieceId();
    const currentPlayerLabel = isPieceId(currentPlayerPieceIdRaw) ? pieceLabels[currentPlayerPieceIdRaw] : '?';
    emitGameDebugLog(
      `Undo: ${appliedUndoCount} turn${appliedUndoCount === 1 ? '' : 's'}, T${turnNumberBeforeUndo} -> T${turnNumberAfterUndo}; current=${currentPlayerLabel}`,
    );
    emitGameStateDebugLog(gameState);
    if (options?.clearAllAiControl) {
      if (aiControlP1Ref.current || aiControlP3Ref.current) {
        updateAiControlPrefs(false, false);
      }
    } else if (!options?.preserveAiControl) {
      const undonePlayerPieceIdRaw = gameState.currentPlayerPieceId();
      const undonePlayerPieceId = isPieceId(undonePlayerPieceIdRaw) ? undonePlayerPieceIdRaw : null;
      const nextAiControlP1 = undonePlayerPieceId === 'player1' ? false : aiControlP1Ref.current;
      const nextAiControlP3 = undonePlayerPieceId === 'player2' ? false : aiControlP3Ref.current;
      if (nextAiControlP1 !== aiControlP1Ref.current || nextAiControlP3 !== aiControlP3Ref.current) {
        updateAiControlPrefs(nextAiControlP1, nextAiControlP3);
      }
    }
    finalizeHistoryMutation(nextRedoStateStack);
    return true;
  };

  const applyRedoSteps = (
    redoCount: number,
    options?: {
      animateFromCurrentState?: boolean;
    },
  ) => {
    if (!gameState) {
      return false;
    }
    const turnNumberBeforeRedo = parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? '?';
    let nextRedoStateStack = redoStateStack.slice();
    let appliedRedoCount = 0;
    let redoError: string | null = null;
    const animateFromCurrentState = options?.animateFromCurrentState ?? false;
    const initialPieceIndicatorsForAnimation = animateFromCurrentState ? capturePieceIndicators(gameState) : null;
    const initialRoomsForAnimation = animateFromCurrentState
      ? Array.from(gameState.piecePositions(), (value) => Number(value))
      : null;
    for (let index = 0; index < redoCount; index += 1) {
      const redoSnapshot = nextRedoStateStack[nextRedoStateStack.length - 1];
      if (!redoSnapshot) {
        break;
      }
      const importError = gameState.importStateJson(redoSnapshot);
      if (importError) {
        redoError = `Redo failed: ${importError}`;
        break;
      }
      nextRedoStateStack = nextRedoStateStack.slice(0, -1);
      appliedRedoCount += 1;
    }
    if (appliedRedoCount === 0) {
      if (redoError) {
        setValidationMessage(redoError);
      }
      return false;
    }
    const turnNumberAfterRedo = parseNormalTurnCountFromSnapshotJson(gameState.exportStateJson()) ?? '?';
    const currentPlayerPieceIdRaw = gameState.currentPlayerPieceId();
    const currentPlayerLabel = isPieceId(currentPlayerPieceIdRaw) ? pieceLabels[currentPlayerPieceIdRaw] : '?';
    emitGameDebugLog(
      `Redo: ${appliedRedoCount} turn${appliedRedoCount === 1 ? '' : 's'}, T${turnNumberBeforeRedo} -> T${turnNumberAfterRedo}; current=${currentPlayerLabel}`,
    );
    emitGameStateDebugLog(gameState);
    finalizeHistoryMutation(nextRedoStateStack, {
      animateIndicators: initialPieceIndicatorsForAnimation,
      animateFromRooms: animateFromCurrentState ? initialRoomsForAnimation : null,
      validationMessage: redoError,
    });
    return true;
  };

  const handleUndo = () => {
    if (!gameState) {
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    if (!applyUndoSteps(1)) {
      setValidationMessage('No previous turn to undo.');
    }
  };

  const handleUndoAi = () => {
    if (!gameState) {
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    if (undoAiUndoCount <= 0 || !applyUndoSteps(undoAiUndoCount, { preserveAiControl: true })) {
      setValidationMessage('No previous AI-controlled turn to undo.');
    }
  };

  const handleUndoAll = () => {
    if (!gameState) {
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    if (!applyUndoSteps(completedNormalTurnCount, { clearAllAiControl: true })) {
      setValidationMessage('No previous turn to undo.');
    }
  };

  const runRedo = (options?: { animateFromCurrentState?: boolean }) => {
    if (!gameState) {
      return;
    }
    if (redoStateStack.length === 0) {
      setValidationMessage('No undone turn to redo.');
      return;
    }
    const animateFromCurrentState = options?.animateFromCurrentState ?? false;
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    applyRedoSteps(1, { animateFromCurrentState });
  };

  const handleRedo = () => {
    runRedo();
  };

  const handleRedoAll = () => {
    if (!gameState) {
      return;
    }
    if (redoStateStack.length === 0) {
      setValidationMessage('No undone turn to redo.');
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    applyRedoSteps(redoStateStack.length);
  };

  const handleReset = () => {
    if (!gameState) {
      return;
    }
    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    gameState.resetGame();
    const resetSetup = parseSetupPrefsJson(gameState.currentNormalSetupJson(), defaultSetupPrefs, boardContext);
    emitGameDebugLog(`Game reset: ${formatSetupDebugInfo(resetSetup, boardContext)}`);
    emitGameStateDebugLog(gameState);
    stopAnimation();
    setPlannedMoves({});
    setPlanOrder([]);
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
    resetAiOutputs();
    redoStateStackRef.current = [];
    setRedoStateStack([]);
    saveRedoStateStack([]);
    saveGameStateSnapshot(gameState);
    const nextTurnCounter = advanceTurnCounter();
    if (!gameState.hasWinner()) {
      startBestTurnAnalysis(false, nextTurnCounter);
    }
  };

  const handleSetupOpen = () => {
    const nextPrefs = loadSetupPrefs(currentSetupPrefs);
    setInfoPopup(null);
    setSetupPrefsDraft(toSetupPrefsDraft(nextPrefs));
    setSetupAdvanced(shouldUseAdvancedSetup(nextPrefs, defaultSetupPrefs));
    setSetupPopupOpen(true);
    setSetupError(null);
  };

  const handleSetupCancel = () => {
    setSetupPopupOpen(false);
    setSetupError(null);
  };

  const handleSetupDraftChange = (field: keyof SetupPrefsDraft, value: string) => {
    const nextDraft = { ...setupPrefsDraft, [field]: value };
    setSetupPrefsDraft(nextDraft);
    setSetupError(null);
    const nextPrefs = parseSetupPrefsDraft(nextDraft);
    if (nextPrefs) {
      saveSetupPrefs(nextPrefs);
    }
  };

  const handleSetupBoardChange = (boardName: string) => {
    const setupBoardContext = getBoardContext(boardName);
    const setupDefaults = defaultSetupPrefsForBoard(setupBoardContext);
    const nextPrefs = loadSetupPrefs(setupDefaults, setupBoardContext);
    setSetupPrefsDraft(toSetupPrefsDraft(nextPrefs));
    setSetupAdvanced(shouldUseAdvancedSetup(nextPrefs, setupDefaults));
    setSetupError(null);
  };

  const handleAnalysisLevelStep = (direction: StepDirection) => {
    if (analysisIsRunningRef.current) {
      return;
    }
    setMinAnalysisLevelDraft((prev) => {
      const next = stepNonNegativeText(prev, direction, 1);
      saveCurrentAiPrefs({
        minAnalysisLevel: Number(next),
        maxAnalysisLevel: parseMaxAnalysisLevelDraft(maxAnalysisLevelDraft) ?? defaultMaxAnalysisLevel,
        analysisMaxTimeIndex,
      });
      return next;
    });
  };

  const handleAnalysisLevelChange = (rawLevel: string) => {
    setMinAnalysisLevelDraft(rawLevel);
    const parsedMinAnalysisLevel = parseMinAnalysisLevelDraft(rawLevel);
    if (parsedMinAnalysisLevel === null) {
      return;
    }
    saveCurrentAiPrefs({
      minAnalysisLevel: parsedMinAnalysisLevel,
      maxAnalysisLevel: parseMaxAnalysisLevelDraft(maxAnalysisLevelDraft) ?? defaultMaxAnalysisLevel,
      analysisMaxTimeIndex,
    });
  };

  const handleMaxAnalysisLevelStep = (direction: StepDirection) => {
    if (analysisIsRunningRef.current) {
      return;
    }
    setMaxAnalysisLevelDraft((prev) => {
      const next = stepNonNegativeText(prev, direction, 1);
      saveCurrentAiPrefs({
        minAnalysisLevel: parseMinAnalysisLevelDraft(minAnalysisLevelDraft) ?? defaultMinAnalysisLevel,
        maxAnalysisLevel: Number(next),
        analysisMaxTimeIndex,
      });
      return next;
    });
  };

  const handleMaxAnalysisLevelChange = (rawLevel: string) => {
    setMaxAnalysisLevelDraft(rawLevel);
    const parsedMaxAnalysisLevel = parseMaxAnalysisLevelDraft(rawLevel);
    if (parsedMaxAnalysisLevel === null) {
      return;
    }
    saveCurrentAiPrefs({
      minAnalysisLevel: parseMinAnalysisLevelDraft(minAnalysisLevelDraft) ?? defaultMinAnalysisLevel,
      maxAnalysisLevel: parsedMaxAnalysisLevel,
      analysisMaxTimeIndex,
    });
  };

  const handleAnalysisMaxTimeStep = (direction: StepDirection) => {
    if (analysisIsRunningRef.current) {
      return;
    }
    const nextIndex = clampAnalysisMaxTimeIndex(direction === 'down' ? analysisMaxTimeIndex - 1 : analysisMaxTimeIndex + 1);
    setAnalysisMaxTimeIndex(nextIndex);
    saveCurrentAiPrefs({
      analysisMaxTimeIndex: nextIndex,
    });
    if (getAnalysisMaxTimeMs(nextIndex) === null) {
      stopAiAnalysisBecauseDisabled();
    } else if (analysisStatusMessage === aiAnalysisDisabledStatusMessage) {
      setAnalysisStatusMessage(null);
    }
  };

  const handleAnalysisMaxTimeChange = (rawIndex: string) => {
    if (analysisIsRunningRef.current) {
      return;
    }
    const parsed = Number(rawIndex);
    const nextIndex = Number.isFinite(parsed) ? clampAnalysisMaxTimeIndex(Math.trunc(parsed)) : analysisMaxTimeIndex;
    setAnalysisMaxTimeIndex(nextIndex);
    saveCurrentAiPrefs({
      analysisMaxTimeIndex: nextIndex,
    });
    if (getAnalysisMaxTimeMs(nextIndex) === null) {
      stopAiAnalysisBecauseDisabled();
    } else if (analysisStatusMessage === aiAnalysisDisabledStatusMessage) {
      setAnalysisStatusMessage(null);
    }
  };

  const handleAiControlChange = (player: 'player1' | 'player2', checked: boolean) => {
    if (player === 'player1') {
      updateAiControlPrefs(checked, aiControlP3Ref.current);
      return;
    }
    updateAiControlPrefs(aiControlP1Ref.current, checked);
  };

  const handleAiShowOnBoardChange = (player: 'player1' | 'player2', checked: boolean) => {
    if (player === 'player1') {
      updateAiShowOnBoardPrefs(checked, aiShowOnBoardP3Ref.current);
      return;
    }
    updateAiShowOnBoardPrefs(aiShowOnBoardP1Ref.current, checked);
  };

  const handleSetupAdvancedChange = (checked: boolean) => {
    setSetupAdvanced(checked);
    setSetupError(null);
  };

  const handleSetupCurrentPlayerChange = (pieceId: NormalPlayerPieceId) => {
    const nextDraft = { ...setupPrefsDraft, currentPlayerPieceId: pieceId };
    setSetupPrefsDraft(nextDraft);
    setSetupError(null);
    const nextPrefs = parseSetupPrefsDraft(nextDraft);
    if (nextPrefs) {
      saveSetupPrefs(nextPrefs);
    }
  };

  const handleSetupStep = (
    field:
      | 'moveCards'
      | 'weaponCards'
      | 'failureCards'
      | 'player2MoveCards'
      | 'player2WeaponCards'
      | 'player2FailureCards',
    direction: StepDirection,
  ) => {
    handleSetupDraftChange(field, stepNonNegativeText(setupPrefsDraft[field], direction, 1));
  };

  const handleSetupRoomStep = (field: SetupRoomField, direction: StepDirection) => {
    if (setupBoardRooms.length === 0) {
      return;
    }
    const currentRoomId = Number(setupPrefsDraft[field]);
    const currentIndex = setupBoardRooms.findIndex((room) => room.id === currentRoomId);
    const baseIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex =
      direction === 'down'
        ? (baseIndex - 1 + setupBoardRooms.length) % setupBoardRooms.length
        : (baseIndex + 1) % setupBoardRooms.length;
    const nextRoom = setupBoardRooms[nextIndex];
    if (!nextRoom) {
      return;
    }
    handleSetupDraftChange(field, nextRoom.id.toString());
  };

  const handleRandomSetupRooms = () => {
    if (setupBoardRooms.length === 0) {
      return;
    }
    const randomRoomId = () => {
      const index = Math.min(setupBoardRooms.length - 1, Math.floor(Math.random() * setupBoardRooms.length));
      return setupBoardRooms[index].id.toString();
    };
    const doctorRoomId = randomRoomId();
    const playerRoomId = randomRoomId();
    const nextDraft = {
      ...setupPrefsDraft,
      doctorRoomId,
      player1RoomId: playerRoomId,
      stranger1RoomId: playerRoomId,
      player2RoomId: playerRoomId,
      stranger2RoomId: playerRoomId,
    };
    setSetupPrefsDraft(nextDraft);
    setSetupError(null);
    const nextPrefs = parseSetupPrefsDraft(nextDraft);
    if (nextPrefs) {
      saveSetupPrefs(nextPrefs);
    }
  };

  const handleRestoreSetupDefaults = () => {
    const setupBoardContext = getBoardContext(setupPrefsDraft.boardName);
    const setupDefaults = defaultSetupPrefsForBoard(setupBoardContext);
    setSetupPrefsDraft(toSetupPrefsDraft(setupDefaults));
    setSetupAdvanced(false);
    saveSetupPrefs(setupDefaults, setupBoardContext);
    setSetupError(null);
  };

  const handleStartNewGameWithSetup = () => {
    if (!gameState) {
      return;
    }
    const parsedSetup = parseSetupPrefsDraft(setupPrefsDraft);
    if (!parsedSetup) {
      setSetupError('Setup values must be valid numbers and rooms.');
      return;
    }
    const setupBoardContext = getBoardContext(parsedSetup.boardName);
    const setupDefaults = defaultSetupPrefsForBoard(setupBoardContext);
    const setupToStart = buildSetupPrefsForStart(parsedSetup, setupDefaults, setupAdvanced);

    if (setupToStart.boardName !== boardJsonName) {
      const boardLoadError = gameState.loadBoard(setupToStart.boardName);
      if (boardLoadError) {
        setSetupError(boardLoadError);
        return;
      }
    }

    const startError = gameState.startNewGameWithSetup(
      setupToStart.moveCards,
      setupToStart.weaponCards,
      setupToStart.failureCards,
      setupToStart.player2MoveCards,
      setupToStart.player2WeaponCards,
      setupToStart.player2FailureCards,
      setupToStart.doctorRoomId,
      setupToStart.player1RoomId,
      setupToStart.stranger1RoomId,
      setupToStart.player2RoomId,
      setupToStart.stranger2RoomId,
      setupToStart.player1Strength,
      setupToStart.stranger1Strength,
      setupToStart.player2Strength,
      setupToStart.stranger2Strength,
      setupToStart.turnId,
      setupToStart.currentPlayerPieceId,
    );
    if (startError) {
      setSetupError(startError);
      return;
    }
    emitGameDebugLog(`New game started: ${formatSetupDebugInfo(setupToStart, setupBoardContext)}`);
    emitGameStateDebugLog(gameState);

    if (analysisIsRunningRef.current) {
      analysisRunIdRef.current += 1;
      stopAnalysisRun('Analysis stopped because the position changed.');
    }
    stopAnimation();
    setPlannedMoves({});
    setPlanOrder([]);
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
    setSetupPopupOpen(false);
    setSetupError(null);
    resetAiOutputs();
    saveSelectedBoardName(setupToStart.boardName);
    setSelectedBoardName(setupToStart.boardName);
    saveSetupPrefs(setupToStart, setupBoardContext);
    setSetupPrefsDraft(toSetupPrefsDraft(setupToStart));
    setSetupAdvanced(shouldUseAdvancedSetup(setupToStart, setupDefaults));
    redoStateStackRef.current = [];
    setRedoStateStack([]);
    saveRedoStateStack([], setupToStart.boardName);
    saveGameStateSnapshot(gameState, setupToStart.boardName);
    const nextTurnCounter = advanceTurnCounter();
    if (!gameState.hasWinner()) {
      startBestTurnAnalysis(false, nextTurnCounter);
    }
  };

  const handleCancel = () => {
    stopAnimation();
    setPlannedMoves({});
    setPlanOrder([]);
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    setValidationMessage(null);
  };

  const handleInfoToggle = (kind: InfoPopupKind) => {
    setInfoPopup((prev) => (prev === kind ? null : kind));
  };

  const handleRoomMouseDown = (event: MouseEvent<SVGRectElement>, roomId: number) => {
    if (hasWinner) {
      return;
    }
    if (event.button !== 1) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!selectedPieceId) {
      handleSubmit();
      return;
    }
    if (pieceRoomMap.get(selectedPieceId) === roomId) {
      const plannedRoomId = plannedMoves[selectedPieceId];
      if (plannedRoomId !== undefined && plannedRoomId !== roomId) {
        clearPlannedMoveForPiece(selectedPieceId);
      }
      setSelectedPieceId(null);
      setDistancePreviewRoomId(null);
      setValidationMessage(null);
      return;
    }
    const nextMoves = { ...plannedMoves, [selectedPieceId]: roomId };
    const nextOrder = planOrder.includes(selectedPieceId) ? planOrder : [...planOrder, selectedPieceId];
    setSelectedPieceId(null);
    setDistancePreviewRoomId(null);
    submitPlan(nextMoves, nextOrder);
  };

  const handleRoomDoubleClick = (roomId: number, options?: { allowTouchShortcut?: boolean }) => {
    // Browser dblclick is trusted for desktop mouse input. Touch gets its own same-room
    // double-tap path in handleRoomClick, so any browser-reported touch dblclick is ignored
    // unless we intentionally routed here from that custom touch path.
    if (lastRoomPointerTypeRef.current === 'touch' && !options?.allowTouchShortcut) {
      return;
    }
    const matchesPendingAutoSelection = roomClickMatchesPendingAutoSelection(roomId, selectedPieceId);
    clearPendingAutoSelectedRoomPiece();
    if (hasWinner) {
      return;
    }
    if (selectedPieceId && !matchesPendingAutoSelection) {
      return;
    }
    if (selectedPieceId) {
      setSelectedPieceId(null);
      setDistancePreviewRoomId(null);
    }
    submitCurrentPlayerMoveToRoom(roomId);
  };

  const planSummary =
    planOrder.length === 0
      ? 'No moves planned.'
      : planOrder
          .map((pieceId) => {
            const roomId = plannedMoves[pieceId];
            return roomId === undefined
              ? `${pieceConfig[pieceId].label}@R?`
              : `${pieceConfig[pieceId].label}@R${roomId}`;
          })
          .join(', ');
  const previewDisplay = (() => {
    if (!gameState) {
      return {
        message: 'Preview unavailable.',
        tokens: [] as PreviewToken[],
      };
    }
    const rawPreview = gameState.previewTurnPlan(JSON.stringify(plannedEntries));
    return toPreviewDisplay(rawPreview, 'Invalid plan.');
  })();
  const aiSuggestionIsCurrent = aiSuggestion ? aiSuggestion.sourceTurnCounter === turnCounter : false;
  const aiPreviewDisplay = (() => {
    if (!aiSuggestion) {
      return {
        message: analysisIsRunning ? 'Analyzing...' : 'Run Think to get a suggested turn.',
        tokens: [] as PreviewToken[],
      };
    }

    if (!aiSuggestion.bestTurn.isValid) {
      return {
        message: aiSuggestion.bestTurn.validationMessage || 'No suggested turn found.',
        tokens: [] as PreviewToken[],
      };
    }

    if (!aiSuggestion.previewRaw) {
      return {
        message: 'Preview unavailable.',
        tokens: [] as PreviewToken[],
      };
    }

    return toPreviewDisplay(aiSuggestion.previewRaw, 'Suggested preview unavailable.');
  })();
  const aiStatusText = analysisIsRunning
    ? `L${analysisRunningLevel ?? '?'}, ${formatWholeSeconds(analysisCurrentLevelElapsedMs)}/${formatWholeSeconds(analysisElapsedMs)}`
    : (analysisStatusMessage ?? (aiSuggestion ? 'Analysis ready.' : 'Idle'));
  const aiCanDoIt = Boolean(aiSuggestion && aiSuggestion.bestTurn.isValid && aiSuggestionIsCurrent);
  const aiSuggestedTurnText =
    aiSuggestion && aiSuggestion.bestTurn.isValid
      ? formatSuggestedTurnText(aiSuggestion.bestTurn)
      : 'No suggestion yet.';
  const aiStatsText =
    aiSuggestion && aiSuggestion.bestTurn.isValid
      ? `L${aiSuggestion.analysisLevel}, ${formatHeuristicScore(aiSuggestion.bestTurn.heuristicScore)}, ${formatElapsedTime(aiSuggestion.levelElapsedMs)}`
      : '-';
  const aiStaleMessage = aiSuggestion && !aiSuggestionIsCurrent ? 'Suggestion is stale. Run Think again.' : null;
  const aiShowOnBoardEnabledForCurrentPlayer =
    currentPlayerPieceId === 'player1' ? aiShowOnBoardP1 : currentPlayerPieceId === 'player2' ? aiShowOnBoardP3 : false;
  const analysisMaxTimeMs = getAnalysisMaxTimeMs(analysisMaxTimeIndex);
  const aiAnalysisDisabled = analysisMaxTimeMs === null;
  const aiIsMulling = analysisIsRunning && analysisRunKind === 'mull';
  const mullButtonDisabled = hasWinner || aiIsMulling;
  const plannerMullButtonLabel = aiIsMulling ? 'Cancel' : 'Mull';
  const displayedAnalysisMaxTimeMs = analysisIsRunning ? analysisRunMaxTimeMs : (analysisMaxTimeMs ?? 0);
  const aiSuggestionBoardText = (() => {
    if (hasWinner || (!aiIsMulling && !aiShowOnBoardEnabledForCurrentPlayer)) {
      return null;
    }
    const runningTimeText = `(${formatWholeSeconds(analysisCurrentLevelElapsedMs)}/${formatWholeSeconds(analysisElapsedMs)}/${formatWholeSeconds(displayedAnalysisMaxTimeMs)})`;
    if (analysisIsRunning && !aiSuggestion) {
      return `L${analysisRunningLevel ?? '?'}: thinking ${runningTimeText}`;
    }
    if (!aiSuggestion || !aiSuggestionIsCurrent) {
      return null;
    }
    if (!aiSuggestion.bestTurn.isValid) {
      return null;
    }
    const suggestedTurnTextForBoard = formatSuggestedTurnTextForBoard(aiSuggestion.bestTurn);
    if (!suggestedTurnTextForBoard) {
      return null;
    }
    const suggestionElapsedText = formatRoundedSeconds(aiSuggestion.levelElapsedMs);
    const terminalOutcomeLabel = terminalHeuristicOutcomeLabel(aiSuggestion.bestTurn.heuristicScore);
    const statusText = analysisIsRunning
      ? runningTimeText
      : terminalOutcomeLabel
        ? `(done, ${terminalOutcomeLabel})`
        : '(done)';
    return `L${aiSuggestion.analysisLevel}(${suggestionElapsedText}): ${suggestedTurnTextForBoard} ${statusText}`;
  })();

  if (!initialAutoAnalysisQueuedRef.current && gameState && !hasWinner) {
    initialAutoAnalysisQueuedRef.current = true;
    queueMicrotask(() => {
      startBestTurnAnalysis(false, turnCounterRef.current);
    });
  }

  const selectedLabel = selectedPieceId ? pieceConfig[selectedPieceId].label : 'None';
  const selectedSuffix = selectedPieceId && plannedMoves[selectedPieceId] !== undefined ? ' (update)' : '';
  const selectedRoomId = selectedPieceId ? pieceRoomMap.get(selectedPieceId) : undefined;
  const distanceOriginRoomId = selectedRoomId ?? distancePreviewRoomId ?? undefined;
  const distanceByRoom = distanceOriginRoomId !== undefined ? buildRoomDistanceMap(distanceOriginRoomId) : null;
  const movePossibleByRoom = (() => {
    if (!selectedPieceId || selectedRoomId === undefined || !distanceByRoom || !gameState) {
      return null;
    }
    const nextOrder = planOrder.includes(selectedPieceId) ? planOrder : [...planOrder, selectedPieceId];
    const byRoom = new Map<number, boolean>();
    distanceByRoom.forEach((_distance, roomId) => {
      if (roomId === selectedRoomId) {
        return;
      }
      const nextMoves = { ...plannedMoves, [selectedPieceId]: roomId };
      const nextEntries = nextOrder
        .map((pieceId) => {
          const plannedRoomId = nextMoves[pieceId];
          if (plannedRoomId === undefined) {
            return null;
          }
          return { pieceId, roomId: plannedRoomId };
        })
        .filter((entry): entry is TurnPlanEntry => entry !== null);
      const validation = gameState.validateTurnPlan(JSON.stringify(nextEntries));
      byRoom.set(roomId, !validation);
    });
    return byRoom;
  })();
  const aiSuggestionOverlayVisible = Boolean(aiSuggestionBoardText && !animatedPieces && !animationRef.current);
  const boardOverlayText = actionOverlay ?? (aiSuggestionOverlayVisible ? aiSuggestionBoardText : null);
  const boardOverlayIsAiSuggestion = actionOverlay === null && aiSuggestionOverlayVisible;
  const actionOverlayLayout = boardOverlayText
    ? buildActionOverlayLayout(boardOverlayText, { bottomAlignToBoardShell: boardOverlayIsAiSuggestion })
    : null;
  const setupDraftBoardContext = getBoardContext(setupPrefsDraft.boardName);
  const setupBoardRooms = setupDraftBoardContext.rooms;
  const aiSuggestionBoardDoButtonVisible =
    actionOverlay === null && aiSuggestionOverlayVisible && aiCanDoIt && !hasWinner;
  const aiSuggestionBoardDoButtonLayout =
    aiSuggestionBoardDoButtonVisible && actionOverlayLayout
      ? (() => {
          const buttonHeight = Math.round(actionOverlayLayout.boxHeight * 1.5);
          const buttonWidth = Math.round(buttonHeight * 1.45);
          const gap = 8;
          const margin = 10;
          const desiredX = actionOverlayLayout.boxX + actionOverlayLayout.boxWidth + gap;
          const maxX = boardWidth - margin - buttonWidth;
          const x = Math.min(Math.max(desiredX, margin), maxX);
          const y = boardOverlayIsAiSuggestion
            ? boardHeight - buttonHeight
            : Math.min(
                Math.max(actionOverlayLayout.boxY + (actionOverlayLayout.boxHeight - buttonHeight) / 2, margin),
                boardHeight - margin - buttonHeight,
              );
          return {
            x,
            y,
            width: buttonWidth,
            height: buttonHeight,
          };
        })()
      : null;
  const actionOverlayBoxClassName =
    boardOverlayIsAiSuggestion
      ? 'action-overlay-box action-overlay-box--ai-suggestion'
      : 'action-overlay-box';
  const winnerOverlayLayout = winnerOverlayText ? buildWinnerOverlayLayout(winnerOverlayText) : null;
  const showWinnerOverlay = hasWinner && !animatedPieces;

  const stopAnimation = (options?: { executeQueuedAutoSubmit?: boolean }) => {
    if (deferredAutoSubmitRafRef.current !== null) {
      cancelAnimationFrame(deferredAutoSubmitRafRef.current);
      deferredAutoSubmitRafRef.current = null;
    }
    const queuedAutoSubmit = options?.executeQueuedAutoSubmit ? queuedAutoSubmitRef.current : null;
    queuedAutoSubmitRef.current = null;
    const current = animationRef.current;
    if (current && current.rafId !== null) {
      cancelAnimationFrame(current.rafId);
    }
    animationRef.current = null;
    setAnimatedPieces(null);
    setAnimatedPieceIndicators(null);
    setActionOverlay(null);
    setActionHighlightPieceId(null);
    const autoSubmitAfterAnimation = queuedAutoSubmit;
    if (!autoSubmitAfterAnimation || autoSubmitAfterAnimation.sourceTurnCounter !== turnCounterRef.current) {
      return;
    }
    deferredAutoSubmitRafRef.current = requestAnimationFrame(() => {
      deferredAutoSubmitRafRef.current = null;
      if (autoSubmitAfterAnimation.sourceTurnCounter !== turnCounterRef.current || animationRef.current) {
        return;
      }
      submitPlan(autoSubmitAfterAnimation.moves, autoSubmitAfterAnimation.order, { animateFromCurrentState: true });
    });
  };

  const buildPositionsForRooms = (roomIds: number[]) => {
    const roomTokens = new Map<number, PieceId[]>();
    pieceOrder.forEach((pieceId, index) => {
      const roomId = roomIds[index];
      if (roomId === undefined) {
        return;
      }
      const list = roomTokens.get(roomId) ?? [];
      list.push(pieceId);
      roomTokens.set(roomId, list);
    });

    const positionsByPiece = new Map<
      PieceId,
      { pieceId: PieceId; roomId: number; x: number; y: number; size: number }
    >();
    roomTokens.forEach((pieceIds, roomId) => {
      const room = boardRoomById.get(roomId);
      if (!room) {
        return;
      }
      const orderedPieceIds = pieceOrder.filter((pieceId) => pieceIds.includes(pieceId));
      const roomRect = getRoomRect(room.coords);
      const { size, positions } = getPiecePositionsInRoom(orderedPieceIds.length, roomRect);
      orderedPieceIds.forEach((pieceId, index) => {
        const placement = positions[index];
        if (!placement) {
          return;
        }
        positionsByPiece.set(pieceId, {
          pieceId,
          roomId,
          x: placement.x,
          y: placement.y,
          size,
        });
      });
    });

    return pieceOrder
      .map((pieceId) => positionsByPiece.get(pieceId))
      .filter(
        (piece): piece is { pieceId: PieceId; roomId: number; x: number; y: number; size: number } =>
          piece !== undefined,
      );
  };

  const startAnimationFromState = (
    initialRoomsOverride?: number[] | null,
    options?: { force?: boolean; indicators?: AnimatedPieceIndicators | null },
  ) => {
    if (!gameState || (!animationEnabled && !options?.force)) {
      return;
    }
    const framesRaw = gameState.animationFrames();
    if (!framesRaw || framesRaw.length === 0) {
      return;
    }
    const frameCount = Math.floor(framesRaw.length / pieceOrder.length);
    if (frameCount <= 1) {
      return;
    }
    const frameRooms = Array.from({ length: frameCount }, (_, frameIndex) =>
      pieceOrder.map((_pieceId, index) => framesRaw[frameIndex * pieceOrder.length + index]),
    );
    const frames = frameRooms.map((rooms) => buildPositionsForRooms(rooms));
    const summaryText = gameState.prevTurnSummaryVerbose();
    const summaryActions = parseActionSummaries(summaryText);
    const actionTexts = summaryActions.map((action, index) => {
      if (!action) {
        return null;
      }
      const pieceIndex = pieceOrder.indexOf(action.actor);
      const rooms = frameRooms[index + 1];
      if (pieceIndex < 0 || !rooms) {
        return null;
      }
      const roomId = rooms[pieceIndex];
      const label = pieceConfig[action.actor].label;
      return action.kind === 'loot' ? `${label} loots R${roomId}` : `${label} attacks in R${roomId}`;
    });
    const actionHighlights = summaryActions.map((action) => {
      if (!action) {
        return null;
      }
      return action.kind === 'attack' ? 'doctor' : action.actor;
    });
    const segments: Array<{
      from: Array<{ pieceId: PieceId; roomId: number; x: number; y: number; size: number }>;
      to: Array<{ pieceId: PieceId; roomId: number; x: number; y: number; size: number }>;
      actionText: string | null;
      highlightPieceId: PieceId | null;
    }> = [];
    for (let index = 0; index < frames.length - 1; index += 1) {
      const startRooms = frameRooms[index];
      const endRooms = frameRooms[index + 1];
      const actionRooms = [startRooms[0], ...endRooms.slice(1)];
      const actionText = actionTexts[index] ?? null;
      const startEqualsAction = startRooms.every((roomId, roomIndex) => roomId === actionRooms[roomIndex]);
      const actionEqualsEnd = actionRooms.every((roomId, roomIndex) => roomId === endRooms[roomIndex]);
      const startFrame = frames[index];
      const endFrame = frames[index + 1];
      const actionFrame = startEqualsAction
        ? startFrame
        : actionEqualsEnd
          ? endFrame
          : buildPositionsForRooms(actionRooms);

      if (!startEqualsAction) {
        segments.push({ from: startFrame, to: actionFrame, actionText: null, highlightPieceId: null });
      }
      if (actionText) {
        segments.push({
          from: actionFrame,
          to: actionFrame,
          actionText,
          highlightPieceId: actionHighlights[index] ?? null,
        });
      }
      if (!actionEqualsEnd) {
        segments.push({ from: actionFrame, to: endFrame, actionText: null, highlightPieceId: null });
      }
    }

    if (initialRoomsOverride && initialRoomsOverride.length === pieceOrder.length) {
      const hasAnyDiff = initialRoomsOverride.some((roomId, index) => roomId !== frameRooms[0][index]);
      if (hasAnyDiff) {
        const initialFrame = buildPositionsForRooms(initialRoomsOverride);
        if (initialFrame.length > 0) {
          segments.unshift({
            from: initialFrame,
            to: frames[0],
            actionText: null,
            highlightPieceId: null,
          });
        }
      }
    }

    stopAnimation();

    if (segments.length === 0) {
      return;
    }

    const animationState = {
      segments,
      segmentIndex: 0,
      startTime: performance.now(),
      durationMs: 1000 / animationSpeed,
      speed: animationSpeed,
      rafId: null as number | null,
    };

    const tick = (now: number) => {
      if (!animationRef.current) {
        return;
      }
      const current = animationRef.current;
      const segment = current.segments[current.segmentIndex];
      const progress = Math.min(1, (now - current.startTime) / current.durationMs);
      const from = segment.from;
      const to = segment.to;
      const interpolated = from.map((piece, index) => {
        const target = to[index] ?? piece;
        return {
          pieceId: piece.pieceId,
          roomId: target.roomId,
          x: piece.x + (target.x - piece.x) * progress,
          y: piece.y + (target.y - piece.y) * progress,
          size: piece.size + (target.size - piece.size) * progress,
        };
      });

      setAnimatedPieces(interpolated);

      if (progress >= 1) {
        const nextIndex = current.segmentIndex + 1;
        if (nextIndex >= current.segments.length) {
          stopAnimation({ executeQueuedAutoSubmit: true });
          return;
        }
        current.segmentIndex = nextIndex;
        current.startTime = now;
        current.durationMs = 1000 / current.speed;
        setActionOverlay(current.segments[nextIndex].actionText);
        setActionHighlightPieceId(current.segments[nextIndex].highlightPieceId);
      }

      current.rafId = requestAnimationFrame(tick);
    };

    animationRef.current = animationState;
    setAnimatedPieceIndicators(options?.indicators ?? null);
    setAnimatedPieces(segments[0].from);
    setActionOverlay(segments[0].actionText);
    setActionHighlightPieceId(segments[0].highlightPieceId);
    animationRef.current.rafId = requestAnimationFrame(tick);
  };

  const handleAnimationEnabledChange = (checked: boolean) => {
    setAnimationEnabled(checked);
    saveAnimationPrefs({
      animationEnabled: checked,
      animationSpeedIndex,
    });
    if (!checked) {
      stopAnimation();
    }
  };

  const handleSpeedChange = (direction: 'slower' | 'faster') => {
    setAnimationSpeedIndex((prev) => {
      const next = direction === 'slower' ? Math.max(0, prev - 1) : Math.min(animationSpeeds.length - 1, prev + 1);
      saveAnimationPrefs({
        animationEnabled,
        animationSpeedIndex: next,
      });
      if (animationRef.current) {
        animationRef.current.speed = animationSpeeds[next];
      }
      return next;
    });
  };

  if (!gameState) {
    return <div className="play-area-error">Failed to create game state.</div>;
  }

  const handleBoardMouseDown = (event: MouseEvent<SVGSVGElement>) => {
    if (hasWinner) {
      return;
    }
    if (event.button !== 1) {
      return;
    }
    if (selectedPieceId) {
      return;
    }
    event.preventDefault();
    handleSubmit();
  };

  const handleBoardContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const currentPlayerColor = currentPlayerPieceId ? pieceConfig[currentPlayerPieceId].color : 'var(--line)';
  const currentPlayerTextColor = currentPlayerPieceId ? pieceConfig[currentPlayerPieceId].textColor : 'var(--ink)';
  const boardOutlineColor = animatedPieces ? '#8c8c8c' : currentPlayerColor;
  let infoPopupTitle =
    infoPopup === 'rules'
      ? 'Rules'
      : infoPopup === 'turnPlanner'
        ? 'Turn Planner Help'
        : infoPopup === 'ai'
          ? 'AI Help'
          : infoPopup === 'playerInfoBox'
            ? 'Player Info Box Help'
            : 'UNKNOWN3854';
  infoPopupTitle += ' (click anywhere to close)';
  const infoPopupContent =
    infoPopup === 'rules' ? (
      <>
        <p>
          This is basically Kill Doctor Lucky but without any randomness and there are no ties; if the stranger before
          you in the turn order kills the Dr, you win. The teams are [P1,p4] and [P2,p3]. P1 and P3 are normal players.
          p3 and p4 are strangers.
        </p>
        <p>What the cards can do...</p>
        <ul>
          <li>Every move card gives 1 movement point and can be converted 1:1 into clovers for defense.</li>
          <li>Every weapon card gives 2 attack points and can be converted 1:1 into clovers for defense.</li>
          <li>Every failure card gives 2 clovers.</li>
        </ul>
        <p>How to gain and use the cards...</p>
        <ul>
          <li>
            Every time you loot a room ("draw a card") as your action, you gain 11/32 of a move card, 11/32 of a weapon
            card, and 11/32 of a failure card. Looting a room is automatic; if you can loot during the action phase of
            your turn, then you do.
          </li>
          <li>
            Every time you attack as your action, you use 1 weapon card if you have 1 or more weapon cards. If you have
            less than 1 weapon card, you use the fractional weapon card you have. You can't decline weapon card use.
            Like looting, attacking is automatically done if possible during your action phase.
          </li>
          <li>
            Every time you defend, cards can be fractionally converted into clovers. Cards are converted to clovers as
            needed in this order: failure, weapon, move.
          </li>
        </ul>
        <p>
          And again: if the stranger before you in the turn order kills the Dr, you win; the teams are [P1,p4] and
          [P2,p3]. If P1 or p4 attack, P3 defends. If p2 or P3 attack, P4 defends. Strangers never contribute clovers to
          defend any attack.
        </p>
      </>
    ) : infoPopup === 'turnPlanner' ? (
      <>
        <h4>Movement / Turn Planning</h4>
        <p>
          There are a few ways to choose movements for your piece and the strangers. You can click the "Submit" button
          to submit your plan, but there are other ways via middle clicks and double clicks to submit your plan. Since
          looting the room (drawing a card) and attacking are done automatically if possible during the action phase,
          movements are the only decision to make.
        </p>
        <ul>
          <li>
            The most basic way to choose a movement is to click a piece and then click a destination room. You can
            select pieces and change their destinations multiple times.
          </li>
          <li>
            Once a piece has a planned destination, you will see a "ghost" version of that piece in the destination room
            and the original piece is grayed out a bit.
          </li>
          <li>
            You do not always have to click a piece to select it; if no piece is selected and you click a room with
            movable pieces, it will select the first piece from this list: [yourself, your allied stranger, the opposing
            stranger].
          </li>
          <li>If no piece is selected, middle-clicking in the board will submit the plan.</li>
          <li>
            If a piece is selected, middle-clicking in a room will choose that room as the piece's destination and
            submit your plan.
          </li>
          <li>
            If no piece is selected, double-clicking on a room is the same as choosing that room as the destination for
            your normal player piece and submitting the plan, which can include previously planned moves.
            Double-clicking a room while a piece is selected is interpreted as 2 single clicks.
          </li>
          <li>Reminder: you can not move or select the Dr or the normal opponent piece.</li>
        </ul>
        <h4>Piece Indicators</h4>
        <p>The small numbers on the pieces give quick reminders of movement and attack strength.</p>
        <ul>
          <li>
            The number above a normal player piece is that player's move cards, rounded down; a trailing ":" means
            they have at least 21/32 of the next move card, and a trailing "." means they have at least 10/32.
          </li>
          <li>
            The number below a normal player piece is that player's next attack strength: natural strength plus 2 if
            they have at least 1 weapon card.
          </li>
          <li>The number below a stranger piece is that stranger's natural strength.</li>
        </ul>
        <h4>Undo/Redo</h4>
        <p>
          Clicking the Undo button will undo everything up to the last submitted plan, which includes all Dr movement
          and stranger turns.
        </p>
        <ul>
          <li>Undo: undoes the most recent submitted turn.</li>
          <li>
            Undo AI: undoes later turns until the board is back on the opponent turn of the most recent AI-controlled
            player, without changing Control checkmarks.
          </li>
          <li>Undo all: undoes every turn, getting you back to start of game.</li>
          <li>Redo: redoes the most recent undone turn.</li>
          <li>Redo all: redoes every currently undone turn.</li>
          <li>Anim Redo: redoes the most recent undone turn and always plays its animation.</li>
        </ul>
      </>
    ) : infoPopup === 'ai' ? (
      <>
        <h4>AI Controls</h4>
        <ul>
          <li>
            <strong>Think</strong>: runs analysis and updates the suggestion using Min Turn Depth, Max Turn Depth, and
            Max Time, unless Max Time is set to No AI.
          </li>
          <li>
            <strong>Do</strong>: submits the current suggested turn if it is valid and not stale.
          </li>
          <li>
            <strong>Mull</strong>: runs analysis like Think, but always uses Min Turn Depth 1, Max Turn Depth 99, and Max
            Time 99999 seconds instead of respecting the choices below. If Think is already running, Mull keeps the
            active analysis going and switches it to these limits.
          </li>
          <li>
            <strong>Cancel</strong>: stops an in-progress analysis run and keeps the best completed level found so far.
          </li>
          <li>
            <strong>Clear Cache</strong>: removes remembered analysis results from memory and localStorage.
          </li>
        </ul>
        <h4>AI Ownership / Display</h4>
        <ul>
          <li>
            <strong>Control (P1/P3)</strong>: lets AI automatically play that normal player when it becomes their turn.
            More specifically, it automatically submits the suggested turn when the analysis completes.
          </li>
          <li>
            <strong>Show On Board (P1/P3)</strong>: shows/hides the suggested move text overlay (and a small Do button)
            on the board for that side.
          </li>
        </ul>
        <h4>Analysis Settings</h4>
        <ul>
          <li>
            <strong>Min Turn Depth</strong>: minimum search depth before the run is allowed to stop from time limits.
          </li>
          <li>
            <strong>Max Turn Depth</strong>: when Min Turn Depth is less than or equal to Max Turn Depth, analysis will
            stop as soon as a result reaches this depth (including a cached result at or above this depth). If Min Turn
            Depth is greater than Max Turn Depth, Max Turn Depth is ignored.
          </li>
          <li>
            <strong>Max Time</strong>: total time budget for one run; analysis will stop when this budget is hit, unless
            we still haven't completed the minimum turn depth analysis. Choose No AI to turn off AI analysis.
          </li>
          <li>The +/- buttons next to each field are quick steppers for mobile and desktop.</li>
        </ul>
        <h4>AI Outputs</h4>
        <ul>
          <li>
            <strong>Status</strong>: current run status (idle/analyzing/cancelled/finished), including level and elapsed
            timing while running.
          </li>
          <li>
            <strong>Suggested</strong>: the best move sequence from the latest completed analysis result.
          </li>
          <li>
            <strong>Stats</strong>: <code>L#</code>, heuristic score, and elapsed time for the completed suggestion
            level.
          </li>
          <li>
            <strong>Preview</strong>: predicted result if the suggested turn is executed (next player, attackers,
            winner, etc.).
          </li>
        </ul>
      </>
    ) : infoPopup === 'playerInfoBox' ? (
      <>
        <h4>Player Info Box</h4>
        <p>
          This box with 4 colorful rows, one for each player, shows vital information about each player. The columns...
        </p>
        <ul>
          <li>D: number of rooms/turns until the Dr arrives at the player's current room.</li>
          <li>S: player strength/natural-attack-power.</li>
          <li>M: number of move cards, each one granting 1 move point and convertable to 1 clover.</li>
          <li>W: number of weapon cards, each one granting 2 attack points and convertable to 1 clover.</li>
          <li>F: number of failure cards, each one convertable to 2 clovers.</li>
          <li>C: number of clovers if you were to convert all their cards.</li>
        </ul>
      </>
    ) : null;
  const modalOverlayOpen = setupPopupOpen || Boolean(infoPopup && infoPopupContent);

  return (
    <section className="play-area">
      {modalOverlayOpen && (
        <style>{`
          html, body {
            overflow: hidden;
            overscroll-behavior: none;
          }
        `}</style>
      )}
      <div className="board-column">
        <div className="board-controls">
          <button className="board-control-button" onClick={handleReset}>
            Reset
          </button>
          <button className="board-control-button" onClick={handleSetupOpen}>
            Setup
          </button>
          <button className="board-control-button" onClick={() => handleInfoToggle('rules')}>
            Rules
          </button>
        </div>
        <div className="board-shell" style={{ borderColor: boardOutlineColor }}>
          <div className="board" onContextMenu={handleBoardContextMenu}>
            <svg
              viewBox={`0 0 ${boardWidth} ${boardHeight}`}
              role="img"
              aria-label="Kill Doctor Lucky Board Alternate Downstairs"
              preserveAspectRatio="xMidYMid meet"
              onMouseDown={handleBoardMouseDown}
            >
              <image href={boardImageHref} width={boardWidth} height={boardHeight} />
              <g className="room-layer">
                {boardRooms.map((room) => {
                  if (room.coords.length !== 4) {
                    return null;
                  }
                  const [x1, y1, x2, y2] = room.coords;
                  const roomClassName = 'room-hit';
                  return (
                    <rect
                      key={room.id}
                      x={x1}
                      y={y1}
                      width={x2 - x1}
                      height={y2 - y1}
                      data-room-id={room.id}
                      className={roomClassName}
                      onClick={() => handleRoomClick(room.id)}
                      onMouseDown={(event) => handleRoomMouseDown(event, room.id)}
                      onPointerDown={handleRoomPointerDown}
                      onDoubleClick={() => handleRoomDoubleClick(room.id)}
                      aria-label={room.name ?? `Room ${room.id}`}
                    />
                  );
                })}
              </g>
              <g className="piece-layer">
                {(animatedPieces ? animatedPieceList : renderPieces.filter((piece) => piece.kind === 'actual')).map(
                  (piece) => {
                    const config = pieceConfig[piece.pieceId];
                    const isSelected = selectedPieceId === piece.pieceId;
                    const isFlashing = actionHighlightPieceId === piece.pieceId;
                    const selectable = isPieceSelectable(piece.pieceId);
                    const isPlannedSource = plannedMoves[piece.pieceId] !== undefined;
                    const className = [
                      'piece',
                      `piece--${config.shape}`,
                      isSelected ? 'piece--selected' : '',
                      selectable ? 'piece--movable' : 'piece--locked',
                      isPlannedSource ? 'piece--planned-source' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const labelSize = Math.min(piece.size * 0.6, 22);
                    const pieceFill = isPlannedSource ? blendHexColor(config.color, '#9c9c9c', 0.5) : config.color;
                    const pieceStroke = isPlannedSource ? '#8c8c8c' : undefined;
                    const pieceStrokeWidth = isPlannedSource ? 2.4 : undefined;
                    const labelFill = isPlannedSource
                      ? blendHexColor(config.textColor, '#6f6f6f', 0.5)
                      : config.textColor;

                    const commonProps = {
                      className,
                      fill: pieceFill,
                      stroke: pieceStroke,
                      strokeWidth: pieceStrokeWidth,
                      onClick: (event: MouseEvent<SVGElement>) => {
                        event.stopPropagation();
                        handlePieceClick(piece.pieceId);
                      },
                      onDoubleClick: (event: MouseEvent<SVGElement>) => {
                        event.stopPropagation();
                        const roomId = pieceRoomMap.get(piece.pieceId);
                        if (roomId === undefined) {
                          return;
                        }
                        if (!isPieceSelectable(piece.pieceId)) {
                          handleRoomDoubleClick(roomId);
                        }
                      },
                      'aria-label': `${config.label} piece`,
                    };

                    const buildShape = (props: ShapeProps) => {
                      if (config.shape === 'circle') {
                        return (
                          <circle
                            cx={piece.x + piece.size / 2}
                            cy={piece.y + piece.size / 2}
                            r={piece.size / 2}
                            {...props}
                          />
                        );
                      }
                      if (config.shape === 'square') {
                        return <rect x={piece.x} y={piece.y} width={piece.size} height={piece.size} {...props} />;
                      }
                      return <polygon points={getHexPoints(piece.x, piece.y, piece.size)} {...props} />;
                    };
                    const outlineShape = isFlashing
                      ? buildShape({
                          className: 'piece--flash-outline',
                          pointerEvents: 'none',
                          'aria-hidden': true,
                        })
                      : null;
                    const shape = buildShape(commonProps);

                    return (
                      <g key={piece.pieceId}>
                        {outlineShape}
                        {shape}
                        {config.showLabel &&
                          renderPieceLabelStack(piece, config.label, labelFill, 'piece-label', labelSize)}
                      </g>
                    );
                  },
                )}
              </g>
              <g className="piece-layer piece-layer--planned">
                {renderPieces
                  .filter((piece) => piece.kind === 'ghost')
                  .map((piece) => {
                    const config = pieceConfig[piece.pieceId];
                    const className = ['piece', `piece--${config.shape}`, 'piece--planned-ghost']
                      .filter(Boolean)
                      .join(' ');
                    const labelSize = Math.min(piece.size * 0.6, 22);
                    const ghostProps = {
                      className,
                      fill: config.color,
                      stroke: '#1f4f7a',
                      strokeWidth: 4,
                      pointerEvents: 'none' as const,
                      'aria-hidden': true,
                    };
                    let shape;
                    if (config.shape === 'circle') {
                      shape = (
                        <circle
                          cx={piece.x + piece.size / 2}
                          cy={piece.y + piece.size / 2}
                          r={piece.size / 2}
                          {...ghostProps}
                        />
                      );
                    } else if (config.shape === 'square') {
                      shape = <rect x={piece.x} y={piece.y} width={piece.size} height={piece.size} {...ghostProps} />;
                    } else {
                      shape = <polygon points={getHexPoints(piece.x, piece.y, piece.size)} {...ghostProps} />;
                    }

                    return (
                      <g key={`ghost-${piece.pieceId}`} pointerEvents="none">
                        {shape}
                        {config.showLabel &&
                          renderPieceLabelStack(
                            piece,
                            config.label,
                            '#1f4f7a',
                            'piece-label piece-label--planned',
                            labelSize,
                          )}
                      </g>
                    );
                  })}
              </g>
              {distanceByRoom && distanceOriginRoomId !== undefined && (
                <g className="room-distance-layer">
                  {boardRooms.map((room) => {
                    if (room.coords.length !== 4) {
                      return null;
                    }
                    if (room.id === distanceOriginRoomId) {
                      return null;
                    }
                    const distance = distanceByRoom.get(room.id);
                    if (distance === undefined) {
                      return null;
                    }
                    const rect = getRoomRect(room.coords);
                    const boxWidth = roomDistanceBoxWidth * 1.5;
                    const boxHeight = roomDistanceBoxHeight * 1.5;
                    const boxX = rect.x + (rect.width - boxWidth) / 2;
                    const boxY = rect.y + rect.height * roomDistanceBoxTopRatio;
                    const textX = boxX + boxWidth / 2;
                    const textY = boxY + boxHeight / 2 + 0.5;
                    const isMovePossible = movePossibleByRoom?.get(room.id) ?? true;
                    const boxClassName = isMovePossible
                      ? 'room-distance-box room-distance-box--possible'
                      : 'room-distance-box room-distance-box--too-far';

                    return (
                      <g key={`distance-${room.id}`}>
                        <rect className={boxClassName} x={boxX} y={boxY} width={boxWidth} height={boxHeight} />
                        <text className="room-distance-text" x={textX} y={textY}>
                          {distance}
                        </text>
                      </g>
                    );
                  })}
                </g>
              )}
              {boardOverlayText && actionOverlayLayout && (
                <g className={boardOverlayIsAiSuggestion ? 'action-overlay action-overlay--board-shell-bottom' : 'action-overlay'}>
                  <rect
                    className={actionOverlayBoxClassName}
                    x={actionOverlayLayout.boxX}
                    y={actionOverlayLayout.boxY}
                    width={actionOverlayLayout.boxWidth}
                    height={actionOverlayLayout.boxHeight}
                  />
                  <text
                    className="action-overlay-text"
                    x={actionOverlayLayout.textX}
                    y={actionOverlayLayout.textY}
                    style={{ fontSize: `${actionOverlayLayout.fontSize}px` }}
                  >
                    {boardOverlayText}
                  </text>
                </g>
              )}
              {aiSuggestionBoardDoButtonLayout && (
                <foreignObject
                  className={
                    boardOverlayIsAiSuggestion
                      ? 'action-overlay-action action-overlay-action--board-shell-bottom'
                      : 'action-overlay-action'
                  }
                  x={aiSuggestionBoardDoButtonLayout.x}
                  y={aiSuggestionBoardDoButtonLayout.y}
                  width={aiSuggestionBoardDoButtonLayout.width}
                  height={aiSuggestionBoardDoButtonLayout.height}
                >
                  <button
                    type="button"
                    className="planner-button planner-button--primary action-overlay-do-button"
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDoSuggestedTurn();
                    }}
                    aria-label="Do suggested turn"
                    title="Do suggested turn"
                    disabled={!aiCanDoIt || hasWinner}
                  >
                    Do
                  </button>
                </foreignObject>
              )}
              {showWinnerOverlay && winnerOverlayText && winnerOverlayLayout && (
                <g className="winner-overlay" aria-hidden>
                  <rect
                    className="winner-overlay-box"
                    x={winnerOverlayLayout.boxX}
                    y={winnerOverlayLayout.boxY}
                    width={winnerOverlayLayout.boxWidth}
                    height={winnerOverlayLayout.boxHeight}
                  />
                  <text className="winner-overlay-text" x={winnerOverlayLayout.textX} y={winnerOverlayLayout.textY}>
                    {winnerOverlayText}
                  </text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
      <div className="side-column">
        <aside className="planner-panel">
          <div className="planner-header">
            <div>
              <h2
                className="planner-title"
                style={{ backgroundColor: currentPlayerColor, color: currentPlayerTextColor }}
              >
                {hasWinner && winnerTurnTitleText ? winnerTurnTitleText : currentTurnTitleText}
              </h2>
            </div>
            <div className="planner-header-actions">
              <button
                className="planner-button planner-button--primary planner-header-ai-button"
                onClick={handleDoSuggestedTurn}
                disabled={!aiCanDoIt || hasWinner}
              >
                AI Do
              </button>
              <button
                className="planner-button planner-header-ai-button"
                onClick={aiIsMulling ? handleAnalysisCancel : handleMull}
                disabled={!aiIsMulling && hasWinner}
              >
                {plannerMullButtonLabel}
              </button>
              <button
                type="button"
                className="planner-help-icon-button"
                onClick={() => handleInfoToggle('turnPlanner')}
                aria-label="Turn planner help"
                title="Turn planner help"
              >
                ?
              </button>
            </div>
          </div>
          <div className="planner-line">
            <span className="planner-label planner-label--small">Selected</span>
            <span className="planner-value">{selectedLabel + selectedSuffix}</span>
          </div>
          <div className="planner-line">
            <span className="planner-label planner-label--small">Planned</span>
            <span className="planner-value">{planSummary}</span>
          </div>
          <div className="planner-line">
            <span className="planner-label planner-label--small">Preview</span>
            <FittingPreviewValue display={previewDisplay} keyPrefix="turn" />
          </div>
          <div className="planner-actions planner-actions--turn">
            <button className="planner-button planner-button--primary" onClick={handleSubmit} disabled={hasWinner}>
              Submit
            </button>
            <button className="planner-button" onClick={handleCancel} disabled={!canCancelTurnPlan}>
              Cancel
            </button>
            <button className="planner-button" onClick={handleUndo} disabled={!canUndo}>
              Undo
            </button>
            <button className="planner-button" onClick={handleRedo} disabled={!canRedo}>
              Redo
            </button>
          </div>
          <div className="planner-actions planner-actions--turn planner-actions--turn-secondary">
            <button className="planner-button" onClick={handleUndoAi} disabled={!canUndoAi}>
              Undo AI
            </button>
            <button className="planner-button" onClick={handleUndoAll} disabled={!canUndoAll}>
              Undo all
            </button>
            <button className="planner-button" onClick={handleRedoAll} disabled={!canRedoAll}>
              Redo all
            </button>
            <button
              className="planner-button"
              onClick={() => runRedo({ animateFromCurrentState: true })}
              disabled={!canRedo}
            >
              Ani Redo
            </button>
          </div>
          {validationMessage && <p className="planner-error">{validationMessage}</p>}
          <div className="planner-animations">
            <p className="planner-animations-title">Animations</p>
            <div className="planner-animations-row">
              <label className="planner-animations-toggle">
                <input
                  type="checkbox"
                  checked={animationEnabled}
                  onChange={(event) => handleAnimationEnabledChange(event.target.checked)}
                />
                On
              </label>
              <button className="planner-button" onClick={() => handleSpeedChange('slower')} aria-label="Slower">
                -
              </button>
              <button className="planner-button" onClick={() => handleSpeedChange('faster')} aria-label="Faster">
                +
              </button>
              <span className="planner-animations-speed">{animationSpeed.toFixed(2)}x</span>
            </div>
          </div>
        </aside>
        <aside className="planner-panel player-stats-panel" onClick={() => handleInfoToggle('playerInfoBox')}>
          {playerStatsRows.length === 0 ? (
            <p className="player-stats-empty">Stats unavailable.</p>
          ) : (
            <>
              <p className="player-stats-attacks">
                {attackHistoryLabel}: {attackHistoryText || 'none'}
              </p>
              <table className="player-stats-table" aria-label="Player stats">
                <thead>
                  <tr>
                    <th scope="col">P</th>
                    <th scope="col">D</th>
                    <th scope="col">S</th>
                    <th scope="col">M</th>
                    <th scope="col">W</th>
                    <th scope="col">F</th>
                    <th scope="col">C</th>
                  </tr>
                </thead>
                <tbody>
                  {playerStatsRows.map((row) => {
                    const config = pieceConfig[row.pieceId];
                    const isNormalPlayerRow = row.pieceId === 'player1' || row.pieceId === 'player2';
                    const numericCellClassName = isNormalPlayerRow ? 'player-stats-cell--bold' : undefined;
                    return (
                      <tr
                        key={`player-stats-${row.pieceId}`}
                        style={{ backgroundColor: config.color, color: config.textColor }}
                      >
                        <th scope="row">{config.label}</th>
                        <td className={numericCellClassName}>{formatPlayerInteger(row.doctorDistance)}</td>
                        <td className={numericCellClassName}>{formatPlayerInteger(row.strength)}</td>
                        <td className={numericCellClassName}>
                          {isNormalPlayerRow ? formatPlayerStatDecimal(row.moveCards) : ''}
                        </td>
                        <td className={numericCellClassName}>
                          {isNormalPlayerRow ? formatPlayerStatDecimal(row.weaponCards) : ''}
                        </td>
                        <td className={numericCellClassName}>
                          {isNormalPlayerRow ? formatPlayerStatDecimal(row.failureCards) : ''}
                        </td>
                        <td className={numericCellClassName}>
                          {isNormalPlayerRow ? formatPlayerStatDecimal(row.equivalentClovers) : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </aside>
        <aside className="planner-panel ai-panel">
          <div className="planner-header">
            <h2
              className="planner-title"
              style={{ backgroundColor: currentPlayerColor, color: currentPlayerTextColor }}
            >
              AI
            </h2>
            <div className="planner-header-actions">
              <button
                type="button"
                className="planner-help-icon-button"
                onClick={() => handleInfoToggle('ai')}
                aria-label="AI help"
                title="AI help"
              >
                ?
              </button>
            </div>
          </div>
          <div className="planner-actions ai-actions ai-actions--header">
            <button
              className="planner-button planner-button--primary"
              onClick={handleThink}
              disabled={hasWinner || analysisIsRunning || aiAnalysisDisabled}
            >
              Think
            </button>
            <button
              className="planner-button planner-button--primary"
              onClick={handleDoSuggestedTurn}
              disabled={!aiCanDoIt || hasWinner}
            >
              Do
            </button>
            <button className="planner-button" onClick={handleMull} disabled={mullButtonDisabled}>
              Mull
            </button>
            <button className="planner-button" onClick={handleAnalysisCancel} disabled={!analysisIsRunning}>
              Cancel
            </button>
          </div>
          <div className="planner-line">
            <span className="planner-label">Control</span>
            <span className="planner-value ai-control-value">
              <label className="ai-control-option">
                <input
                  type="checkbox"
                  checked={aiControlP1}
                  onChange={(event) => handleAiControlChange('player1', event.target.checked)}
                />
                P1
              </label>
              <label className="ai-control-option">
                <input
                  type="checkbox"
                  checked={aiControlP3}
                  onChange={(event) => handleAiControlChange('player2', event.target.checked)}
                />
                P3
              </label>
            </span>
          </div>
          <div className="planner-line">
            <span className="planner-label">ShowOnBoard</span>
            <span className="planner-value ai-control-value">
              <label className="ai-control-option">
                <input
                  type="checkbox"
                  aria-label="ShowOnBoard P1"
                  checked={aiShowOnBoardP1}
                  onChange={(event) => handleAiShowOnBoardChange('player1', event.target.checked)}
                />
                P1
              </label>
              <label className="ai-control-option">
                <input
                  type="checkbox"
                  aria-label="ShowOnBoard P3"
                  checked={aiShowOnBoardP3}
                  onChange={(event) => handleAiShowOnBoardChange('player2', event.target.checked)}
                />
                P3
              </label>
            </span>
          </div>
          <div className="planner-line ai-level-line">
            <label className="planner-label" htmlFor="analysis-min-level">
              MinTurnDepth
            </label>
            {/* Firefox Android hides native number spinners; explicit steppers keep increment/decrement available on mobile. */}
            <div className="number-stepper">
              <input
                id="analysis-min-level"
                className="ai-level-input number-stepper-input"
                type="number"
                min="0"
                step="1"
                value={minAnalysisLevelDraft}
                onChange={(event) => handleAnalysisLevelChange(event.target.value)}
                disabled={analysisIsRunning}
              />
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleAnalysisLevelStep('down')}
                aria-label="Decrease min turn depth"
                disabled={analysisIsRunning}
              >
                -
              </button>
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleAnalysisLevelStep('up')}
                aria-label="Increase min turn depth"
                disabled={analysisIsRunning}
              >
                +
              </button>
            </div>
          </div>
          <div className="planner-line ai-level-line">
            <label className="planner-label" htmlFor="analysis-max-level">
              MaxTurnDepth
            </label>
            <div className="number-stepper">
              <input
                id="analysis-max-level"
                className="ai-level-input number-stepper-input"
                type="number"
                min="0"
                step="1"
                value={maxAnalysisLevelDraft}
                onChange={(event) => handleMaxAnalysisLevelChange(event.target.value)}
                disabled={analysisIsRunning}
              />
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleMaxAnalysisLevelStep('down')}
                aria-label="Decrease max turn depth"
                disabled={analysisIsRunning}
              >
                -
              </button>
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleMaxAnalysisLevelStep('up')}
                aria-label="Increase max turn depth"
                disabled={analysisIsRunning}
              >
                +
              </button>
            </div>
          </div>
          <div className="planner-line ai-level-line">
            <label className="planner-label" htmlFor="analysis-max-time">
              MaxTime
            </label>
            <div className="number-stepper">
              <select
                id="analysis-max-time"
                className="ai-max-time-select number-stepper-input"
                value={analysisMaxTimeIndex.toString()}
                onChange={(event) => handleAnalysisMaxTimeChange(event.target.value)}
                disabled={analysisIsRunning}
              >
                {analysisMaxTimeOptions.map((option, index) => (
                  <option key={`analysis-max-time-${option.label}-${index}`} value={index.toString()}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleAnalysisMaxTimeStep('down')}
                aria-label="Decrease max time"
                disabled={analysisIsRunning}
              >
                -
              </button>
              <button
                type="button"
                className="number-stepper-button"
                onClick={() => handleAnalysisMaxTimeStep('up')}
                aria-label="Increase max time"
                disabled={analysisIsRunning}
              >
                +
              </button>
            </div>
          </div>
          <div className="planner-line">
            <span className="planner-label">Status</span>
            <span className="planner-value ai-status-value">{aiStatusText}</span>
          </div>
          <div className="planner-line">
            <span className="planner-label">Suggested</span>
            <span className="planner-value">{aiSuggestedTurnText}</span>
          </div>
          <div className="planner-line">
            <span className="planner-label">Stats</span>
            <span className="planner-value">{aiStatsText}</span>
          </div>
          <div className="planner-line">
            <span className="planner-label">Preview</span>
            <FittingPreviewValue display={aiPreviewDisplay} keyPrefix="ai" />
          </div>
          {aiStaleMessage && <p className="ai-note">{aiStaleMessage}</p>}
          <div className="planner-actions ai-actions ai-actions--footer">
            <button className="planner-button" onClick={handleClearAiResultsCache}>
              Clear Cache
            </button>
          </div>
        </aside>
      </div>
      <div className="play-area-summary">
        <pre className="game-summary">{summary}</pre>
        {prevTurnSummary && <pre className="game-summary">{prevTurnSummary}</pre>}
        {history && <pre className="game-summary game-summary--history">{history}</pre>}
      </div>
      {setupPopupOpen && (
        <div className="info-overlay" role="dialog" aria-modal="true" onClick={handleSetupCancel}>
          <div className="info-popup setup-popup" onClick={(event) => event.stopPropagation()}>
            <h3>Setup</h3>
            <div className="setup-popup-form">
              <label className="setup-popup-row">
                <span>Board</span>
                <select
                  aria-label="Board"
                  value={setupPrefsDraft.boardName}
                  onChange={(event) => handleSetupBoardChange(event.target.value)}
                >
                  {boardLayouts.map((layout) => (
                    <option key={layout.JsonName} value={layout.JsonName}>
                      {layout.Name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="setup-popup-toggle">
                <input
                  type="checkbox"
                  checked={setupAdvanced}
                  onChange={(event) => handleSetupAdvancedChange(event.target.checked)}
                />
                <span>Advanced</span>
              </label>
              <label className="setup-popup-row">
                <span>{setupAdvanced ? 'Move Cards (P1)' : 'Move Cards (Normal)'}</span>
                <div className="number-stepper">
                  <input
                    className="number-stepper-input"
                    aria-label={setupAdvanced ? 'P1 move cards' : 'Normal move cards'}
                    type="number"
                    min="0"
                    step={setupAdvanced ? '0.01' : '1'}
                    value={setupPrefsDraft.moveCards}
                    onChange={(event) => handleSetupDraftChange('moveCards', event.target.value)}
                  />
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('moveCards', 'down')}
                    aria-label="Decrease move cards"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('moveCards', 'up')}
                    aria-label="Increase move cards"
                  >
                    +
                  </button>
                </div>
              </label>
              <label className="setup-popup-row">
                <span>{setupAdvanced ? 'Weapon Cards (P1)' : 'Weapon Cards (Normal)'}</span>
                <div className="number-stepper">
                  <input
                    className="number-stepper-input"
                    aria-label={setupAdvanced ? 'P1 weapon cards' : 'Normal weapon cards'}
                    type="number"
                    min="0"
                    step={setupAdvanced ? '0.01' : '1'}
                    value={setupPrefsDraft.weaponCards}
                    onChange={(event) => handleSetupDraftChange('weaponCards', event.target.value)}
                  />
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('weaponCards', 'down')}
                    aria-label="Decrease weapon cards"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('weaponCards', 'up')}
                    aria-label="Increase weapon cards"
                  >
                    +
                  </button>
                </div>
              </label>
              <label className="setup-popup-row">
                <span>{setupAdvanced ? 'Failure Cards (P1)' : 'Failure Cards (Normal)'}</span>
                <div className="number-stepper">
                  <input
                    className="number-stepper-input"
                    aria-label={setupAdvanced ? 'P1 failure cards' : 'Normal failure cards'}
                    type="number"
                    min="0"
                    step={setupAdvanced ? '0.01' : '1'}
                    value={setupPrefsDraft.failureCards}
                    onChange={(event) => handleSetupDraftChange('failureCards', event.target.value)}
                  />
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('failureCards', 'down')}
                    aria-label="Decrease failure cards"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="number-stepper-button"
                    onClick={() => handleSetupStep('failureCards', 'up')}
                    aria-label="Increase failure cards"
                  >
                    +
                  </button>
                </div>
              </label>
              {setupAdvanced && (
                <>
                  <label className="setup-popup-row">
                    <span>Move Cards (P3)</span>
                    <div className="number-stepper">
                      <input
                        className="number-stepper-input"
                        aria-label="P3 move cards"
                        type="number"
                        min="0"
                        step="0.01"
                        value={setupPrefsDraft.player2MoveCards}
                        onChange={(event) => handleSetupDraftChange('player2MoveCards', event.target.value)}
                      />
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2MoveCards', 'down')}
                        aria-label="Decrease P3 move cards"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2MoveCards', 'up')}
                        aria-label="Increase P3 move cards"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>Weapon Cards (P3)</span>
                    <div className="number-stepper">
                      <input
                        className="number-stepper-input"
                        aria-label="P3 weapon cards"
                        type="number"
                        min="0"
                        step="0.01"
                        value={setupPrefsDraft.player2WeaponCards}
                        onChange={(event) => handleSetupDraftChange('player2WeaponCards', event.target.value)}
                      />
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2WeaponCards', 'down')}
                        aria-label="Decrease P3 weapon cards"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2WeaponCards', 'up')}
                        aria-label="Increase P3 weapon cards"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>Failure Cards (P3)</span>
                    <div className="number-stepper">
                      <input
                        className="number-stepper-input"
                        aria-label="P3 failure cards"
                        type="number"
                        min="0"
                        step="0.01"
                        value={setupPrefsDraft.player2FailureCards}
                        onChange={(event) => handleSetupDraftChange('player2FailureCards', event.target.value)}
                      />
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2FailureCards', 'down')}
                        aria-label="Decrease P3 failure cards"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupStep('player2FailureCards', 'up')}
                        aria-label="Increase P3 failure cards"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <p className="setup-popup-note">Card amounts entered in hundredths snap to the nearest 1/32 on start.</p>
                  <div className="setup-popup-command-row">
                    <button type="button" className="planner-button" onClick={handleRandomSetupRooms}>
                      Random Rooms
                    </button>
                  </div>
                  <label className="setup-popup-row">
                    <span>Dr</span>
                    <div className="number-stepper">
                      <select
                        aria-label="Doctor room"
                        value={setupPrefsDraft.doctorRoomId}
                        onChange={(event) => handleSetupDraftChange('doctorRoomId', event.target.value)}
                      >
                        {setupBoardRooms.map((room) => (
                          <option key={`setup-doctor-${room.id}`} value={room.id.toString()}>
                            {`R${room.id}: ${room.name ?? `Room ${room.id}`}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('doctorRoomId', 'down')}
                        aria-label="Previous Doctor"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('doctorRoomId', 'up')}
                        aria-label="Next Doctor"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>P1</span>
                    <div className="number-stepper">
                      <select
                        aria-label="P1 room"
                        value={setupPrefsDraft.player1RoomId}
                        onChange={(event) => handleSetupDraftChange('player1RoomId', event.target.value)}
                      >
                        {setupBoardRooms.map((room) => (
                          <option key={`setup-p1-${room.id}`} value={room.id.toString()}>
                            {`R${room.id}: ${room.name ?? `Room ${room.id}`}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('player1RoomId', 'down')}
                        aria-label="Previous P1"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('player1RoomId', 'up')}
                        aria-label="Next P1"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>p2</span>
                    <div className="number-stepper">
                      <select
                        aria-label="p2 room"
                        value={setupPrefsDraft.stranger1RoomId}
                        onChange={(event) => handleSetupDraftChange('stranger1RoomId', event.target.value)}
                      >
                        {setupBoardRooms.map((room) => (
                          <option key={`setup-p2-${room.id}`} value={room.id.toString()}>
                            {`R${room.id}: ${room.name ?? `Room ${room.id}`}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('stranger1RoomId', 'down')}
                        aria-label="Previous p2"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('stranger1RoomId', 'up')}
                        aria-label="Next p2"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>P3</span>
                    <div className="number-stepper">
                      <select
                        aria-label="P3 room"
                        value={setupPrefsDraft.player2RoomId}
                        onChange={(event) => handleSetupDraftChange('player2RoomId', event.target.value)}
                      >
                        {setupBoardRooms.map((room) => (
                          <option key={`setup-p3-${room.id}`} value={room.id.toString()}>
                            {`R${room.id}: ${room.name ?? `Room ${room.id}`}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('player2RoomId', 'down')}
                        aria-label="Previous P3"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('player2RoomId', 'up')}
                        aria-label="Next P3"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>p4</span>
                    <div className="number-stepper">
                      <select
                        aria-label="p4 room"
                        value={setupPrefsDraft.stranger2RoomId}
                        onChange={(event) => handleSetupDraftChange('stranger2RoomId', event.target.value)}
                      >
                        {setupBoardRooms.map((room) => (
                          <option key={`setup-p4-${room.id}`} value={room.id.toString()}>
                            {`R${room.id}: ${room.name ?? `Room ${room.id}`}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('stranger2RoomId', 'down')}
                        aria-label="Previous p4"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="number-stepper-button"
                        onClick={() => handleSetupRoomStep('stranger2RoomId', 'up')}
                        aria-label="Next p4"
                      >
                        +
                      </button>
                    </div>
                  </label>
                  <label className="setup-popup-row">
                    <span>P1 Strength</span>
                    <input
                      aria-label="P1 strength"
                      type="number"
                      min="0"
                      step="1"
                      value={setupPrefsDraft.player1Strength}
                      onChange={(event) => handleSetupDraftChange('player1Strength', event.target.value)}
                    />
                  </label>
                  <label className="setup-popup-row">
                    <span>p2 Strength</span>
                    <input
                      aria-label="p2 strength"
                      type="number"
                      min="0"
                      step="1"
                      value={setupPrefsDraft.stranger1Strength}
                      onChange={(event) => handleSetupDraftChange('stranger1Strength', event.target.value)}
                    />
                  </label>
                  <label className="setup-popup-row">
                    <span>P3 Strength</span>
                    <input
                      aria-label="P3 strength"
                      type="number"
                      min="0"
                      step="1"
                      value={setupPrefsDraft.player2Strength}
                      onChange={(event) => handleSetupDraftChange('player2Strength', event.target.value)}
                    />
                  </label>
                  <label className="setup-popup-row">
                    <span>p4 Strength</span>
                    <input
                      aria-label="p4 strength"
                      type="number"
                      min="0"
                      step="1"
                      value={setupPrefsDraft.stranger2Strength}
                      onChange={(event) => handleSetupDraftChange('stranger2Strength', event.target.value)}
                    />
                  </label>
                  <label className="setup-popup-row">
                    <span>Turn Id</span>
                    <input
                      aria-label="Turn id"
                      type="number"
                      min="1"
                      step="1"
                      value={setupPrefsDraft.turnId}
                      onChange={(event) => handleSetupDraftChange('turnId', event.target.value)}
                    />
                  </label>
                  <p className="setup-popup-note">Doctor activation checks this internal turn id against the 4-player turn count.</p>
                  <fieldset className="setup-popup-fieldset">
                    <legend>Current Player</legend>
                    <label className="setup-popup-radio">
                      <input
                        type="radio"
                        name="setup-current-player"
                        checked={setupPrefsDraft.currentPlayerPieceId === 'player1'}
                        onChange={() => handleSetupCurrentPlayerChange('player1')}
                      />
                      <span>P1</span>
                    </label>
                    <label className="setup-popup-radio">
                      <input
                        type="radio"
                        name="setup-current-player"
                        checked={setupPrefsDraft.currentPlayerPieceId === 'player2'}
                        onChange={() => handleSetupCurrentPlayerChange('player2')}
                      />
                      <span>P3</span>
                    </label>
                  </fieldset>
                </>
              )}
            </div>
            {setupError && <p className="setup-popup-error">{setupError}</p>}
            <div className="setup-popup-actions">
              <button className="planner-button planner-button--primary" onClick={handleStartNewGameWithSetup}>
                Start New Game
              </button>
              <button className="planner-button" onClick={handleSetupCancel}>
                Cancel
              </button>
              <button className="planner-button" onClick={handleRestoreSetupDefaults}>
                Restore Defaults
              </button>
            </div>
          </div>
        </div>
      )}
      {infoPopup && infoPopupContent && (
        <div className="info-overlay" role="dialog" aria-modal="true" onClick={() => setInfoPopup(null)}>
          <div className="info-popup">
            <h3>{infoPopupTitle}</h3>
            {infoPopupContent}
          </div>
        </div>
      )}
    </section>
  );
}

export default PlayArea;
